// @ts-nocheck -- Wave H typing debt: renamed to TypeScript in b59aeab4 without
// being typed (601 strict errors at 94e4d877). As .jsx it was never type-checked,
// so this keeps its pre-rename status while every other file stays gated.
// Ledger: docs/typescript-migration/WAVE-H-TYPECHECK-DEBT.md
import { migrateLegacyQuoteStatuses } from './services/legacy-quote-status-migration';
import { runBackgroundBootWarmup } from './services/app-boot-warmup';
import { PHOTO_CAPTION_EVENT, withPhotoCaption, type PhotoCaptionDetail } from './services/photo-caption-queue';
import { computeAssistantStageNavigationState } from './utils/assistant-navigation-state';
import { generateProjectName } from './utils/project-name-generator';
import { computeBottomNavQuotesFilter, computeBottomNavDashboardPulse } from './utils/bottom-nav-helpers';
import { ProjectMissingFilesBanner } from './components/ui/ProjectMissingFilesBanner';
import ProjectClientLink from './components/crm/ProjectClientLink';
import { useGlobalDashboardDrop } from './hooks/use-global-dashboard-drop';
import { useStageRowsMirror } from './hooks/use-stage-rows-mirror';
import { GlobalDashboardDropOverlay } from './components/ui/GlobalDashboardDropOverlay';
import { useAppEventListeners } from './hooks/use-app-event-listeners';
import { usePhotoOcr } from './hooks/use-photo-ocr';
import { useGlobalFileExplorer } from './hooks/use-global-file-explorer';
import { useQuoteRunPage } from './hooks/use-quote-run-page';
import { useStageCompletionAssessment } from './hooks/use-stage-completion-assessment';
import { useTenantSessionVerification } from './hooks/use-tenant-session-verification';
import { DEMO_SEND_REFUSED_EVENT, installDemoMailLinkGuard } from './services/demo-send-guard';
import {
    SENT_QUOTE_RECORD_EVENT,
    startSentQuoteRecorder,
    type SentQuoteRecordEventDetail,
} from './services/quote-recovery/sent-quote-recorder';
import { useQuoteSubfolders } from './hooks/use-quote-subfolders';
import React, { useState, useEffect, useRef, useCallback, useReducer, useMemo, useSyncExternalStore, Suspense, lazy } from 'react';
import { useQuoteEditor } from './hooks/use-quote-editor';
import { useQuoteObjective, useQuoteObjectiveStorage } from './hooks/use-quote-objective';
import { useProjectSummary } from './hooks/use-project-summary';
import { markProjectSummarySuccessSeenFresh, projectSummaryRerunHoldIsCurrent } from './hooks/use-project-summary';
import { runPrepareStage1Evidence } from './services/prepare-stage1-evidence';
import { useStage1PostProcessing } from './hooks/use-stage1-post-processing';
import { usePhotoAutoAssign } from './hooks/use-photo-auto-assign';
import {
    executeCostingsSent,
    executeMarkWon,
    executeMarkLost,
    executeSetFollowUp,
} from './services/project-outcome-actions';
import { useCostingGeneration } from './hooks/use-costing-generation';
import { useStageNavigation } from './hooks/use-stage-navigation';
import { useAppRouting } from './hooks/use-app-routing';
import { intakeJobFromHistory } from './hooks/use-intake-job-navigation';
import { useProjectPersistence } from './hooks/use-project-persistence';
import type { ProjectSaveOptions } from './hooks/use-project-persistence';
import { devLog } from './utils/dev-log';
import { processingReducer, initialState as processingInitialState } from './pages/stages/stage1/processingReducer';
import { AppAssistantPanelWrapper } from './components/assistant/AppAssistantPanelWrapper';
import { requestCockpitAssistant, useAssistantDockHost } from './services/assistant-dock';
import { AppSecondaryPagesRouter } from './components/routing/AppSecondaryPagesRouter';
import { isActionableRunningPipelineEntry, PipelineMonitor } from './services/pipeline-monitor';
import { shouldBlockSiteVisitExit } from './services/site-visit-exit-guard';
import { PROJECT_WORKFLOW_MODES, normalizeProjectWorkflowMode, workflowModeToast } from './services/project-workflow';
// TS-060 / DEC-COUNCIL-015 T1: the following were extracted verbatim from
// App.jsx's module-scope prelude (behaviour unchanged; see each module header).
import { addressNamesPage, pageToPath, pathToPage } from './utils/page-routing';
import { persistQuoteMakerViewReceipt, readQuoteMakerViewReceipt, runInitialUrlSync } from './services/app-reload-routing';
import {
    isSiteVisitRole,
    effectiveAccess,
    resolveDeniedAppAreaRoute,
} from './config/access-areas';
import {
    computeProjectFingerprint,
    consumeProjectHydrationFingerprintReceipt,
    diffProjectFingerprintKeys,
    overlayProjectFingerprintSaveOverrides,
} from './utils/project-fingerprint';
import { isProjectSaveRetryBlocked } from './utils/project-save-retry-policy';
import { createAssistantDraftSessionKey } from './utils/assistant-draft-session';
// TS-060 / DEC-COUNCIL-015 T2: converge App.jsx's local stale-chunk copy onto
// the canonical util (already imported by analysis-pipeline + ai-learning).
import { isStaleChunkError, tryStaleChunkRecovery } from './utils/stale-chunk-recovery';
import {
    canRenderAuthenticatedApp,
    decodeJwtClaims,
    isSessionRecoverable,
    passwordRotationStateFromClaims,
} from './utils/session-boot';
import {
    acquireProjectWriteLease,
    createProjectSingleflight,
    isSingleflightActiveForPaintedProject,
} from './utils/project-singleflight';
// TS-060 / DEC-COUNCIL-015 T2: the error-boundary classes were extracted to
// self-contained .tsx components. AssistantErrorBoundary is rendered here;
// ErrorBoundary is re-exported at the bottom of this file for src/main.jsx.
import { framesOfVideo } from './utils/video-frames';
import { buildProjectSummaryEvidenceFingerprint } from './utils/project-summary-evidence';
import { canAutoSummarise } from './utils/meaningful-input';
import {
    stage1SummaryLandedThisSession,
    stage1WaitingNotice,
    SUMMARY_FAILURE_RETRY_MS,
} from './services/stage1-workbench-status';
import { useInspectorNotesPauseRelease } from './hooks/use-inspector-notes-pause-release';
import {
    buildProjectSummaryFocusRecoveryKey,
    claimProjectSummaryFocusRecovery,
} from './utils/project-summary-recovery';
import { createStageChanger } from './utils/stage-transition';
import {
    beginAttendedAutomaticSummary,
    observeAttendedSummaryEvidence,
    registerAdoptedEvidence,
    revokeAttendedSummary,
    type AttendedSummaryWitness,
} from './services/attended-summary-run';
import { executeRequoteShortcut } from './services/requote-shortcut-runner';
import {
    acknowledgeProjectMediaRetirementReceipts,
    clearRetiredProjectFormDataPhotoAliases,
    clearRetiredProjectPhotoAliases,
    clearRetiredProjectQuotePhotoAliases,
    clearRetiredProjectScopePhotoAliases,
    clearRetiredProjectSelectedHero,
    createProjectMediaRetirementQueue,
    pendingProjectMediaRetirementReceipts,
    queueProjectMediaRetirementReceipt,
} from './services/project-media-retirement';
import { getProject, getAllProjectSummaries, cleanupProjectStorage, deleteProject } from './services/database';
import db, {
    closeLocalTenantDatabase,
} from './services/db';
import { getVerifiedTenantBrowserKey } from './services/tenant-runtime-scope';
import { shutdownLocalTenantRuntime } from './services/local-session-runtime';
import { requestCloudMirrorRetryCancel } from './services/cloud-mirror-events';
import {
    broadcastProjectsChanged,
    createProjectSummaryRefreshCoordinator,
    subscribeProjectsChanged,
} from './services/project-changes-channel';
import { createProjectRemoteVersionMonitor } from './services/project-remote-version-monitor';
import { tabProjectWriteLock } from './utils/project-write-lock';
import { firstRunHbcfStepAtStage5, hbcfNoticeAfterJobChange, hbcfNoticeRefusal, isSameHbcfNoticeJob, scopeCarriesAssistantChange } from './utils/stage4-pricing';
import { afterToastExit, toastKey } from './utils/toast-key';
import { serverVersionToAdopt } from './utils/server-version-adoption';
import { FileSystemService } from './services/file-system';
import { QM_OUTPUT_SUBFOLDER } from './config/folder-conventions';
import { SyncFolderService } from './services/sync-folder';
// File explorer intake & folder access support extracted to useGlobalFileExplorer
// DEC-COUNCIL-007 / Candidate C6 Slice 1: photo-* surface consolidated under
// the Photo namespace barrel at src/services/photo.js. The named imports
// below continue to resolve via the barrel's back-compat re-exports.
import {
    cloneProjectBlobs,
    autoAssignPhotosToScope,
    selectAssignablePhotos,
} from './services/photo.js';
import {
    cloneProjectDocumentBlobs,
    registerProjectDocumentUrl,
    releaseProjectDocumentUrl,
} from './services/document-blob-store';
import { describeRfqSourceDoubt, planKeepAttachedRequest, planSwitchToSetAsideRequest, withRfqSourceAnswer } from './services/rfq-source-reading';
import { useRfqSourceDoubt } from './hooks/use-rfq-source-doubt';
import { useQuoteMakerReview } from './hooks/use-quotemaker-review';
import { useQuoteMakerWrittenNotices } from './hooks/use-quotemaker-written-notices';
import { createConfirmDialogSlot } from './services/confirm-dialog-slot';
import { isAnyModalOpen } from './utils/use-modal-accessibility';
import { cloneProjectVideoBlobs } from './services/video-blob-store';
import {
    mergeProjectSaveResultForDexieOwned,
} from './services/project-media-cache-merge';
import {
    claimProjectDuplicateAttempt,
    clearProjectDuplicateAttempt,
    reconcileProjectDuplicateFailure,
} from './services/project-duplicate-recovery';
import { restoreProjectFromDirectoryHandle } from './services/project-folder-restore';
// drop-walk extracted to useGlobalDashboardDrop
import {
    buildDocumentSourceMetadata,
    dedupeSelectedFilesByIdentity,
    ensureDocumentSourceFileId,
    mergeDocsByIdentity,
} from './utils/merge-docs-by-identity';
import {
    hasContradictoryPdfSignals,
    isPdfReferenceFile,
} from './services/reference-file-import-policy';
import { createOwnedDocumentPreviewTransaction } from './services/owned-document-preview-transaction';
import { compressIngestedImage } from './services/image';
import { isCanonicalProjectUuid } from './services/project-media-write-boundary';
import { AssessorNamesService } from './services/assessor-names';
import { BusinessPricingService } from './services/pricing';
import { subscribePricingAuthorityInvalidation } from './services/pricing-authority-channel';
import { ratesheetFingerprint } from './services/ratesheet-fingerprint';
import { ActivityLogService } from './services/activity-log';
import { ensureItemIds } from './utils/item-id';
import { buildDraftFromCorpusEntry } from './services/quote-corpus-rebuild';
import { titleSourceKey } from './utils/initials';
import { generateRbbQuoteTitle, nextAutoQuoteTitle } from './utils/quote-title';
// (photo-auto-assign, photo-client-images, photo-persistence imports moved into the consolidated `./services/photo.js` import above)
import { DataService, subscribeApiStatus, retryBackendLogin } from './services/data-service';
import { clearTokens as clearAuthTokens } from './services/data-service-api';
import { deriveQuoteDisplayTotalsFromScope } from './utils/quote-display-totals';
import { buildOverwriteReview } from './utils/project-overwrite-review';
import {
    isCostingStale,
    withoutCostingReceiptQmQuoteNo,
} from './services/project-costing';
import { DEFAULT_GENERAL_MODEL, readAudioVideoModelPreference } from './services/model-selector';
import {
    switchExpressProjectAfterSaving,
} from './services/project-switch-after-save';
import { normalizeToSnake } from './utils/jsonb-normalize';
import { bindStage5PresentationToScope, canInspectorNotesAutoRefreshPipeline, canNavigateQuoteStage, historyEntryRecordsView, historyViewState, projectHasCompletedQuote, projectHasStage5Presentation } from './utils/project-stage';
import { fingerprintValue } from './utils/quote-snapshot-diff';
import { fmtNavMoney, resolveSavedQuoteTotalForNav, computeStageNavContext, buildNavScopeTotals } from './utils/quote-nav-totals';
import {
    assignablePhotoCoverage,
    canPersistCompletedStage4PhotoReview,
    createProjectReviewEditIntent,
    importedProjectContinuation,
    isConfirmedProjectReviewEditIntent,
    photoAssignmentReceiptFingerprint,
    scopeFingerprintForAutoDraft,
    scopeFingerprintForQuoteAuthoring,
} from './utils/auto-pipeline';
import { buildQuoteJobEditorProp, useOpenQuoteJob, useQuoteJobActions } from './hooks/use-quote-job-actions';
import { useWaitForCommit } from './hooks/use-wait-for-commit';
import { metaAfterNoteEdit } from './utils/quote-note-decision';
import { getQuoteRevision } from './services/quote-jobs/quote-jobs-client';
import { formatQuoteNumberLabel } from './services/quote-jobs/quote-number';
import { QUOTE_RUN_STOP_EVENT, createQuoteRunActiveError, createQuoteRunStopListener, ignoreQuoteRunActive, isQuoteRunActiveError } from './services/quote-run-open-guard';
import {
    captureProjectRunContext,
    isProjectRunContextCurrent,
} from './utils/project-run-context';
import { isReceivablesIntelligenceDisabled } from './services/receivables-intelligence';
import { Icons } from './components/ui/Icon';
import { Toast as QmToast, ToastStack as QmToastStack } from './components/ui/Toast';
import BottomNavBar from './components/ui/BottomNavBar';
import ProjectUpdateNotice from './components/ui/ProjectUpdateNotice';
import QuoteMakerWrittenNotices from './components/quote-brain/QuoteMakerWrittenNotices';
import WorkspaceProjectTabs from './components/staging/WorkspaceProjectTabs';
// TS-060 / DEC-COUNCIL-015 T3: render-layer leaf banners extracted to
// prop-driven .tsx (state stays in App and is passed down).
import { BackendOfflineBanner } from './components/ui/BackendOfflineBanner';
import { ServerUnreachableScreen } from './components/ui/ServerUnreachableScreen';
import { planAutosave } from './utils/autosave-plan';
import { saveThenStartNew } from './utils/save-then-start-new';
import { startNewProjectWithChosenWorkflow } from './utils/start-new-project-sequence';
import { runManualSave } from './utils/manual-save-outage-notice';
import {
    NEW_PROJECT_SLOT,
    clearUnsavedProjectEdits,
    getKeptUnsavedEditsFingerprint,
    isLatestEditKeptOnDevice,
} from './services/unsaved-project-edits';
import { BootstrapPasswordBanner } from './components/ui/BootstrapPasswordBanner';
import { QuoteBrainAlertBanner } from './components/ui/QuoteBrainAlertBanner';
import { useQuoteBrainReportAccess } from './hooks/use-quote-brain-report-access';
import { InstallAppPrompt } from './components/ui/InstallAppPrompt';
import { AiCompletionRibbon } from './components/ui/AiCompletionRibbon';
// The top-nav strip's 7 leaves (StagePills, NavScopeTotals, StageNavContextBanner,
// NavMessageBanner, StopAiButton, AiPipelineStatus, NavActions) are now composed
// inside QuoteMakerNavBar; App imports only the container (TS-060 T3-NavBar compose).
import { QuoteMakerNavBar } from './components/ui/QuoteMakerNavBar';
import OperationalFieldBoundary from './components/ui/OperationalFieldBoundary';
// ─────────────────────────────────────────────────────────────────────────
// Eager imports — critical-path only. Login + Dashboard + nav chrome render
// on first paint; lazy-loading them would add a spinner where the user
// would see latency. ConfirmModal stays eager too because it's small and
// on the destructive-action confirmation path (delete project / discard
// changes) where a chunk-load spinner would be confusing UX.
// ─────────────────────────────────────────────────────────────────────────
import { AppAuxiliaryModalsContainer } from './components/modals/AppAuxiliaryModalsContainer';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AnalysisPage from './pages/AnalysisPage';
// Lazy: Quick Edit pulls in the PDF reader and the gold-standard extractor the
// moment a file is dropped, and most sessions never open it at all.
const QuickEditPage = lazy(() => import('./pages/QuickEditPage'));
const ImportQuotePage = lazy(() => import('./pages/ImportQuotePage'));
// Stage4FinalQuote was removed - all quote rendering goes through FullQuoteEditorModal
import TooltipManager from './components/ui/TooltipManager';
import UploadStatusBar from './components/ui/UploadStatusBar';
import { ModalLoadingShim, PageLoadingShim } from './components/ui/LoadingShim';

// ─────────────────────────────────────────────────────────────────────────
// Lazy imports — route-level pages + heavy modals. Each becomes its own
// Rollup chunk and is only fetched when the user actually navigates to or
// opens it. Suspense boundaries below supply the loading fallbacks.
//
// v5.13.13: Settings/History/ReconnectFolder/EmailQuote/Costings modals
// promoted from eager to lazy. They were never on the critical path —
// each opens from a button click — and their parse cost was inflating the
// main bundle. SettingsModal alone is ~80KB minified.
// ─────────────────────────────────────────────────────────────────────────
const AllQuotesPage            = lazy(() => import('./pages/AllQuotesPage'));
const TasksPage                = lazy(() => import('./pages/TasksPage'));
// The site assessor's phone surface. Lazy like its siblings: most sessions are
// office sessions that never open it, and it pulls the intake components in.
const SiteVisitsPage           = lazy(() => import('./pages/SiteVisitsPage'));
const IntakeDialogProvider     = lazy(() => import('./components/intake/IntakeDialogProvider'));
// People administration (migration 090). Lazy like every other destination —
// most sessions never open it.
// Pre-login surfaces. AcceptInvitationPage renders for someone with no account
// at all, and the forced-rotation gate renders over a signed-in but
// temporary-password session, so neither belongs behind the normal page portal.
const AcceptInvitationPage     = lazy(() => import('./pages/AcceptInvitationPage'));
const ForcePasswordChangeGate  = lazy(() => import('./components/modals/ForcePasswordChangeGate'));
const DeveloperPage            = lazy(() => import('./pages/DeveloperPage'));
const IntakePage               = lazy(() => import('./pages/IntakePage'));
const AppStageViewsContainer = lazy(() => import('./components/routing/AppStageViewsContainer'));
const AppQuoteEditorModalWrapper = lazy(() => import('./components/modals/AppQuoteEditorModalWrapper'));
const AppFileExplorerContainer = lazy(() => import('./components/modals/AppFileExplorerContainer'));
// DEC-TQ-003 (2026-05-06): coarse "AI is running" banner. Renders once
// globally; reads PipelineMonitor for the active project's running entry.
import LivePipelineBanner from './components/ui/LivePipelineBanner';
import { shouldShowToast, isPipelineBannerEnabled } from './config/notification-policy';
// v5.40.0 (UX audit Wave-1 P0 #5): wire the network-resilience signal into a
// visible top-of-app banner. Was: signal existed, no UI consumer.
import OfflineBanner from './components/ui/OfflineBanner';
import { AppTopBanners } from './components/ui/AppTopBanners';
import {
    getQuoteBrainJobRulesVersion,
    isQuoteBrainNewJob,
    isQuoteBrainRunLive,
    noteQuoteBrainBusinessMode,
    quoteBrainStage1Route,
    quoteBrainWritesNewJobs,
    quoteBrainWriteUnloaded,
    scopeSetterRefusingWhileQuoteBrainWrites,
    subscribeQuoteBrainJobRules,
} from './utils/quote-brain-project';
import { QuoteBrainBar } from './components/quote-brain/QuoteBrainBar';
import { getQuoteBrainStatus } from './services/quote-brain-runs';
// Settings v3 (2026-07-31): the seven-tab SettingsModal is retired for
// SettingsPage — one scrolling document behind a sticky index. The lazy import
// stays: the page pulls in the tenant-profile form, the folder service and the
// export service, none of which belong in the main bundle.
const SettingsModal            = lazy(() => import('./pages/SettingsPage'));
// EmailQuoteModal is imported (statically) by FullQuoteEditorModal; it has no
// JSX render site here in App.jsx, so we don't import it directly.

// Production detection. Retained for conditional UI hints.
const IS_PRODUCTION = window.location.hostname !== 'localhost'
    && window.location.hostname !== '127.0.0.1'
    && !window.location.hostname.includes('192.168.')
    && window.location.protocol !== 'file:';

// v5.11.28 (2026-04-21): Legacy SHA-256-over-localStorage auth retired on
// every origin. Auth now flows exclusively through /api/auth/login (real
// backend JWT in sessionStorage). Customer Zero hit the foot-gun where the
// legacy form showed on localhost, silently lost to devAutoLogin in the
// background, and rejected real credentials as "invalid". Setting this to
// false everywhere means: LoginPage is the JWT form, devAutoLogin handles
// the localhost fast-path, no localStorage user object anywhere.
const LEGACY_AUTH_ENABLED = false;

// ============================================
// STALE-CHUNK RECOVERY (window-level listeners)
// ============================================
// The React error-boundary classes (ErrorBoundary / AssistantErrorBoundary)
// were extracted to src/components/*.tsx (TS-060 / DEC-COUNCIL-015 T2); what
// remains here is the module-level window-error net for stale-chunk failures
// that never reach React.
// why: detects + recovers from stale-chunk lazy-load failures after a
// mid-session deploy. Vite uses content-hashed chunk names — when prod
// rebuilds, the old hash the running bundle references no longer exists, so
// `import('./X')` throws; recovery auto-reloads once to pull the new bundle.
// TS-060 / DEC-COUNCIL-015 T2: `isStaleChunkError` + `tryStaleChunkRecovery`
// were converged onto the canonical `./utils/stale-chunk-recovery` (imported
// at the top of this file; already used by analysis-pipeline + ai-learning).
// The App-local copies were a drift duplicate — the canonical additionally
// matches Firefox's "error loading dynamically imported module" and guards
// `typeof window === 'undefined'` (SSR) before touching browser globals.

// Catch stale-chunk errors that never reach React (e.g. rejected dynamic imports
// with no fallback). Fires once per page life.
if (typeof window !== 'undefined') {
    window.addEventListener('error', (e) => { if (isStaleChunkError(e.error || e)) tryStaleChunkRecovery(e.error || e); });
    window.addEventListener('unhandledrejection', (e) => { if (isStaleChunkError(e.reason)) tryStaleChunkRecovery(e.reason); });
    // Clear the one-shot guard on a successful navigation (fresh page load that didn't crash)
    window.addEventListener('load', () => { setTimeout(() => { try { sessionStorage.removeItem('qm_stale_chunk_recovered'); } catch {} }, 4000); });
}

// ============================================
// MAIN APP
// ============================================
const App: React.FC = () => {
    // Auth state — gated on JWT presence in sessionStorage (v5.11.28).
    // If there's a token, devAutoLogin either already ran successfully or a
    // prior LoginPage submission stored one; either way we treat the user as
    // logged-in with an opaque admin sentinel. If the token turns out to be
    // stale, the first /api call will 401 and the token-refresh path kicks
    // in; terminal refresh failure surfaces via the api-status banner.
    const [user, setUser] = useState(() => {
        const tok = sessionStorage.getItem('qm_access_token');
        if (!tok) return null;
        // Reload-resume auth race (2026-08-05): an access token that is BOTH
        // expired and unrefreshable (no qm_refresh_token) can never recover
        // silently — prod builds carry no devAutoLogin credentials — so
        // booting "logged-in" produces a half-alive app: every boot fetch
        // 401s ("Authentication required"), and the session-resume load used
        // to fail, toast, and delete the saved-project receipt. Boot straight
        // to the login page instead. The receipt (qm_last_view_state) is
        // deliberately LEFT ALONE: LoginPage hard-reloads after sign-in and
        // the resume then restores the open project with fresh auth.
        if (!isSessionRecoverable(tok, sessionStorage.getItem('qm_refresh_token'))) {
            try {
                sessionStorage.removeItem('qm_access_token');
                sessionStorage.removeItem('qm_refresh_token');
            } catch { /* storage sealed — LoginPage still renders via null below */ }
            return null;
        }
        // Restore the REAL role from the JWT claims instead of a hardcoded
        // 'admin' sentinel — otherwise a platform_admin loses the GLOBAL
        // learning controls on every refresh until they re-login. Display
        // gating only — the backend 403 remains the enforcement layer, so
        // trusting the unverified client-side decode is safe.
        const claims = decodeJwtClaims(tok);
        return {
            id: claims?.userId || 'session',
            tenantId: claims?.tenantId || null,
            name: 'Admin',
            role: claims?.role || 'admin',
            email: 'admin@rjbird.com.au',
            mustChangePassword: typeof claims?.mustChangePassword === 'boolean'
                ? claims.mustChangePassword
                : undefined,
            passwordRotationBootState: passwordRotationStateFromClaims(claims),
        };
    });
    // Browser builds may select a tenant cache namespace from the signed JWT,
    // but no customer-owned local data is readable until /auth/me confirms the
    // exact tenant and user captured when the Dexie singleton was constructed.
    // Vitest keeps its isolated in-memory fixture behaviour; the compiled UAT
    // lane proves the production lock.
    const enforceVerifiedLocalTenant = import.meta.env.MODE !== 'test';
    const [localTenantSessionVerified, setLocalTenantSessionVerified] = useState(
        () => !enforceVerifiedLocalTenant,
    );
    const localSessionShutdownRef = useRef(null);
    const teardownAuthenticatedLocalSession = useCallback(({
        tokenReason = 'invalid-session',
        tokensAlreadyCleared = false,
    } = {}) => {
        setLocalTenantSessionVerified(false);
        setUser(null);
        if (!tokensAlreadyCleared) {
            try { clearAuthTokens({ reason: tokenReason }); } catch { /* best-effort */ }
        }
        if (!enforceVerifiedLocalTenant) return Promise.resolve();
        if (!localSessionShutdownRef.current) {
            localSessionShutdownRef.current = shutdownLocalTenantRuntime().catch((error) => {
                console.error('[QM] Local tenant session shutdown failed:', error?.message || error);
                closeLocalTenantDatabase();
            });
        }
        return localSessionShutdownRef.current;
    }, [enforceVerifiedLocalTenant]);
    // A signed false claim is sufficient to render. A legacy token with no
    // claim remains fail-closed until /auth/me supplies the authoritative state.
    const passwordRotationBootState = user
        ? (user.passwordRotationBootState || passwordRotationStateFromClaims(user))
        : null;
    // Keep effect dependencies primitive and stable while still reacting when
    // /auth/me replaces the JWT role estimate with its authoritative access
    // map. A grant starts tenant boot work; a revocation aborts it.
    const hasQuotemakerAccess = passwordRotationBootState === 'clear'
        && effectiveAccess(user).quotemaker === true;

    // ─── Backend API status (v5.12.0 rewrite, 2026-04-22) ──────────────────
    // `disabled` flips true when a WRITE fails with an unreachable-class
    // error (network/5xx/timeout). Clears on the next successful backend op.
    // Not a latch — Dexie-first failover was retired in v5.12.0. The banner
    // below subscribes so the user sees reconnection state in real time
    // instead of data silently diverging from Postgres.
    const [apiStatus, setApiStatus] = useState({ disabled: false, failCount: 0 });
    const [apiRetrying, setApiRetrying] = useState(false);
    const [apiRetryMessage, setApiRetryMessage] = useState(null);
    // Dismissed for THIS outage only (connectivity warnings are dismissible,
    // CLAUDE.md). The next outage shows it again.
    const [apiBannerDismissed, setApiBannerDismissed] = useState(false);
    useEffect(() => subscribeApiStatus(setApiStatus), []);
    useEffect(() => {
        if (!apiStatus.disabled) {
            setApiBannerDismissed(false);
            setApiRetryMessage(null);
        }
    }, [apiStatus.disabled]);
    const handleApiRetry = async () => {
        if (apiRetrying) return;
        setApiRetrying(true);
        setApiRetryMessage(null);
        try {
            const ok = await retryBackendLogin();
            if (ok) {
                setApiRetryMessage({ tone: 'success', text: 'QuoteMaker is back. Your changes will save now.' });
                // Give the user a beat to see the success, then auto-dismiss
                setTimeout(() => setApiRetryMessage(null), 2800);
            } else {
                setApiRetryMessage({ tone: 'error', text: "Still no answer from QuoteMaker's server. We'll keep trying by ourselves." });
            }
        } catch (e) {
            setApiRetryMessage({ tone: 'error', text: "Still no answer from QuoteMaker's server. We'll keep trying by ourselves." });
            console.warn('[QM] Try now failed:', e?.message || e);
        } finally {
            setApiRetrying(false);
        }
    };

    useEffect(() => {
        // v5.11.28: one-shot cleanup of retired legacy auth keys. Does NOT
        // touch `user` state — that's gated on JWT presence in sessionStorage
        // via the useState initializer above. If we have a JWT, we stay
        // logged in; if not, LoginPage is already rendering.
        localStorage.removeItem('rjb_current_user');
        localStorage.removeItem('rjb_users');

        // v5.11.28: proactively fire devAutoLogin on mount when no token
        // exists. Explicit non-Docker dev builds can opt into local
        // VITE_DEV_AUTH_EMAIL/PASSWORD; prod-style Docker builds leave those
        // empty and use LoginPage.
        if (!user && !sessionStorage.getItem('qm_access_token')) {
            (async () => {
                try {
                    const { devAutoLogin } = await import('./services/data-service-api.js');
                    await Promise.race([
                        devAutoLogin(),
                        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000)),
                    ]);
                    if (sessionStorage.getItem('qm_access_token')) {
                        // The current module was constructed against quarantine
                        // before devAutoLogin acquired a tenant token. Reload so
                        // the immutable DB binding is selected from that token.
                        window.location.reload();
                        return;
                    }
                } catch (err) {
                    // Silent — LoginPage is already showing and will prompt.
                    console.info('[QM] Auto-login unavailable, showing sign-in form');
                }
            })();
        }
        if (!localTenantSessionVerified) return undefined;

        // Activate pipeline monitoring only after authenticated tenant binding.
        PipelineMonitor.activate();

        // PB-CP-1 cleanup (file-explorer-redesign 2026-05-11): opportunistic
        // FileSystemObserver registration for any sync-folder root whose
        // FSA permission is still 'granted' from the prior session (no
        // user-gesture needed). Council seat #4 PB-CP-0 hybrid-boot path.
        // The gesture-gated half lives in SyncFolderService.setHandle().
        // Lazy import + non-awaited fire-and-forget so app boot stays fast.
        (async () => {
            try {
                const { opportunisticBootRegistration } = await import('./services/file-explorer-observer');
                await opportunisticBootRegistration();
            } catch (err) {
                console.warn('[QM] file-explorer-observer opportunistic boot registration failed:', err?.message);
            }
        })();

        if (!sessionStorage.getItem('qm_access_token')) return undefined;
        // ─── Everything below this line is QUOTING-BRAIN boot work ──────────
        //
        // Business-bio seeding, the tenant-owner warm and the Tier-5 model
        // probe. All of it serves the Stage 1-5 pipeline.
        //
        // A Site Assessor reaches NONE of those surfaces: their whole product
        // is a phone list of their own visits. Running this for them spends AI
        // budget on somebody who cannot see the result, over site mobile data,
        // and it was doing exactly that — the assessor mobile UAT caught a live
        // POST /api/ai/embeddings on the capture surface (2026-08-17).
        //
        // Gated on the AREA rather than on a role string, so a viewer and an
        // office admin are covered by the same guard, and so granting somebody
        // the pipeline through `area_overrides` also grants them its warm-up.
        if (!hasQuotemakerAccess) {
            devLog('[QM] Boot: skipping quoting-brain warm-up; this account has no pipeline access.');
            return undefined;
        }

        // TS-060: App boot warmup tasks extracted to src/services/app-boot-warmup.ts
        runBackgroundBootWarmup();
        // Stable identity fields, not `user`: the /auth/me enrichment below
        // mints a new object. The tenant id is included because tenant-owned
        // maintenance must run again if an authenticated workspace changes in
        // the same browser session.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return undefined;
    }, [user?.id, user?.tenantId, hasQuotemakerAccess, localTenantSessionVerified]);

    // Portal state with transition tracking.
    //
    // The landing page is role-dependent. A site assessor's account grants only
    // 'site-visits', and dashboard/settings are the always-allowed floor — so
    // without this they would sign in on their phone and land on the Run Sheet,
    // a list of every quote in the business, which is neither their job nor
    // something they should be reading. `user` is built synchronously from the
    // JWT claims before this line, so the role is already in hand and there is
    // no first-paint flash of the wrong page.
    //
    // Deliberately NOT a general "first allowed area" rule: every other role
    // legitimately starts on the Run Sheet, and inventing a per-role landing
    // matrix would be a lot of behaviour to maintain for one real case.
    const [currentPage, setCurrentPage] = useState(
        // Both spellings of the site-visit role land here — see isSiteVisitRole.
        () => (isSiteVisitRole(user?.role) ? 'site-visits' : 'dashboard'),
    );

    // v5.37.5 (F1/F11) — Routing sync. `popstateInFlightRef` flips true
    // while a popstate (back/forward) event is updating currentPage so the
    // currentPage-watcher useEffect doesn't re-push the same URL into
    // history and double the entries.
    const popstateInFlightRef = useRef(false);
    // Defer the initial-mount URL → page sync until ONE auth tick so handleNavigate's
    // closures see the latest user.
    const didInitialUrlSyncRef = useRef(false);
    // v5.36.30: pending-select state for the Stage 1 → intake reverse link.
    // When IntakeContextChip clicks "View job", handleNavigate stashes the
    // jobId here + flips currentPage to 'intake'. IntakePage reads + clears
    // it on mount so a refresh / re-nav doesn't keep auto-selecting the
    // same job.
    const [intakePendingSelectJobId, setIntakePendingSelectJobId] = useState(null);
    // 2026-05-19 (Intake Arsenal): mirror IntakePage's selected-job state up
    // here via the qm:intake-selected CustomEvent so the AssistantPanel's
    // toolContext can carry it. Stays null unless currentPage === 'intake'.
    const [intakeAssistantContext, setIntakeAssistantContext] = useState({
        selectedJobId: null,
        selectedJobStatus: null,
        selectedJobShortLine: null,
        selectedDoctecId: null,
        sidebarTab: null,
        attention: { urgent: 0, soft: 0, total: 0 },
    });
    const [prevPage, setPrevPage] = useState(null);
    const [pageDirection, setPageDirection] = useState('forward');
    const [isPageTransitioning, setIsPageTransitioning] = useState(false);
    // Cross-page navigation state - consumed once by target page then cleared
    const [pageNavOptions, setPageNavOptions] = useState(null);

    // Reset counter - bumped on resetForm() so Stage1 can reset its local state
    const [resetCounter, setResetCounter] = useState(0);

    // CRM refresh trigger - increment to tell ClientsPage to reload after AI merge actions
    const [clientRefreshTrigger, setClientRefreshTrigger] = useState(0);

    const receivablesDisabled = isReceivablesIntelligenceDisabled();

    // The Quote Brain report and its alerts (slice LIVE): the server decides
    // whether this account reads them; the menu row and banner follow.
    const quoteBrainReport = useQuoteBrainReportAccess(user?.id || user?.userId || null);

    // Page order for determining animation direction
    // Settings v2 (v5.34.0, 2026-05-18): 'settings' is now a true page,
    // not a modal overlay. Placed at the end of PAGE_ORDER because it's
    // a destination reachable via openSettings() from anywhere — it's
    // not a stage in the linear back/next flow that PAGE_ORDER's index
    // arithmetic drives.
    const PAGE_ORDER = receivablesDisabled
        ? ['dashboard', 'tasks', 'quotemaker', 'quotes', 'intake', 'clients', 'business-training', 'aimodels', 'audit', 'developer', 'component-library', 'settings']
        : ['dashboard', 'tasks', 'quotemaker', 'quotes', 'intake', 'clients', 'receivables', 'business-training', 'aimodels', 'audit', 'developer', 'component-library', 'settings'];

    // ─── QuoteMaker view-state persistence ─────────────────────────────────
    // sessionStorage key that mirrors the user's last-known QuoteMaker view state.
    // Used to restore stage and the view when the user clicks the
    // QuoteMaker nav button after being on another page.
    //
    // Why sessionStorage and not localStorage:
    //   - Per-tab isolation (multiple tabs = independent sessions)
    //   - Cleared when the tab closes (no stale state across days)
    //   - Survives navigation but not browser restart
    //
    // Why an object snapshot, not individual keys:
    //   - Atomic write/read avoids torn state
    //   - Easy to validate the whole shape on read
    const QM_STATE_KEY_BASE = 'qm_last_view_state';
    const QM_STATE_KEY = getVerifiedTenantBrowserKey(QM_STATE_KEY_BASE);

    // Safe parse helper - returns null if anything is wrong. The body lives in
    // services/app-reload-routing.ts so the reload tests run the real reader.
    const readQmStateFromStorage = () => readQuoteMakerViewReceipt(QM_STATE_KEY);

    // Custom navigation handler with transitions — the handleNavigate BODY
    // moved VERBATIM into src/hooks/use-app-routing.ts (TS-060 T4 slice 6,
    // DEC-COUNCIL-015). `handleNavigate` is destructured from useAppRouting
    // BELOW the useQuoteEditor alias block + canNavigateToQuoteStage region
    // (TDZ: the body reads editor/quoteEditorOpen/saveCurrentProject,
    // declared there). References textually ABOVE that instantiation — the
    // initial URL→page sync effect, setSettingsOpen/openSettings/
    // handleLeaveSettings, and the dropDepsRef mirror effect — all execute
    // inside effect/handler bodies strictly after render completes, so the
    // destructured const is assigned by call time (the same lazy pattern the
    // dropDepsRef comment documents).

    // ─── v5.37.5 (F1/F11) — URL ↔ currentPage routing sync ─────────────────
    //
    // Three independent effects keep the URL and currentPage in sync:
    //
    // 1. Initial-mount sync: once the user is authenticated, read the URL.
    //    If it maps to a known page that differs from currentPage's default
    //    ('dashboard'), call handleNavigate to go there. Covers fresh
    //    page-load on a deep URL (e.g. user types localhost:8888/intake or
    //    clicks a bookmark) and reload-on-page (F11 from the UAT register).
    //    Runs ONCE per auth session via didInitialUrlSyncRef.
    //
    // 2. currentPage-change → pushState: when currentPage flips via menu
    //    click, push the new path to history so back/forward works. Skipped
    //    when the flip was driven by popstate (popstateInFlightRef) and when
    //    the URL already matches (avoids duplicate history entries during
    //    component re-render).
    //
    // 3. popstate listener: when the user clicks browser back/forward, read
    //    the URL and call setCurrentPage directly. Sets popstateInFlightRef
    //    so the pushState effect doesn't loop.
    //
    // The effect body lives in services/app-reload-routing.ts
    // (runInitialUrlSync) so the reload order is testable with the real code.
    // It waits for localTenantSessionVerified (RT1, 2026-09-29): until GET
    // /auth/me confirms the business, the tab's saved-project record cannot be
    // read, and a reload on /quotemaker synced that early opened an empty
    // project and then saved it over the record.
    useEffect(() => {
        // Wait for /auth/me too (outage lane, 2026-09-29, kept at the merge
        // with RT1). During a server outage verification waits on the server,
        // so this is also what brings the project (and any edits kept on this
        // device) back once it answers. The wait applies only where the
        // verified-tenant lock is enforced: in the vitest build the business
        // is never re-verified after a sign-out (use-tenant-session-
        // verification sets it true only when enforcing), so there it must
        // not block the sync.
        if (enforceVerifiedLocalTenant && !localTenantSessionVerified) return;
        runInitialUrlSync({
            user,
            sessionVerified: localTenantSessionVerified || !enforceVerifiedLocalTenant,
            syncedRef: didInitialUrlSyncRef,
            currentPage,
            navigate: (page) => handleNavigate(page),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, localTenantSessionVerified]);

    // Menu visibility is guidance, not an access boundary. A stale bookmark,
    // browser Back entry or hand-edited URL is redirected when its area has
    // been revoked. Sensitive routes also keep their own render guard so they
    // cannot mount during the redirect tick. The backend remains authoritative.
    useEffect(() => {
        if (!user) return;
        const deniedRoute = resolveDeniedAppAreaRoute(currentPage, user);
        if (!deniedRoute) return;
        const fallback = deniedRoute.fallbackPage;
        if (fallback === currentPage) return;
        window.history.replaceState({ page: fallback }, '', pageToPath(fallback));
        setCurrentPage(fallback);
    }, [currentPage, user?.id, user?.role, user?.access, user?.areaOverrides]);

    // The Quote Brain report is one account's (slice LIVE). Anyone else who
    // types or follows its address lands on the dashboard once the server has
    // said no, so the page does not show that it exists.
    useEffect(() => {
        if (currentPage !== 'quote-brain-report' || !quoteBrainReport.checked || quoteBrainReport.visible) return;
        window.history.replaceState({ page: 'dashboard' }, '', pageToPath('dashboard'));
        setCurrentPage('dashboard');
    }, [currentPage, quoteBrainReport.checked, quoteBrainReport.visible]);

    // v5.41.172 — the separate URL-sync pushState + popstate handlers that used
    // to live here have been REMOVED. They formed a second browser-history layer
    // (entries shaped { qmPage }) alongside the { page, stage } layer further
    // down, which: (a) double-pushed on every page nav (so Back needed two
    // presses to leave a page), (b) let the address bar drift out of sync with
    // the rendered page, and (c) blanked the screen when Back popped a { qmPage }
    // entry into the { page, stage } handler. There is now ONE history layer (see
    // "Unified in-app browser history" below) that carries { page, stage } AND
    // keeps the URL in sync. The initial deep-link URL sync above is retained.

    // Settings
    //
    // v5.13.7: Provider keys retired from the user surface. The QuoteMaker
    // dev/service provider configures OPENROUTER_API_KEYS on the backend
    // (1 primary + up to 4 backups, round-robin in ai-gateway.js). Every AI
    // call goes through POST /api/ai/* with the user's JWT — the frontend
    // never holds a provider key.
    //
    // We keep the `apiKey` state variable for two reasons:
    //   1. Hundreds of callsites accept it as a parameter; ripping the
    //      threading would be a sprawling, bug-prone refactor for zero user-
    //      visible benefit.
    //   2. Dev/admin users with `qm_dev_keys=1` can still populate it via
    //      Settings → API Keys when they want to test browser-direct mode.
    //
    // For end-users (no dev flag), we seed a `'gateway'` sentinel so the
    // existing `if (!apiKey) return` guards across App.jsx don't trip. The
    // sentinel is never sent over the wire — `_isBackendGatewayActive()` in
    // ai-api.js prefers /api/ai/chat and only consults `apiKey` in the
    // browser-direct fallback (which non-dev users never reach).
    const [apiKey, setApiKey] = useState(() => {
        try {
            const devEnabled = localStorage.getItem('qm_dev_keys') === '1';
            if (devEnabled) {
                // Dev/admin path: keep the historic sessionStorage flow so a
                // QM staff member testing browser-direct mode keeps their key.
                const session = sessionStorage.getItem('qm_apikey');
                if (session) return session;
            }
        } catch { /* SSR / Safari private mode — fall through to sentinel */ }
        // End-user (and dev with no key configured): sentinel that satisfies
        // the `!apiKey` guards without leaking anything sensitive.
        return 'gateway';
    });
    const [model, setModel] = useState(() => localStorage.getItem('qm_model') || DEFAULT_GENERAL_MODEL);
    const [audioModel, setAudioModel] = useState(() => readAudioVideoModelPreference());
    // Light theme only - optimized for construction site visibility
    const theme = 'light';

    useEffect(() => {
        // v5.13.7: scrub legacy `qm_apikey` from BOTH storages on boot for
        // end-users. The key is no longer needed on the client. We leave the
        // sessionStorage entry alone for dev/admin users (who explicitly opt
        // in via the qm_dev_keys flag) so their browser-direct workflows
        // keep working.
        try {
            const devEnabled = localStorage.getItem('qm_dev_keys') === '1';
            if (!devEnabled) {
                sessionStorage.removeItem('qm_apikey');
                localStorage.removeItem('qm_apikey');
            } else {
                // Dev path: still migrate any leftover localStorage key into
                // sessionStorage so the secure-storage invariant holds.
                const legacyApiKey = localStorage.getItem('qm_apikey');
                if (legacyApiKey && !sessionStorage.getItem('qm_apikey')) {
                    sessionStorage.setItem('qm_apikey', legacyApiKey);
                }
                localStorage.removeItem('qm_apikey');
            }
        } catch { /* storage access denied — no-op */ }
    }, []);
    // v5.13.7: pre-auth 401/403 promise-rejection swallow lives in
    // src/boot/silence-pre-auth-rejections.js, imported synchronously from
    // main.jsx so the listener is in place before any React component mounts.
    // (A useEffect-based install raced with child components' fire-and-forget
    // API reads, which fired their unhandledrejection before App's effect.)
    // Settings v2 (v5.34.0, 2026-05-18): Settings is now a true full page
    // (currentPage === 'settings'), not a fixed-position overlay. The
    // initial-tab + "back to prev page" wiring lives here.
    //
    // settingsInitialTab — preserves the v5.33.0 openSettings(tab) call
    //   sites: openSettings('data'), openSettings('account'), etc. The
    //   value is read by the Settings page on mount via the initialTab
    //   prop and resolved through resolveTab() inside the page.
    // settingsReturnPage — captures the page the user was on when they
    //   opened Settings so the Back button can return them there. Falls
    //   back to 'dashboard' for the first invocation.
    const [settingsInitialTab, setSettingsInitialTab] = useState('business');
    const [settingsReturnPage, setSettingsReturnPage] = useState('dashboard');
    const settingsDirtyRef = useRef({ count: 0, fieldNames: [] });
    const [settingsDirtyCount, setSettingsDirtyCount] = useState(0);
    const siteVisitsExitGuardRef = useRef({ busyCount: 0, failedCount: 0, sideRequestBusy: false });
    const [siteVisitsExitRiskCount, setSiteVisitsExitRiskCount] = useState(0);
    const handleSettingsDirtyStateChange = useCallback((summary) => {
        const next = {
            count: Math.max(0, Number(summary?.count) || 0),
            fieldNames: Array.isArray(summary?.fieldNames) ? summary.fieldNames.map(String) : [],
        };
        settingsDirtyRef.current = next;
        setSettingsDirtyCount(next.count);
    }, []);
    const handleSiteVisitsExitGuardStateChange = useCallback((summary) => {
        const next = {
            busyCount: Math.max(0, Number(summary?.busyCount) || 0),
            failedCount: Math.max(0, Number(summary?.failedCount) || 0),
            sideRequestBusy: summary?.sideRequestBusy === true,
        };
        siteVisitsExitGuardRef.current = next;
        setSiteVisitsExitRiskCount(next.busyCount + next.failedCount + (next.sideRequestBusy ? 1 : 0));
    }, []);
    // Derived alias: legacy call sites read `settingsOpen` (boolean). It
    // is now equivalent to "currentPage === 'settings'".
    const settingsOpen = currentPage === 'settings';
    // Legacy setter shim. setSettingsOpen(true) → openSettings(); false →
    // navigate back. Preserves the old API surface without forcing every
    // call site to migrate. The 6 internal call sites are mid-file inside
    // hot AI-pipeline branches — keeping the shim avoids a wide diff.
    const setSettingsOpen = (open) => {
        if (open) {
            openSettings('business');
        } else if (currentPage === 'settings') {
            handleNavigate(settingsReturnPage || 'dashboard');
        }
    };
    // Default landing changed 'api' -> 'business' (Settings v3). The old chain
    // was: 'api' -> resolveTab rewrote to 'keys' -> 'keys' was dev-gated ->
    // redirected to 'models'. Every real operator landed on a screen whose job
    // is picking an LLM. They now land on the business identity.
    const openSettings = (tab = 'business') => {
        setSettingsInitialTab(tab);
        // Capture where the user came from BEFORE the navigate flips
        // currentPage. Use the raw currentPage state (not currentPageRef —
        // ref isn't declared until later in this component but the state
        // it mirrors IS in scope here).
        if (currentPage && currentPage !== 'settings') {
            setSettingsReturnPage(currentPage);
        }
        handleNavigate('settings');
    };
    // Bootstrap-credential session flag (set by LoginPage from the backend's
    // usingBootstrapPassword login response; survives LoginPage's hard reload).
    const [bootstrapPwLogin, setBootstrapPwLogin] = useState(() => {
        try { return sessionStorage.getItem('qm_bootstrap_pw_login') === '1'; } catch { return false; }
    });
    const handleLeaveSettings = () => {
        handleNavigate(settingsReturnPage || 'dashboard');
    };
    const [toast, setToast] = useState(null);
    useEffect(() => {
        if (toast) {
            // Allow per-toast duration override (e.g. longer "Saving…" indicators).
            // `sticky: true` disables auto-clear entirely - caller must clear manually.
            if (toast.sticky) return undefined;
            const ms = typeof toast.duration === 'number' && toast.duration > 0 ? toast.duration : 3000;
            const timer = setTimeout(() => setToast(null), ms);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    // ─── Safe-write failure toasts ───────────────────────────────────────────
    // `services/safe-write.js` dispatches `qm:safe-write-fail` when a fire-and-
    // forget DataService write rejects under the v5.12.0 API-only contract.
    // Surface a single toast (safe-write already dedupes per (what,error.message)
    // in a 4-second window, so loops won't spam). We bias toward 'error' for
    // connectivity-class failures — the reconnection banner handles the status,
    // but the toast tells the user WHICH write just failed.
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const onFail = (e) => {
            const detail = e?.detail || {};
            setToast({
                message: detail.message || `Save failed (${detail.what || 'write'})`,
                type: 'error',
                duration: 5000,
            });
        };
        window.addEventListener('qm:safe-write-fail', onFail);
        return () => window.removeEventListener('qm:safe-write-fail', onFail);
    }, []);

    // K3: the sent-quote recorder retries failed writes by itself; this shows
    // its first failure, a refusal and a late success, and retries whatever an
    // earlier session left in its outbox.
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const onRecord = (e: Event) => {
            const detail = (e as CustomEvent<SentQuoteRecordEventDetail>).detail;
            if (!detail?.message) return;
            setToast({
                message: detail.message,
                type: detail.status === 'failed' ? 'error' : detail.status === 'retrying' ? 'warning' : 'success',
                duration: detail.status === 'recorded' ? 4000 : 8000,
            } as any);
        };
        window.addEventListener(SENT_QUOTE_RECORD_EVENT, onRecord);
        const stopRecorder = startSentQuoteRecorder();
        return () => {
            stopRecorder();
            window.removeEventListener(SENT_QUOTE_RECORD_EVENT, onRecord);
        };
    }, []);

    // DEMO-SPEC E4: in a demo business one capture-phase listener cancels any
    // mailto: link to an address that does not end in .example, and says why
    // here. A real business is never touched (the guard reads the flag per click).
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const uninstall = installDemoMailLinkGuard(document);
        const onRefused = (e: Event) => {
            const message = (e as CustomEvent<{ message?: string }>).detail?.message || 'This demo business does not send email.';
            setToast({ message, type: 'warning', duration: 6000 } as any);
        };
        window.addEventListener(DEMO_SEND_REFUSED_EVENT, onRefused);
        return () => {
            uninstall();
            window.removeEventListener(DEMO_SEND_REFUSED_EVENT, onRefused);
        };
    }, []);


    // ─── 2026-05-19 (Intake Arsenal): subscribe to IntakePage's broadcasts ──
    // so the AssistantPanel's toolContext carries the operator's current selection +
    // inbox snapshot. Two events: qm:intake-selected (selected job/doctec) +
    // qm:intake-attention (counts). Both fire every time the underlying state
    // changes; we coalesce into a single context object.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onSelected = (e) => {
            const d = e?.detail || {};
            setIntakeAssistantContext((prev) => ({
                ...prev,
                selectedJobId: d.selectedJobId || null,
                selectedJobStatus: d.selectedJobStatus || null,
                selectedJobShortLine: d.selectedJobShortLine || null,
                selectedDoctecId: d.selectedDoctecId || null,
                sidebarTab: d.sidebarTab || null,
            }));
        };
        const onAttention = (e) => {
            const d = e?.detail || {};
            setIntakeAssistantContext((prev) => ({
                ...prev,
                attention: {
                    urgent: Number(d.urgent || 0),
                    soft: Number(d.soft || 0),
                    total: Number(d.total || 0),
                },
            }));
        };
        window.addEventListener('qm:intake-selected', onSelected);
        window.addEventListener('qm:intake-attention', onAttention);
        return () => {
            window.removeEventListener('qm:intake-selected', onSelected);
            window.removeEventListener('qm:intake-attention', onAttention);
        };
    }, []);

    // ─── Session-expired sticky toast (F-02, v5.13.7) ───────────────────────
    // data-service-api.js fires `qm:session-expired` (idempotent over 30 s)
    // when apiFetch exhausts its refresh + re-login retries against a 401.
    // Surface a sticky toast with an action that mirrors the existing logout
    // flow: clear tokens, drop the user back to the dashboard/login screen.
    // Without this, the boot-time unhandledrejection swallow would silently
    // hide every session-expiry 401 (audit finding F-02).
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const onExpired = () => {
            // Authentication is part of the Pricing snapshot's authority.
            // Dropping the principal triggers the read-only snapshot effect's
            // cleanup immediately, so a previously-ready rate book cannot
            // remain trusted after terminal session expiry.
            void teardownAuthenticatedLocalSession({ tokenReason: 'invalid-session' });
            setToast({
                message: 'Your session expired — please log in again.',
                type: 'error',
                sticky: true,
                action: {
                    label: 'Log in',
                    onClick: () => {
                        try {
                            localStorage.removeItem('rjb_current_user');
                            clearAuthTokens();
                        } catch { /* storage may be sealed; reload below clears state */ }
                        window.location.reload();
                    },
                },
            });
        };
        window.addEventListener('qm:session-expired', onExpired);
        return () => window.removeEventListener('qm:session-expired', onExpired);
    }, [teardownAuthenticatedLocalSession]);

    // data-service-api coordinates explicit logout across same-principal tabs.
    // sessionStorage changes are not observable across tabs, so it emits this
    // local event after applying a peer `session-cleared` message. Clear the
    // React principal immediately; the strict Pricing effect then invalidates
    // its snapshot without waiting for a later request to fail with 401.
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const onPeerSessionCleared = () => {
            setCurrentBusinessPricingSnapshot(null);
            setBusinessPricingSnapshotStatus('unavailable');
            void teardownAuthenticatedLocalSession({ tokensAlreadyCleared: true });
        };
        window.addEventListener('qm:session-cleared', onPeerSessionCleared);
        return () => window.removeEventListener('qm:session-cleared', onPeerSessionCleared);
    }, [teardownAuthenticatedLocalSession]);

    // ─── Identity hydration from the server (2026-08-05) ────────────────────
    // The `user` state above is built synchronously from the JWT claims so the
    // first paint has a role to gate the menu with. New JWTs also carry the
    // password-rotation boolean; legacy tokens do not. No JWT carries per-user
    // area overrides. GET /api/auth/me resolves legacy rotation state and the
    // access map; `access` computed here client-side is a first-paint estimate,
    // the server's map is the truth (and the backend re-checks every request
    // regardless, so a tampered client gains a menu entry and a 403).
    //
    // While the identity check cannot reach the server (a deploy, a dropped
    // connection) the session is KEPT and `sessionCheckOutage` drives the
    // "QuoteMaker is updating" page below instead of the login form. Only a
    // real 401/403 from the server signs the operator out (outage lane,
    // 2026-09-29).
    const {
        outage: sessionCheckOutage,
        retryNow: retrySessionCheck,
    } = useTenantSessionVerification({
        user,
        setUser,
        enforceVerifiedLocalTenant,
        setLocalTenantSessionVerified,
        teardownAuthenticatedLocalSession,
        passwordRotationBootState,
    });
    // ─── Completion ribbon notification ──────────────────────────────────────
    // Persistent ribbon that appears when AI processing finishes while the user
    // is on a different page. Stays until dismissed or the user navigates to QM.
    const [ribbonNotification, setRibbonNotification] = useState(null);
    const [ribbonDismissing, setRibbonDismissing] = useState(false);
    const currentPageRef = useRef(currentPage);
    useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);
    const dismissRibbon = useCallback(() => {
        setRibbonDismissing(true);
        setTimeout(() => { setRibbonNotification(null); setRibbonDismissing(false); }, 300);
    }, []);

    // Confirm modal state - must be before any conditional returns
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {}, onCancel: null, confirmText: 'Confirm', cancelText: 'Cancel', confirmColor: 'red', icon: 'warning', zIndex: undefined, defaultFocus: 'cancel', hint: null });

    // Stage navigation - must be before any conditional returns
    const [stage, setStage] = useState(1);
    // Mirror the current stage onto stageRef so async paths (e.g. WMS completion
    // 30-90s after kick-off) can read the user's CURRENT location instead of a
    // stale closure value when deciding whether to auto-advance.
    useEffect(() => { stageRef.current = stage; }, [stage]);
    // Navigation-intent epoch: advances on every stage change, however it was
    // made, and (below) on page changes. A pending Stage 3 NEXT or re-quote extraction
    // compares it so a Back (or a Back and return to the same view) while it
    // ran is honoured, which the live stage alone cannot show.
    // setStageWithTransition bumps it synchronously (so a click cannot be
    // overtaken by an already-resolved await); this effect covers the direct
    // setStage callers (browser history, scope-jump, batch).
    const navigationIntentEpochRef = useRef(0);
    useEffect(() => { navigationIntentEpochRef.current += 1; }, [stage]);
    // Accepted-operator-navigation receipt (packet 1B-B). Unlike the broad epoch
    // above it does NOT count the pipeline's own stage changes (Risk painting
    // Stage 2, handing off to Stage 3, Work Method advancing to Stage 4), so a
    // run can capture it when it begins presenting its work and ask at its
    // delayed handoff whether the operator has moved since. It advances at the
    // operator's stage change (setStageWithTransition below), an accepted menu or
    // history navigation (use-app-routing), the scope-issue jump and the
    // Summary/Data Entry choices. Rejected and no-op requests never reach it.
    const operatorNavigationEpochRef = useRef(0);

    // Keep visible Stage in sync with background batch progression (Quote Run)
    useEffect(() => {
        const onBatchStageAdvance = (e: Event) => {
            const detail = (e as CustomEvent)?.detail;
            if (!detail?.projectId || !detail?.stage) return;
            if (detail.projectId === currentProjectIdRef.current && currentPageRef.current === 'quotemaker') {
                const stageNum = typeof detail.stage === 'number'
                    ? detail.stage
                    : parseInt(String(detail.stage).replace(/\D/g, ''), 10);
                if (Number.isFinite(stageNum) && stageNum >= 1 && stageNum <= 5) {
                    setStage(stageNum);
                }
            }
        };
        window.addEventListener('qm-batch-stage-advance', onBatchStageAdvance);
        return () => window.removeEventListener('qm-batch-stage-advance', onBatchStageAdvance);
    }, []);
    const [aiAssistantState, setAiAssistantState] = useState(null);
    const scopeSnapshotCallbackRef = useRef(null);
    // Extract pushScopeSnapshot callback from Stage3's assistant state; clear when leaving scope builder
    useEffect(() => {
        if (aiAssistantState?.pushScopeSnapshot) {
            scopeSnapshotCallbackRef.current = aiAssistantState.pushScopeSnapshot;
        } else {
            scopeSnapshotCallbackRef.current = null;
        }
    }, [aiAssistantState]);
    const [globalAiAssistantOpen, setGlobalAiAssistantOpen] = useState(false);
    // Set while a stage cockpit shows the Assistant tab (assistant-dock.ts).
    const assistantDockHost = useAssistantDockHost();
    const [globalAiProcessing, setGlobalAiProcessing] = useState(false);
    const [aiAssistantViewToggleSignal, setAiAssistantViewToggleSignal] = useState(0);
    const [hasViewedRationale, setHasViewedRationale] = useState(false);
    // Project-history filter chip state - lifted here so the BottomNavBar can
    // render the same filter affordance when the user is on the Projects page.
    // Peter 2026-04-20: "move the filter buttons to the bottom navbar."
    const [quotesFilter, setQuotesFilter] = useState('all');
    const [prevStage, setPrevStage] = useState(1);
    const [stageDirection, setStageDirection] = useState('forward');
    const [isStageTransitioning, setIsStageTransitioning] = useState(false);
    const [completedStages, setCompletedStages] = useState(new Set());
    const inspectorNotesCanAutoRefresh = useMemo(
        () => canInspectorNotesAutoRefreshPipeline({ stage, completedStages }),
        [stage, completedStages],
    );

    // Custom stage setter with transition tracking. The OPERATOR's flavour is
    // the default for every caller; only the Risk and Work Method hooks are given
    // the automatic flavour for their own advances (see utils/stage-transition).
    //
    // 5C / C26: the changer's no-op rule ("a request for the stage already shown does nothing") must compare with the stage that is ACTUALLY
    // PAINTED, read live. It used to compare with `stage` as of the render that built the changer, and a Risk run keeps the changer of the render
    // it began in: begun on Stage 3 it painted Stage 2 and then asked for Stage 3, which that stale value skipped, leaving Risk on screen while the
    // run's receipt said Stage 3 had been presented. `paintedStageRef` is assigned in render and set by the changer's own `setStage` the moment it
    // paints, so two requests in one tick (2 then 3) each see the stage the previous one painted. It is NOT `stageRef`: Risk assigns that eagerly
    // to its hand-off stage before it asks for the stage, which would recreate the same skip.
    const paintedStageRef = useRef(stage);
    paintedStageRef.current = stage;
    const { setStageWithTransition } = createStageChanger({
        getStage: () => paintedStageRef.current,
        navigationIntentEpochRef,
        operatorNavigationEpochRef,
        setStageDirection,
        setIsStageTransitioning,
        setPrevStage,
        setStage: (next) => {
            paintedStageRef.current = next;
            setStage(next);
        },
    });

    // TS-060: Global drag and drop extracted to useGlobalDashboardDrop below


    // Browser history navigation (back/forward buttons)
    const isPopstateNav = useRef(false);
    // Skips the mount run of the history-push effect so a deep link / page
    // refresh (e.g. /quotes) survives until the initial URL sync resolves the
    // page, instead of being rewritten to the default page's path.
    const navSeededRef = useRef(false);
    const canNavigateToQuoteStageRef = useRef(() => false);
    const handleQuoteStageNavigateRef = useRef(() => ({ ok: false, reason: 'Quote stage navigation is not ready.' }));

    // (The unified browser-history effects (push, seed, popstate registration) sit below the Project Summary state they read.)

    // Logout handler
    const handleLogout = () => {
        try {
            setSettingsOpen(false);
            editor.reset();
            // Route through closeConfirm (not a bare isOpen flip) so any
            // promise-wrapped confirm in flight settles via its onDismiss
            // instead of hanging across the logout, and none waiting opens after it.
            closeConfirm({ dropWaiting: true });

            // v5.11.28: clear BOTH legacy localStorage key AND the JWT tokens
            // in sessionStorage. Missing the JWT clear meant a page refresh
            // after logout silently auto-logged the user back in, defeating
            // the whole operation.
            localStorage.removeItem('rjb_current_user');
            // Auth-token clear is coordinated across same-user tabs and also
            // invalidates any refresh already in flight, so Sign out cannot be
            // undone by a late rotation response.
            setCurrentPage('dashboard');
            return teardownAuthenticatedLocalSession({ tokenReason: 'logout' });
        } catch (error) {
            console.error('Logout error:', error);
            window.location.reload();
            return Promise.resolve();
        }
    };

    // v5.46.x duplicate-fork guard: `onDismiss` fires when the dialog closes
    // WITHOUT either key being pressed (backdrop click / Escape — ConfirmModal
    // routes both straight to onClose, bypassing onConfirm/onCancel). Callers
    // that promise-wrap showConfirm need it or a dismissed dialog leaves the
    // promise pending forever (the pre-existing folder-dedup confirm at the
    // pre-process path has exactly that latent hang). Ref-based so the button
    // paths stay untouched: buttons resolve their callback first, then
    // onClose fires the (now harmless, already-settled) dismiss.
    // The one dialog slot (confirm-dialog-slot.ts): a replaced dialog is settled
    // through its onDismiss, and a caller passing `queue` waits until no dialog
    // is open anywhere on screen.
    const confirmSlotRef = useRef(null);
    if (!confirmSlotRef.current) {
        confirmSlotRef.current = createConfirmDialogSlot({
            render: ({ title, message, onConfirm, onCancel, confirmText = 'Confirm', cancelText = 'Cancel', confirmColor = 'red', icon = 'warning', zIndex, defaultFocus = 'cancel', hint }) => {
                setConfirmModal({ isOpen: true, title, message, onConfirm, onCancel, confirmText, cancelText, confirmColor, icon, zIndex, defaultFocus, hint });
            },
            hide: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
            otherDialogOpen: isAnyModalOpen,
        });
    }

    const showConfirm = useCallback((options) => {
        confirmSlotRef.current.show(options);
    }, []);

    const closeConfirm = useCallback((options = undefined) => {
        confirmSlotRef.current.close(options);
    }, []);

    const clearSettingsDirtyGuard = useCallback(() => {
        settingsDirtyRef.current = { count: 0, fieldNames: [] };
        setSettingsDirtyCount(0);
    }, []);

    // One host-owned decision covers the Settings Back key, every application
    // destination, browser Back/Forward and explicit sign-out. The routing seam
    // supplies continuations so no navigation side effect runs before approval.
    const requestSettingsPageExit = useCallback((request) => {
        const summary = settingsDirtyRef.current;
        if (request?.fromPage !== 'settings'
            || request?.toPage === 'settings'
            || summary.count <= 0) return false;

        let settled = false;
        const stay = () => {
            if (settled) return;
            settled = true;
            request.stay?.();
        };
        showConfirm({
            title: 'Leave with unsaved settings?',
            message: `${summary.count} field${summary.count === 1 ? '' : 's'} ${summary.count === 1 ? 'has' : 'have'} not been saved.${summary.fieldNames.length ? `\n${summary.fieldNames.join(', ')}` : ''}`,
            confirmText: 'Discard changes',
            cancelText: 'Keep editing',
            confirmColor: 'red',
            icon: 'warning',
            defaultFocus: 'cancel',
            onConfirm: () => {
                if (settled) return;
                settled = true;
                clearSettingsDirtyGuard();
                request.proceed?.();
            },
            onCancel: stay,
            onDismiss: stay,
        });
        return true;
    }, [clearSettingsDirtyGuard, showConfirm]);

    // Site-visit captures hold the failed File in component memory so Retry can
    // resend the exact bytes. Unlike editable Settings, there is no safe
    // "discard and leave" choice: block every app route and browser-history
    // exit until the current mutation settles or the failed upload succeeds.
    const requestAppPageExit = useCallback((request) => {
        const capture = siteVisitsExitGuardRef.current;
        if (shouldBlockSiteVisitExit(request, capture)) {
            request.stay?.();
            setToast({
                type: 'warning',
                message: capture.failedCount > 0
                    ? 'Retry the failed site-visit upload before leaving. Its original file is still held safely on this screen.'
                    : 'Site-visit capture is still saving. Wait for the upload, note or on-site request to finish before leaving.',
            });
            return true;
        }
        return requestSettingsPageExit(request);
    }, [requestSettingsPageExit]);

    const handleSettingsLogout = () => {
        const deferred = requestAppPageExit({
            fromPage: currentPageRef.current || currentPage,
            toPage: 'logout',
            source: 'navigation',
            proceed: handleLogout,
            stay: () => {},
        });
        if (!deferred) handleLogout();
    };

    // Stage 1 data - MUST be before conditional return
    const [formData, setFormData] = useState({
        assessor: '',
        visitDate: new Date().toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }),
        address: '',
        strataplan: '',
        orderNumber: '',
        notes: '',
        clientName: '',
        clientCompany: '',
        clientEmail: '',
        clientPhone: '',
        // Quote requester's job title / role (e.g. "Strata Manager"). Flows to
        // crm_contacts.title on save; AI pre-fills from the RFQ when present.
        clientTitle: '',
        siteContactName: '',
        siteContactPhone: '',
        unitNumber: '',
        jobDescription: '',
        // Phase 3C Gap #8 Layer A (2026-05-04): "Attention: X" addressee for
        // the printed quote header. Auto-filled from primaryContact at
        // postprocessing complete; user can override manually and the
        // override is never clobbered.
        attentionTo: '',
    });
    // Long-running Stage 1 summary work must merge against the latest operator
    // edits, not the render that dispatched the AI call. The summary seam also
    // updates this ref synchronously when it commits its verified focus marker.
    const formDataRef = useRef(formData);
    formDataRef.current = formData;

    // Generate quote title from address
    // One shared generator keeps attended imports, queued imports, persisted
    // project titles and the final quote header on the same naming contract.
    const generateQuoteTitle = (sourceFormData) => generateRbbQuoteTitle({
        formData: sourceFormData || formData,
    });

    const [quoteTitle, setQuoteTitle] = useState(generateQuoteTitle());
    const quoteTitleRef = useRef(quoteTitle);
    quoteTitleRef.current = quoteTitle;
    // Track whether the user manually typed a title - prevents auto-regeneration
    // from overwriting their edit when the address changes.
    const [titleEditedByUser, setTitleEditedByUser] = useState(false);
    const titleEditedByUserRef = useRef(titleEditedByUser);
    titleEditedByUserRef.current = titleEditedByUser;
    // titleSourceKey snapshot of the formData fields quoteTitle was derived
    // from. The effect below regenerates ONLY when this key changes — i.e.
    // the user edited a title input in-session. Without the comparison, a
    // project LOAD (which swaps formData wholesale) also fired the effect,
    // and because the regenerated code stamps the CURRENT month, merely
    // opening + saving an old project rewrote its stored "(0526)" title
    // suffix to the present month (title drift, observed live 2026-07-03 on
    // project 88c2f633 — projects.title moved to "(0726)" while the quote
    // header kept the true month). completeProjectLoad syncs this ref so a
    // load never counts as an edit.
    const titleSourceRef = useRef(null);

    // Update quote title when address changes - only if user hasn't manually edited
    useEffect(() => {
        const source = titleSourceKey(formData);
        if (titleSourceRef.current === source) return; // no real input change (e.g. load-synced) — keep the current title
        titleSourceRef.current = source;
        if (!titleEditedByUser) {
            // Follows the address, but never turns a real descriptive title (adopted
            // from the saved row, or loaded) back into the "XXX" a blank address makes.
            setQuoteTitle(previousTitle => nextAutoQuoteTitle({
                previousTitle,
                generatedTitle: generateQuoteTitle(),
                formData,
            }));
        }
    }, [formData.address, formData.unitNumber, formData.jobDescription]);

    const [photos, setPhotos] = useState([]);
    const photosRef = useRef(photos);
    useEffect(() => { photosRef.current = photos; }, [photos]);
    // Captions on upload (photo-caption-queue.ts): a caption the server wrote
    // for a photo of the open project goes on that photo unless it has one.
    // setPhotos marks the project dirty, so the autosave stores it.
    useEffect(() => {
        const onPhotoCaption = (e: Event) => {
            const detail = (e as CustomEvent<PhotoCaptionDetail>)?.detail;
            if (!detail?.photoId || detail.projectId !== String(currentProjectIdRef.current ?? '')) return;
            setPhotos((prev) => withPhotoCaption(prev, detail));
        };
        window.addEventListener(PHOTO_CAPTION_EVENT, onPhotoCaption);
        return () => window.removeEventListener(PHOTO_CAPTION_EVENT, onPhotoCaption);
    }, []);
    // Markup-anywhere (Peter 2026-06-30): persist a marked-up reference photo
    // back onto the project photos, matched by identity (id/name). setPhotos
    // marks the project dirty (the unsaved-changes effect lists `photos`), so
    // the debounced autosave stores it — the same persistence path scope-photo
    // markup already uses. Preserves the original id/name so the photo keeps its
    // slot and any scope assignment that references it stays linked. Threaded to
    // every stage's cockpit (ReferenceFilesCockpitCard onMarkupPhoto).
    const handleMarkupReferencePhoto = useCallback((orig, updated) => {
        if (!orig || !updated) return;
        const key = orig.id || orig.name;
        if (!key) return;
        setPhotos(prev => Array.isArray(prev)
            ? prev.map(p => ((p.id || p.name) === key) ? { ...updated, id: p.id, name: p.name } : p)
            : prev);
    }, []);
    // Top success/info ribbon (Peter 2026-06-30): a transient confirmation in
    // the global top-of-viewport banner stack (alongside the offline / save
    // banners). Used to confirm a project duplicate. Auto-dismisses after a few
    // seconds; also manually dismissable. Shape: { kind:'success'|'info', message }.
    const [topNotice, setTopNotice] = useState(null);
    useEffect(() => {
        if (!topNotice) return undefined;
        const t = setTimeout(() => setTopNotice(null), 6000);
        return () => clearTimeout(t);
    }, [topNotice]);
    const [documents, setDocuments] = useState([]);
    const [videos, setVideos] = useState([]);
    const videosRef = useRef(videos);
    useEffect(() => { videosRef.current = videos; }, [videos]);
    const [videoTranscripts, setVideoTranscripts] = useState([]);
    const [selectedHeroImage, setSelectedHeroImage] = useState(null);
    const selectedHeroImageRef = useRef(selectedHeroImage);
    selectedHeroImageRef.current = selectedHeroImage;

    // Phase 16: project-level admin Costing Sheet - sibling of the WMS.
    // Keep this state/ref ahead of the attended Stage 4 watcher: that watcher's
    // dependency array reads projectCostingSheet during render, and a later
    // declaration creates a production-only temporal-dead-zone crash after
    // minification even though callback bodies do not run until later.
    const [projectCostingSheet, setProjectCostingSheet] = useState(null);
    const projectCostingSheetRef = useRef(projectCostingSheet);
    useEffect(() => { projectCostingSheetRef.current = projectCostingSheet; }, [projectCostingSheet]);
    const costingGenerationOwnerRef = useRef(0);
    const [costingRegenerating, setCostingRegenerating] = useState(false);

    // Increment C (2026-06-17): add / remove reference files from the cockpit on
    // ANY stage. ADD reads dropped File[] (image -> photos, pdf -> documents)
    // and appends to state; REMOVE filters the item out of state and clears the
    // hero if it was the removed photo. Both surface a toast. Defined as plain
    // consts (NOT useCallback) so they never eagerly read later-declared state
    // in a dep array (see gotcha_useCallback_dep_array_tdz). Passed to Stages
    // 1/2/3 and the Stage 5 quote-editor modal. handleAddReferenceFiles mirrors
    // the inline ADD the Stage 5 modal still passes at its own mount below; kept
    // in sync intentionally until a later consolidation pass.
    // Returns a promise that settles once the files are queued into state
    // (React has not committed them yet), with the names of the files it did
    // not add. `origin` (optional) is spread into
    // each new document and photo: a New revision passes
    // { addedFrom: 'revision', quoteRev } so the Reference Files panel can say
    // "Added with Rev N" (JOBS-UX-SPEC 3.3 step 6, 3.5 G3). Existing callers
    // ignore both and behave exactly as before.
    const handleAddReferenceFiles = (files, origin: Record<string, unknown> | null = null): Promise<{ rejected: string[] }> => {
        if (!Array.isArray(files) || files.length === 0) return Promise.resolve({ rejected: [] });
        const originFields = origin && typeof origin === 'object' ? origin : {};
        const nameOfReferenceFile = (file: { name?: string } | null | undefined): string => String(file?.name || '').trim() || 'a file';
        const nothingAdded = () => ({ rejected: files.map(nameOfReferenceFile) });
        const selectedFiles = dedupeSelectedFilesByIdentity(files);
        selectedFiles.forEach(file => ensureDocumentSourceFileId(file));
        const contradictoryFiles = selectedFiles.filter(hasContradictoryPdfSignals);
        const routedFiles = contradictoryFiles.length > 0
            ? selectedFiles.filter(file => !contradictoryFiles.includes(file))
            : selectedFiles;
        if (contradictoryFiles.length > 0) {
            setToast({
                type: 'warning',
                message: `Skipped ${contradictoryFiles.length} file${contradictoryFiles.length === 1 ? '' : 's'} whose PDF filename and file type disagree. Rename or export the source correctly, then retry.`,
            });
        }
        if (routedFiles.length === 0) return Promise.resolve({ rejected: contradictoryFiles.map(nameOfReferenceFile) });
        const importRunContext = captureProjectRunContext({
            projectId: currentProjectIdRef.current,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectPersistenceAbortRef.current?.signal || null,
        });
        const importRunIsCurrent = () => isProjectRunContextCurrent(importRunContext, {
            projectId: currentProjectIdRef.current,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectPersistenceAbortRef.current?.signal || null,
        });
        const newPhotos = [];
        const newDocs = [];
        const rejectedFiles = [];
        const liveDocumentOwner = () => importRunContext.projectId || currentProjectIdRef.current || null;
        const previewTransaction = createOwnedDocumentPreviewTransaction({
            isCurrent: importRunIsCurrent,
            initialOwner: liveDocumentOwner(),
            getFinalOwner: liveDocumentOwner,
            register: registerProjectDocumentUrl,
            release: releaseProjectDocumentUrl,
        });
        const stopIfProjectChanged = () => !previewTransaction.guard();
        const readAsDataURL = (file) => new Promise(resolve => {
            const reader = new FileReader();
            reader.onload = (ev) => resolve(ev.target?.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
        });
        return (async (): Promise<{ rejected: string[] }> => {
            for (const file of routedFiles) {
                if (stopIfProjectChanged()) return nothingAdded();
                const mime = String(file.type || '').toLowerCase();
                const isPdf = isPdfReferenceFile(file);
                const isImage = !isPdf && (mime.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|tiff?)$/i.test(file.name || ''));
                if (!isImage && !isPdf) {
                    rejectedFiles.push(file.name || 'unsupported file');
                    continue;
                }
                let dataUrl = null;
                if (isPdf) {
                    try {
                        const previewOwner = liveDocumentOwner();
                        dataUrl = registerProjectDocumentUrl(
                            previewOwner,
                            URL.createObjectURL(file),
                        );
                        previewTransaction.track(dataUrl, previewOwner);
                    } catch {
                        // The durable File remains attached and will upload on
                        // save even if this browser cannot mint a preview URL.
                    }
                } else {
                    dataUrl = await readAsDataURL(file);
                }
                if (stopIfProjectChanged()) return nothingAdded();
                if (!dataUrl && !isPdf) continue;
                if (isImage) {
                    // Compress, exactly as the Stage 1 import loop does. This
                    // path used to push the raw camera data URL straight into
                    // photos[], so a drag-and-drop photo was 3.6-7.9 MB where an
                    // imported one was ~200 KB — same array, same downstream
                    // vision call, twenty times the bytes. See
                    // compressIngestedImage for why that mattered on 2026-08-18.
                    const shrunk = await compressIngestedImage(
                        dataUrl, file.name || 'dropped-photo', newPhotos.length + 1,
                    );
                    if (stopIfProjectChanged()) return nothingAdded();
                    newPhotos.push({
                        id: 'photo_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9),
                        name: file.name || 'dropped-photo',
                        data: shrunk.data,
                        dataUrl: shrunk.data,
                        cloudMedia: shrunk.cloudMedia,
                        file,
                        ...(shrunk.compressed
                            ? { sizeKB: shrunk.sizeKB, width: shrunk.width, height: shrunk.height, fileName: shrunk.fileName }
                            : {}),
                        addedFrom: 'cockpit-drop',
                        ...originFields,
                        addedAt: new Date().toISOString(),
                    });
                } else if (isPdf) {
                    let pdfText = '';
                    let pdfPageCount = null;
                    let contentComplete = true;
                    let extractionFailed = false;
                    try {
                        const { extractPdfText } = await import('./utils/helpers');
                        const result = await extractPdfText(file);
                        if (result && typeof result.text === 'string') {
                            pdfText = result.text;
                            pdfPageCount = result.pageCount || result.pages || null;
                            contentComplete = result.contentComplete !== false;
                        }
                    } catch (err) {
                        console.warn('[QuoteMaker] Cockpit PDF text extraction non-fatal error for', file.name, err);
                        extractionFailed = true;
                    }
                    if (stopIfProjectChanged()) return nothingAdded();
                    newDocs.push({
                        id: 'doc_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9),
                        name: file.name || 'dropped-document.pdf',
                        originalName: file.name || 'dropped-document.pdf',
                        ext: 'pdf',
                        contentType: 'application/pdf',
                        pdfDataUrl: dataUrl,
                        pdfBlob: file,
                        content: pdfText,
                        text: pdfText,
                        pageCount: pdfPageCount,
                        contentComplete,
                        extractionFailed,
                        size: file.size || 0,
                        sizeKB: Math.round((file.size || 0) / 1024),
                        addedFrom: 'cockpit-drop',
                        ...originFields,
                        addedAt: new Date().toISOString(),
                        ...buildDocumentSourceMetadata(file),
                    });
                }
            }
            if (stopIfProjectChanged()) return nothingAdded();
            if (!previewTransaction.commit(newDocs.map(document => document?.pdfDataUrl))) return nothingAdded();
            if (newPhotos.length > 0) {
                setPhotos(prev => [...(Array.isArray(prev) ? prev : []), ...newPhotos]);
            }
            if (newDocs.length > 0) {
                setDocuments(prev => mergeDocsByIdentity(prev, newDocs));
            }
            if (rejectedFiles.length > 0) {
                setToast({
                    type: 'warning',
                    duration: 6000,
                    message: `Only photos and PDFs can be added via the cockpit drawer (${rejectedFiles.slice(0, 2).join(', ')}${rejectedFiles.length > 2 ? ` and ${rejectedFiles.length - 2} more` : ''}). To import Word documents, emails, or text files, please use Stage 1 Data Entry.`,
                });
            }
            // No "Added N photos + M PDFs" confirmation. The dropped files appear
            // in the reference list immediately, and completion toasts were retired
            // by the notification policy (Peter 2026-06-27).
            const added = new Set<unknown>([...newPhotos.map((photo) => photo.file), ...newDocs.map((doc) => doc.pdfBlob)]);
            return {
                rejected: [
                    ...contradictoryFiles.map(nameOfReferenceFile),
                    ...routedFiles.filter((file) => !added.has(file)).map(nameOfReferenceFile),
                ],
            };
        })().catch((error) => {
            previewTransaction.rollback();
            console.error('[QuoteMaker] Reference-file import failed:', error);
            setToast({ type: 'error', message: error?.message || 'Reference-file import failed.' });
            return nothingAdded();
        });
    };

    const handleSupersedeReferenceFile = (kind, item) => {
        if (!item || !['photo', 'document', 'video'].includes(kind)) return;
        queueProjectMediaRetirementReceipt(
            projectMediaRetirementQueueRef.current,
            currentProjectId || currentProjectIdRef.current,
            item,
        );
    };

    const handleRemoveReferenceFile = (kind, item) => {
        if (!item) return;
        const isSame = (a) => a === item || (item.id != null && a?.id === item.id);
        if (kind === 'photo') {
            handleSupersedeReferenceFile(kind, item);
            setPhotos(prev => clearRetiredProjectPhotoAliases(prev, item));
            setFormData(prev => clearRetiredProjectFormDataPhotoAliases(prev, item));
            setScope(prev => clearRetiredProjectScopePhotoAliases(prev, item));
            setSelectedHeroImage(prev => clearRetiredProjectSelectedHero(prev, item));
            setQuoteData(prev => clearRetiredProjectQuotePhotoAliases(prev, item));
        } else if (kind === 'document') {
            handleSupersedeReferenceFile(kind, item);
            const previewUrl = item.pdfDataUrl;
            const previewIsShared = Array.isArray(documents) && documents.some(document => (
                !isSame(document) && document?.pdfDataUrl === previewUrl
            ));
            if (!previewIsShared) {
                releaseProjectDocumentUrl(currentProjectIdRef.current, previewUrl);
            }
            setDocuments(prev => (Array.isArray(prev) ? prev.filter(d => !isSame(d)) : prev));
        } else if (kind === 'video') {
            handleSupersedeReferenceFile(kind, item);
            setVideos(prev => (Array.isArray(prev) ? prev.filter(v => !isSame(v)) : prev));
            // The frames taken from the video go with it (Peter 2026-10-28), each removed like any photo so scope
            // items, the hero and the quote let go of them too.
            framesOfVideo(photosRef.current, item).forEach(frame => handleRemoveReferenceFile('photo', frame));
        }
        // No "Removed X" confirmation: the file disappears from the reference list
        // on its own. The old dead branch (`toast` is state, not a function) was an
        // info/completion toast the notification policy now suppresses anyway.
    };

    // File System Access API state - Auto-subfolder architecture
    const [parentFolder, setParentFolder] = useState(null);
    // TS-060: Global file explorer state & actions (DEC-COUNCIL-015)
    const {
        globalFileExplorer,
        setGlobalFileExplorer,
        globalFileExplorerRef,
        closeGlobalFileExplorer,
        scanIntoGlobalFileExplorer,
        openGlobalFileExplorer,
        fileExplorerSelectProject,
        fileExplorerChangeFolder,
        fileExplorerShowToast,
        fileExplorerOpenSyncSettings,
        rescanGlobalFileExplorer,
    } = useGlobalFileExplorer({ setToast, setParentFolder, openSettings });
    const [quoteSubfolder, setQuoteSubfolder] = useState(null);
    const [folderFiles, setFolderFiles] = useState({ photos: [], documents: [], outputs: [] });
    const [saveStatus, setSaveStatus] = useState('idle');
    // v5.40.0 (UX audit Wave-1 P0 #2 + #4): persistent save-error state.
    //   shape: { kind, message, detail, projectId, at } | null
    //   kind: 'quota' | 'offline' | 'auth' | 'backend' | 'unknown'
    // The banner stays visible until either (a) a subsequent save succeeds
    // (clears the state) or (b) the user explicitly dismisses it. Pre-fix the
    // 3-5s toast disappeared and the user thought their work had saved.
    const [saveError, setSaveError] = useState(null);
    // Deterministic cloud-validation failures must not resend the same large
    // project envelope forever. Kept separate from saveError so dismissing the
    // visible banner cannot silently re-arm automatic saves.
    const [manualSaveRetryLatch, setManualSaveRetryLatch] = useState(null);
    // Mirror of saveError into a ref so the save path (a plain async fn that
    // closes over a possibly-stale render scope) can tell whether a SUCCESS is
    // recovering from a prior failure. Drives the reassuring "your changes have
    // been saved" confirmation on reconnect (Peter 2026-06-30): the autosave
    // silently retries + succeeds when the connection returns, so without an
    // explicit cue the red banner just vanishes and the user can't tell whether
    // it saved or was lost.
    const saveErrorRef = useRef(null);
    useEffect(() => { saveErrorRef.current = saveError; }, [saveError]);
    // v5.40.0: classifier returns { kind, message }. The message is the
    // operator-facing copy rendered in the banner; the kind drives the
    // suggested-action button (Retry / Open Project History for export /
    // sign-in-again).
    const classifySaveError = useCallback((err) => {
        if (!err) return { kind: 'unknown', message: 'Save failed. Please try again.' };
        const name = err.name || '';
        const msg = String(err.message || '');
        // v5.40.15 (Wave 15 fix F7): apiFetch attaches `err.status` (Fetch idiom),
        // NOT `err.statusCode`. Pre-fix the 401/403/5xx branches never fired
        // for real HTTP errors — they fell through to the broad regex which
        // false-positives ("address token #3 invalid" → tagged auth-expired).
        // Now reads both keys for safety. AbortError is suppressed entirely
        // (user-initiated cancel shouldn't surface as a red banner).
        if (name === 'AbortError' || /aborted a request|user aborted/i.test(msg)) {
            return {
                kind: 'aborted',
                message: 'Save was cancelled.',
            };
        }
        const status = err.status ?? err.statusCode;
        // Checked BEFORE the offline branch on purpose. A 409 is proof we
        // reached the backend and it answered, whereas the offline branch
        // leads with `!navigator.onLine` — browser folklore that reports
        // offline on healthy embedded/desktop stacks (the standing
        // "connectivity warnings need service evidence" rule). Letting a
        // stale-write refusal be relabelled "the connection dropped" would
        // send the operator to reconnect instead of to the real choice
        // between reloading and overwriting.
        if (status === 409 && (err.code === 'PROJECT_STALE_WRITE' || err.body?.code === 'PROJECT_STALE_WRITE')) {
            return {
                kind: 'conflict',
                message: 'This project changed somewhere else after you opened it. Nothing was overwritten — reload to pull the newer version, or choose to overwrite it.',
            };
        }
        if (name === 'QuotaExceededError' || /quota|exceeded|disk full/i.test(msg)) {
            return {
                kind: 'quota',
                message: 'Browser storage is full. Open Project History and export or delete an old project to make room, then try saving again.',
            };
        }
        if (!navigator.onLine || /failed to fetch|network|offline/i.test(msg)) {
            return {
                kind: 'offline',
                message: 'Save failed because the connection dropped. Your work is still in this tab. Reconnect and click Retry.',
            };
        }
        if (status === 401 || status === 403 || /unauthor|forbidden|session expired/i.test(msg)) {
            return {
                kind: 'auth',
                message: 'Save failed: your session expired. Sign in again to keep saving.',
            };
        }
        if ((typeof status === 'number' && status >= 500) || /5\d\d|server error|internal server/i.test(msg)) {
            return {
                kind: 'backend',
                message: `Save failed: the QuoteMaker backend returned an error. Try again in a moment.`,
            };
        }
        return { kind: 'unknown', message: `Save failed: ${msg.slice(0, 160) || 'unknown error'}.` };
    }, []);
    const [reconnectModalOpen, setReconnectModalOpen] = useState(false);
    const [pendingProjectLoad, setPendingProjectLoad] = useState(null);
    const [missingFiles, setMissingFiles] = useState({ photos: [], documents: [], subfolder: null });
    // Customer-Zero issue D (2026-05-15): set when a project loads with
    // missing reference files / photos / videos AND no sync-folder handle is
    // configured. Drives the in-flow "Reconnect folder" banner so the user
    // doesn't have to bounce to Settings → File sync.
    const [mediaRecoveryNeeded, setMediaRecoveryNeeded] = useState(null);

    // Set inspector name from last used or logged-in user when entering QuoteMaker (only once)
    const [assessorNameInitialized, setAssessorNameInitialized] = useState(false);
    useEffect(() => {
        if (currentPage === 'quotemaker' && !assessorNameInitialized) {
            // A default fills a BLANK assessor only (4B / O-F7). Entering QuoteMaker from another page used to overwrite the
            // assessor a saved project had just restored with the last-used or logged-in name, and the next save kept the
            // overwrite. Blank new projects still get the default from here or from the new-project reset.
            const lastUsed = AssessorNamesService.getLastUsed() || user?.name || '';
            if (lastUsed) {
                setFormData(prev => (String(prev?.assessor ?? '').trim() ? prev : { ...prev, assessor: lastUsed }));
            }
            setAssessorNameInitialized(true);
        } else if (currentPage !== 'quotemaker') {
            setAssessorNameInitialized(false);
        }
    }, [currentPage, user, assessorNameInitialized]);

    // Stage 2 data - MUST be before conditional return. The Risk report went with
    // the old pipeline (docs/old-pipeline-removal/PLAN.md); an old job's saved
    // report, photo analysis and diagnosis are still loaded and saved back as
    // they are, and the Work Method, scope and quote stages still read them.
    const [analysis, setAnalysis] = useState(null);
    const [photoAnalysis, setPhotoAnalysis] = useState(null);
    const [diagnosticResult, setDiagnosticResult] = useState(null);
    // H1 (2026-10-23): the saved Stage 2 Risk Report (aiContext.riskReport).
    const [riskReport, setRiskReport] = useState(null);
    // Nothing sets this any more (the Risk run is gone); the shared busy readers still take it.
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [isStage1Processing, setIsStage1Processing] = useState(false);
    // Pre-processed files - deterministic extraction results from preprocessing-pipeline.js
    // Each entry is a normalized PreprocessedFile { id, category, text, metadata, thumbnail, ... }
    const [preprocessedFiles, setPreprocessedFiles] = useState([]);
    // Post-processing context - populated after import completes
    // Holds cross-references to CRM, gold standards, learnings, plus confidence score
    const [postProcessContext, setPostProcessContext] = useState(null);
    const [isPostProcessing, setIsPostProcessing] = useState(false);
    const importAbortRef = useRef(false); // Set true by resetForm to cancel in-progress imports

    // ─── Lifted Stage 1 state (for nav-resume during AI processing) ───────
    // These were previously local to Stage1DataEntry, but they need to survive
    // when the user navigates AWAY from QuoteMaker mid-import and back. The
    // Stage1DataEntry component unmounts on nav-away, which would lose all
    // local state. Lifting them to App.jsx (which never unmounts) means:
    //   - The animated processing view continues from where it left off
    //   - The Import Complete view shows when files have landed
    //   - Background async operations (procDispatch) keep working because
    //     useReducer's dispatch function is stable across renders
    //
    // importPhase: 'import' (drop zone) | 'processing' (animated view) | 'form' (Import Complete)
    const [importPhase, setImportPhase] = useState('import');
    const [procState, procDispatch] = useReducer(processingReducer, processingInitialState);
    const [extractedFacts, setExtractedFacts] = useState([]);

    // ─── Project Summary (best-guess pre-analysis pass) ───
    // SPEC 2026-10-21 §14: the summary is no longer a page between Data Entry
    // and Risk. Stage 1 is the one screen on stage 1 and shows the summary in
    // its Job cockpit's Summary tab. What the old page's view flag meant is now
    // said by this one-shot request for a cockpit tab, which Stage 1 consumes
    // once.
    const [cockpitTabRequest, setCockpitTabRequest] = useState(null);
    const cockpitTabRequestSeqRef = useRef(0);
    const requestCockpitTab = useCallback((tab) => {
        cockpitTabRequestSeqRef.current += 1;
        setCockpitTabRequest({ tab, seq: cockpitTabRequestSeqRef.current });
    }, []);
    const consumeCockpitTabRequest = useCallback((seq) => {
        setCockpitTabRequest(prev => (prev && prev.seq === seq ? null : prev));
    }, []);
    // Page changes are navigation too (a menu trip changes neither `stage` nor
    // its epoch otherwise). Same epoch as stage changes: see navigationIntentEpochRef.
    useEffect(() => { navigationIntentEpochRef.current += 1; }, [currentPage]);
    // Pricing Worktable interstitial (Stage 4 → 5) sub-view flag: a named
    // sub-view, no new stage number.
    // Opened from the Stage 4 NEXT button when the feature is enabled; cleared
    // whenever the user is not on Stage 4 so it never lingers across a jump.
    const [pricingWorktableActive, setPricingWorktableActive] = useState(false);
    useEffect(() => {
        if (stage !== 4 && pricingWorktableActive) setPricingWorktableActive(false);
    }, [stage, pricingWorktableActive]);
    const [projectSummary, setProjectSummary] = useState(null);
    const projectSummaryRef = useRef(projectSummary);
    projectSummaryRef.current = projectSummary;
    const [isSummarizing, setIsSummarizing] = useState(false);
    const [projectWorkflowMode, setProjectWorkflowMode] = useState(PROJECT_WORKFLOW_MODES.FULL_AI);
    const isFastTrackManualScope = projectWorkflowMode === PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE;
    // v5.38.0: re-quote shortcut (the summary → Stage 4). Spinner state for the
    // "Skip to Scope Builder" button in the Job cockpit's Summary tab.
    const [isRequoteShortcutRunning, setIsRequoteShortcutRunning] = useState(false);
    // Token of the re-quote run that owns the busy flag above, so a stale run's
    // cleanup can never release a newer run's lock (requote-shortcut-runner).
    const requoteRunOwnerRef = useRef<unknown>(null);

    // ── Unified in-app browser history (v5.41.172) ──────────────────────────
    // The ONE history layer. Every entry carries { page, stage, summary } AND keeps the
    // address bar in sync (pageToPath), so browser/mouse Back & Forward step
    // through the app — across pages AND Stage 1→5 — and never out of it. `summary`
    // is always written false now (Stage 1 has one view); an older entry's true
    // still restores Stage 1 with the Summary tab (use-app-routing). This
    // replaces the old { qmPage } URL layer (removed above) that double-pushed on
    // page nav (a dead Back-press at the page boundary), let the URL drift out of
    // sync, and blanked the screen when Back popped a { qmPage } entry.
    useEffect(() => {
        if (isPopstateNav.current) {
            isPopstateNav.current = false;
            // Skip the entry Back/Forward already moved to, but only when the
            // address names the page on screen. A Back press that changed
            // nothing (same page, or a stage the gate refused) leaves the flag
            // set, and the next real page change used to be swallowed here,
            // leaving the old address for the next reload to land on
            // (2026-09-29, "resets from wherever back to quotemaker").
            if (addressNamesPage(window.location.pathname, currentPage)) return;
        }
        // Skip the mount run: the seed below owns the initial entry, and a deep
        // link / refresh (e.g. /quotes) must survive until the initial URL sync
        // resolves the page. On mount currentPage is still the default 'dashboard'
        // (the sync's handleNavigate is async), so rewriting the URL here would
        // clobber the deep link back to '/'. (Regression fix, v5.41.175.)
        if (!navSeededRef.current) {
            navSeededRef.current = true;
            return;
        }
        // Intake owns its selected RFQ entry. In particular, initial URL sync
        // after a reload must not add a generic Intake entry over the restored
        // detail, which would discard its selection and add a dead Back step.
        if (currentPage === 'intake' && pathToPage(window.location.pathname) === 'intake'
            && intakeJobFromHistory(window.history.state)) return;
        const state = historyViewState({ page: currentPage, stage });
        // A change that leaves the recorded view as it was adds nothing.
        if (historyEntryRecordsView(window.history.state, state)) return;
        const path = pageToPath(currentPage) + (window.location.search || '') + (window.location.hash || '');
        try { window.history.pushState(state, '', path); } catch { /* sandboxed contexts */ }
    }, [currentPage, stage]);

    // Seed the first entry on mount WITHOUT touching the URL ('' keeps the current
    // address) so a deep link / refresh survives. The initial URL sync above
    // resolves the page from the URL; the first real nav then syncs via the push.
    useEffect(() => {
        // App initially renders the role's landing page while URL sync is
        // pending. Keep a valid RFQ entry until Intake mounts and restores it.
        if (pathToPage(window.location.pathname) === 'intake'
            && intakeJobFromHistory(window.history.state)) return;
        try { window.history.replaceState(historyViewState({ page: currentPage, stage }), '', ''); } catch { /* sandboxed contexts */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Listen for popstate (browser back/forward)
    // The handler BODY (handlePopState) moved VERBATIM into
    // src/hooks/use-app-routing.ts (TS-060 T4 slice 6, DEC-COUNCIL-015); it
    // is destructured from useAppRouting below the useQuoteEditor alias
    // block (this effect body runs strictly after render, so the const is
    // assigned by call time). The effect KEEPS its registration/cleanup
    // lines + [currentPage, stage] deps at this exact position so React's
    // effect order is unchanged. Listener-identity semantics are preserved:
    // the original re-created the handler only when these deps changed;
    // this form captures the hook's per-render handlePopState binding from
    // the render the effect last ran — the same staleness window (the body
    // reads no App state beyond currentPage/stage;
    // everything else is refs, stable setters, module imports, or the
    // lazily-wrapped openStage5FromCurrentScope and canShowSummaryView).
    //
    // 2026-09-29: localTenantSessionVerified joined the deps. Back into
    // QuoteMaker with nothing open now reopens the tab's saved project, which
    // reads QM_STATE_KEY. A reload on the Run Sheet never changes currentPage
    // or stage, so without it the listener kept the first render's closure,
    // where that key is still null, for the whole session.
    useEffect(() => {
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [currentPage, stage, localTenantSessionVerified]);

    // (Risk report freshness is tracked below, beside the Summary's: it needs currentProjectId, declared later.)

    // Ref-based locks preventing concurrent runProjectSummary / runAnalysis
    // calls. React state flags are not enough because two parallel auto-
    // trigger effects can both read the flag as false before the first one
    // has committed its set-true. Refs flip synchronously. Fix for the
    // duplicate project-summary pipeline fires (49s + 24.6s) reported
    // 2026-04-08.
    const isSummarizingRef = useRef(false);
    const projectSummaryGenerationOwnerRef = useRef(0);
    // Terminal automatic failures are retained with structured metadata and
    // latched per input/config fingerprint. This prevents an ordinary React
    // re-render from silently firing the same doomed AI request forever.
    const projectSummaryFailureRef = useRef(null);
    // The successful twin of the latch above: the Summary this project last
    // published and the evidence its run started from (use-project-summary.ts).
    const projectSummarySuccessRef = useRef(null);
    const [projectSummaryRecoveryRevision, setProjectSummaryRecoveryRevision] = useState(0);
    const projectSummaryConfigurationRevisionRef = useRef(0);
    const projectSummaryFocusRecoveryReceiptsRef = useRef(new Set());
    const lastProjectSummaryFocusSignalAtRef = useRef(0);
    useEffect(() => {
        const signalExplicitProviderConfigurationChanged = () => {
            // This event is emitted only by an explicit model-lane/config edit.
            // It is a material configuration change, so it starts a new bounded
            // recovery family even when browser-visible model state is stable.
            projectSummaryConfigurationRevisionRef.current += 1;
            if (projectSummaryFailureRef.current) {
                setProjectSummaryRecoveryRevision(revision => revision + 1);
            }
        };
        const recoverOnceAfterBrowserFocus = () => {
            const failure = projectSummaryFailureRef.current;
            if (!failure || isLoadingProjectRef.current) return;
            if (pipelineOwnerRef.current === 'batch-runner') return;
            if (aiPipelineHaltedRef.current || openedForReviewRef.current) return;
            if (currentPageRef.current !== 'quotemaker' || Number(stageRef.current) !== 1) return;
            const now = Date.now();
            if (now - lastProjectSummaryFocusSignalAtRef.current < 5000) return;
            const focusRecoveryClaimed = claimProjectSummaryFocusRecovery(
                projectSummaryFocusRecoveryReceiptsRef.current,
                {
                    projectId: currentProjectIdRef.current,
                    loadGeneration: projectLoadGenerationRef.current,
                    evidenceFingerprint: currentProjectSummaryEvidenceFingerprintRef.current,
                    apiKey,
                    model,
                    configurationRevision: projectSummaryConfigurationRevisionRef.current,
                    failure,
                },
            );
            if (!focusRecoveryClaimed) return;
            // Claim before state changes. Repeated focus events and a failed
            // recovery therefore remain bounded even if React paints slowly.
            lastProjectSummaryFocusSignalAtRef.current = now;
            setProjectSummaryRecoveryRevision(revision => revision + 1);
        };
        window.addEventListener('qm-task-model-overrides-changed', signalExplicitProviderConfigurationChanged);
        window.addEventListener('focus', recoverOnceAfterBrowserFocus);
        return () => {
            window.removeEventListener('qm-task-model-overrides-changed', signalExplicitProviderConfigurationChanged);
            window.removeEventListener('focus', recoverOnceAfterBrowserFocus);
        };
    }, [apiKey, model]);
    const isAnalyzingRef = useRef(false);
    // Guards auto-analysis from firing during project load hydration.
    // Set true at the start of completeProjectLoad, cleared after a rAF tick
    // so all setState calls have committed before auto-analysis evaluates.
    const isLoadingProjectRef = useRef(false);
    // The open whose project has not painted yet: an "Open in QuoteMaker"
    // (Clients, Client history, Intake, the Assistant) or a plain open (a Run
    // Sheet row, Analysis, All Quotes, the workspace tabs). useAppRouting's
    // intake bridge and the persistence hook's loadProjectUnchecked set and
    // clear it; while it is set, Menu > QuoteMaker and Back wait for it
    // instead of painting an empty QuoteMaker (2026-09-29, 2026-09-30).
    const pendingProjectOpenRef = useRef(null);
    // Pre-emptive photo auto-assign - fired right after Risk Analysis
    // populates the scope, so by the time the user navigates to the Scope
    // Builder the photos are already attached. The abort ref lets us cancel an in-flight
    // run when the user re-runs analysis, which is critical because a stale
    // result would apply its assignments to the NEW scope using OLD indices.
    const autoAssignAbortRef = useRef(null);
    // v5.19.18 (Peter 2026-05-05): App-level boolean state for "any photo
    // auto-assign is currently running, regardless of which trigger started
    // it". Both the pre-emptive path (App.jsx, fires inside runAnalysis) and
    // the Stage 4 fallback (Stage4ScopeBuilder, fires when component mounts
    // with unassigned photos) flip this to true on start and false on end.
    // The ref is the source of truth for control flow; the state is the
    // re-render-triggering mirror that Stage 4 reads via prop. Pre-fix,
    // Stage 4 had its own per-instance abortControllerRef — re-mounting
    // Stage 4 (navigate Stage 4 → 3 → 4 mid-call) created a fresh ref that
    // didn't see the in-flight App-level call, so a SECOND duplicate AI
    // call fired and both setScope writes overwrote each other.
    const [photoAssignBusy, setPhotoAssignBusy] = useState(false);
    const photoAssignBusyRef = useRef(false);
    // Same-project callers join the one active assignment instead of treating
    // `photoAssignBusy` as completion. Stage 3's pre-Stage-4 coordinator can
    // therefore await a watcher/manual run already in progress without firing
    // a duplicate paid vision request or revealing Stage 4 too early.
    const photoAutoAssignCompletionRef = useRef(null);
    // Project identity is a dependency of the attended photo-assignment
    // watcher below. Declare the state before that hook so production
    // minification cannot expose a temporal-dead-zone read during App render.
    const [currentProjectId, setCurrentProjectId] = useState(null);

    // TS-060: Photo OCR seam (DEC-COUNCIL-015)
    const {
        ocrScanning,
        setOcrScanning,
        ocrResults,
        setOcrResults,
        ocrApplying,
        setOcrApplying,
        scanPhotoForText,
        scanPhotosForOCR,
        applyOcrData,
    } = usePhotoOcr({
        apiKey,
        model,
        currentProjectId,
        setFormData,
    });
    // Automatic Stage 5 authoring must not race the photo worker. This receipt
    // is deliberately bound to the painted project + load generation and is
    // published as `saved` only after the exact linked scope crosses the normal
    // project persistence boundary. The revision wakes the quote auto-effect;
    // the ref supplies the synchronous value to its 900 ms fire-time recheck.
    const photoAssignmentReceiptRef = useRef(null);
    const photoReviewSaveInFlightRef = useRef(false);
    const [photoAssignmentReceiptRevision, setPhotoAssignmentReceiptRevision] = useState(0);
    const publishPhotoAssignmentReceipt = useCallback((receipt) => {
        photoAssignmentReceiptRef.current = receipt;
        setPhotoAssignmentReceiptRevision(revision => revision + 1);
    }, []);
    // Quote-critical background work must have a user-visible terminal state.
    // Photo assignment previously failed only in DevTools, so the app could
    // advance while the operator had no indication that client evidence was
    // still unassigned. This shared nav slot is declared before the dispatcher
    // so primary failure, visible fallback, and all-route failure can be shown.
    const [navMessage, setNavMessage] = useState(null);
    // v5.19.24 (Peter 2026-05-05): track which photo IDs have already been
    // through an auto-assign cycle. The auto-assign re-fire decision is
    // based on "have any new photos been added since the last run?" — Peter:
    // "adding a new photo might be the ONLY time it should RE-fire". A pure
    // length comparison would miss the case where the user replaces photos.
    // Using a Set of stable IDs handles add/replace/delete cleanly:
    //   - new photo whose id isn't in the set → re-fire (good)
    //   - photo deleted (no new id added)    → no re-fire (good)
    //   - same photos as before              → no re-fire (good)
    // The set is keyed by photo.id || photo.blobId || photo.s3Key (first
    // available stable identifier) and is cleared on resetForm and
    // completeProjectLoad so a fresh project starts with a clean slate.
    const lastAssignedPhotoIdsRef = useRef(new Set());
    // v5.19.24: surfaces a STOP-AI button + a "live" indicator in the top
    // nav. Cancelled by the user via cancelAiPipeline (defined below); it
    // aborts every in-flight pipeline AND re-arms a fresh master controller
    // so subsequent runs (e.g. a manual RE-ANALYSE after the cancel) work.
    const [aiPipelineCancelToast, setAiPipelineCancelToast] = useState(null);
    // Master abort controller for the current project. Aborted in resetForm()
    // to cancel ALL in-flight AI processes (analysis, summary, rationale, etc.)
    // so they don't write stale results into a fresh project.
    const projectAbortRef = useRef(new AbortController());
    // Project-lifecycle owner for ordinary persistence/media work. AI STOP and
    // inspector-notes edits replace projectAbortRef above, but must not cancel
    // an unrelated save. Reset/project load abort and replace BOTH controllers.
    const projectPersistenceAbortRef = useRef(new AbortController());
    // ─── STOP-button halt latch (2026-07-28) ─────────────────────────────
    // cancelAiPipeline() aborts in-flight work, but the auto-trigger effects
    // below (summary → analysis → WMS → quote draft, photo auto-assign) are a
    // self-healing state machine: the cancel's own busy-flag flips re-ran them
    // and they saw "work missing + idle → dispatch", so STOP during a resumed
    // cascade re-fired the next stage within seconds (three STOPs in a row did
    // nothing on 2026-07-28). This ref latches "the user said stop": every
    // AUTO trigger refuses while it is set. It clears on project load / reset
    // (fresh context) and on any MANUAL pipeline action (re-analyse, summary,
    // WMS button, quote gen) — Peter's stated cancel semantics: "subsequent
    // runs (e.g. a manual RE-ANALYSE after the cancel) work".
    const aiPipelineHaltedRef = useRef(false);
    // v5.54.22 — REVIEW MODE. Opening a project to LOOK at it used to dispatch
    // the whole AI cascade: completeProjectLoad clears the STOP latch above on
    // every load, and the auto-fire effects are keyed on STATE ("summary
    // present, analysis missing"), never on INTENT. So a mid-pipeline job that
    // the operator merely opened from the Run Sheet spent OpenRouter budget and
    // rewrote the record (recorded live 2026-07-28; STOP did not halt it, only
    // a reload did). This ref carries the missing intent: set when a load
    // completes, cleared by any DELIBERATE act — a manual pipeline run, or the
    // first genuine edit the fingerprint comparator confirms. While set, every
    // AUTO trigger refuses, exactly like the STOP latch.
    //
    // Deliberately NOT folded into aiPipelineHaltedRef: App.jsx's autoDraft
    // guard refuses on that latch with no batchMode exemption, so reusing it
    // would stall every batch-runner job at Stage 5.
    const openedForReviewRef = useRef(false);
    const deliberateProjectEditIntentRef = useRef(null);
    // Capture-phase events happen before the child mutates project state. The
    // probe revision guarantees one post-event fingerprint comparison even
    // for controls whose local state would not otherwise rerender App. It is
    // not an automatic-attempt receipt.
    const [reviewEditProbeRevision, setReviewEditProbeRevision] = useState(0);
    const reviewEditProbeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => {
        if (reviewEditProbeTimerRef.current !== null) clearTimeout(reviewEditProbeTimerRef.current);
    }, []);
    // A real Stage 1 import is an explicit operator action, unlike merely
    // opening a saved project. It is the narrow intent token that may clear
    // review mode and resume the Full AI cascade. The all-files-done effect
    // consumes it exactly once; load/hydration never writes this ref.
    const deliberateImportIntentRef = useRef(false);
    // Exact successful Stage 4/5 package save owned by the normal project
    // persistence seam. Stage 5 view navigation may consume this receipt but
    // may never manufacture one from a successful render.
    const stage45PersistenceReceiptRef = useRef(null);
    // The attended automatic Summary's navigation epoch: every automatic run
    // that begins, and every operator choice that revokes (STOP, an accepted
    // navigation, the Data Entry pill, a new import), advances it, so a run
    // that is still working loses its right to select the Summary tab
    // (attended-summary-run.ts). Merely loading or viewing an old summary
    // never writes it.
    const attendedAutomaticRiskIntentEpochRef = useRef(0);
    // What one ordinary automatic Summary run has seen and written; lets the
    // run tell its own enrichment from an operator edit (C21).
    const attendedSummaryWitnessRef = useRef<AttendedSummaryWitness | null>(null);
    // Automatic Summary timers are declared before the useProjectSummary seam
    // for hook-order reasons. They must invoke the current render's closure,
    // never the one captured when a debounce was scheduled.
    const runProjectSummaryRef = useRef(null);
    const handleStage1ImportIntent = useCallback(() => {
        // A new import is the operator going on: a Summary still working for
        // the evidence before it loses its right to select the Summary tab.
        revokeAttendedSummary({ epoch: attendedAutomaticRiskIntentEpochRef });
        // The import's end must read as a new end even when its files settle in
        // the same render they start (a drop of only unreadable types), so its
        // continuation shows the summary again.
        prevFilesProcessedRef.current = false;
        deliberateImportIntentRef.current = true;
        openedForReviewRef.current = false;
        aiPipelineHaltedRef.current = false;
    }, []);
    const [summaryError, setSummaryError] = useState(null);
    // An automatic Summary run the success latch refused while the Summary was
    // out of date (use-project-summary.ts). Stage 1 shows it, never Stage 1.5.
    const [summaryRerunHold, setSummaryRerunHold] = useState(null);
    const [summaryNeedsRefresh, setSummaryNeedsRefresh] = useState(false);
    const [lastSummarizedSnapshot, setLastSummarizedSnapshot] = useState(null);
    const [inspectorNotesAiPaused, setInspectorNotesAiPaused] = useState(false);
    const inspectorNotesAiPausedRef = useRef(false);
    const lastAutoProjectFocusRef = useRef('');
    const isPipelineAbort = (err) => (
        err?.name === 'AbortError'
        || err?.code === 20
        || /aborted|abort|cancelled|canceled|inspector-notes/i.test(String(err?.message || err || ''))
    );

    // No preprocessed-file lane: the Summary is made from the prepared project
    // record, which never stores it (project-summary-evidence.ts).
    const currentProjectSummaryEvidenceFingerprint = useMemo(() => (
        buildProjectSummaryEvidenceFingerprint({
            formData,
            documents,
            photos,
            videoTranscripts,
        })
    ), [formData, documents, photos, videoTranscripts]);
    const currentProjectSummaryEvidenceFingerprintRef = useRef(currentProjectSummaryEvidenceFingerprint);
    currentProjectSummaryEvidenceFingerprintRef.current = currentProjectSummaryEvidenceFingerprint;
    const savedProjectSummaryEvidenceFingerprint = String(
        projectSummary?.summaryEvidenceFingerprint
        || projectSummary?.summary_evidence_fingerprint
        || '',
    );
    const savedProjectSummaryEvidenceFingerprintRef = useRef(savedProjectSummaryEvidenceFingerprint);
    savedProjectSummaryEvidenceFingerprintRef.current = savedProjectSummaryEvidenceFingerprint;
    const projectSummaryEvidenceIsFresh = !!projectSummary
        && !!savedProjectSummaryEvidenceFingerprint
        && savedProjectSummaryEvidenceFingerprint === currentProjectSummaryEvidenceFingerprint;

    const resetProjectSummaryFocusRecoveryAuthority = useCallback(() => {
        const failure = projectSummaryFailureRef.current;
        if (!failure) return;
        const focusRecoveryKey = buildProjectSummaryFocusRecoveryKey({
            projectId: currentProjectIdRef.current,
            loadGeneration: projectLoadGenerationRef.current,
            evidenceFingerprint: currentProjectSummaryEvidenceFingerprintRef.current,
            apiKey,
            model,
            configurationRevision: projectSummaryConfigurationRevisionRef.current,
            failure,
        });
        if (focusRecoveryKey) {
            projectSummaryFocusRecoveryReceiptsRef.current.delete(focusRecoveryKey);
        }
        // A deliberate Try Again may be followed immediately by a real config
        // correction/window return. Do not let the debounce inherit the prior
        // passive focus event.
        lastProjectSummaryFocusSignalAtRef.current = 0;
    }, [apiKey, model]);

    // An ordinary automatic Summary selects its tab from its own durable
    // result, not from the evidence it was scheduled against (C21). The refs
    // are read inside the callback so declaration order further down does not
    // matter. `source` names the trigger at each call site.
    const beginAttendedAutomaticSummaryRun = useCallback((
        _source: string,
        scheduledEvidenceFingerprint: string | null = null,
    ) => beginAttendedAutomaticSummary({
        epoch: attendedAutomaticRiskIntentEpochRef,
        witness: attendedSummaryWitnessRef,
        form: formDataRef,
        evidence: evidencePreparationLiveRef,
        summarizing: isSummarizingRef,
        currentFingerprint: currentProjectSummaryEvidenceFingerprintRef,
        page: currentPageRef,
        stage: stageRef,
        loading: isLoadingProjectRef,
        halted: aiPipelineHaltedRef,
        review: openedForReviewRef,
    }, scheduledEvidenceFingerprint), []);
    // The operator chose something else (STOP, an accepted navigation, the
    // Data Entry pill): an automatic Summary still working may not select its tab.
    const revokeAttendedAutomaticRiskIntent = useCallback(() => {
        revokeAttendedSummary({ epoch: attendedAutomaticRiskIntentEpochRef });
    }, []);
    // Every evidence fingerprint the screen commits while an automatic Summary
    // works. The comparison with what the run wrote itself happens at its save.
    useEffect(() => {
        observeAttendedSummaryEvidence(
            { witness: attendedSummaryWitnessRef, summarizing: isSummarizingRef },
            currentProjectSummaryEvidenceFingerprint,
        );
    }, [currentProjectSummaryEvidenceFingerprint]);

    // Track exact evidence freshness, rather than count-only approximations.
    // A same-count photo/document replacement is a new summary input, and a
    // legacy summary without a durable fingerprint reads as out of date.
    useEffect(() => {
        if (!projectSummary) return;
        setSummaryNeedsRefresh(!projectSummaryEvidenceIsFresh);
    }, [
        projectSummary,
        projectSummaryEvidenceIsFresh,
    ]);

    // Once the screen shows the latest published Summary fresh, a later return
    // to that run's starting evidence is the operator's change, and the success
    // latch lets the stale Summary re-run (use-project-summary.ts).
    useEffect(() => {
        if (!projectSummaryEvidenceIsFresh) return;
        markProjectSummarySuccessSeenFresh(projectSummarySuccessRef, {
            projectId: currentProjectIdRef.current ?? currentProjectId,
            evidenceFingerprint: currentProjectSummaryEvidenceFingerprint,
        });
    }, [projectSummary, projectSummaryEvidenceIsFresh, currentProjectSummaryEvidenceFingerprint, currentProjectId]);
    const summaryRerunHeld = projectSummaryRerunHoldIsCurrent(summaryRerunHold, {
        projectId: currentProjectId,
        evidenceFingerprint: currentProjectSummaryEvidenceFingerprint,
        summaryEvidenceFingerprint: savedProjectSummaryEvidenceFingerprint,
    });

    // A summary a run saved in this session while Stage 1 was on screen. The
    // Summary tab speaks only for this, never on project open (B10).
    const [summaryLanding, setSummaryLanding] = useState(null);
    const noteSummaryLanded = useCallback((summary) => {
        setSummaryLanding(prev => ({ seq: (prev?.seq || 0) + 1, summary }));
    }, []);
    // K11: a summary run an edit stopped (a stale-run error while it prepared
    // its evidence or saved its writes). Stage 1's header says why until the
    // next run starts or the summary is fresh again (stage1WaitingNotice).
    const [summaryRunInterrupted, setSummaryRunInterrupted] = useState(false);
    const noteSummaryRunInterrupted = useCallback(() => setSummaryRunInterrupted(true), []);

    // (QM persistence useEffect moved further down, after currentProjectId is declared)

    // ─── Auto-trigger Project Summary when all files finish processing ───
    // Watches the processing reducer state directly - fires as soon as all files
    // reach done/error status, even while the Sorting Floor is still visible.
    // This pre-caches the summary so it's instant when the user clicks Continue.
    const prevFilesProcessedRef = useRef(false);
    useEffect(() => {
        if (pipelineOwnerRef.current === 'batch-runner') return;
        const allDone = procState.totalFiles > 0 && procState.processedFiles === procState.totalFiles;
        const wasDone = prevFilesProcessedRef.current;
        prevFilesProcessedRef.current = allDone;

        const importContinuation = importedProjectContinuation({
            allDone,
            wasDone,
            hasImportIntent: deliberateImportIntentRef.current,
            hasProjectSummary: !!projectSummary,
            // Derive from the exact saved/current fingerprints in this render.
            // Waiting for the mirrored state effect can consume the one-shot
            // import intent against an old same-count summary.
            summaryNeedsRefresh: !projectSummaryEvidenceIsFresh,
        });
        if (importContinuation === 'none') return;
        // One deliberate import produces one continuation. Consuming before
        // any async work prevents a later render from dispatching a duplicate
        // summary/analysis run for the same completed reducer state.
        deliberateImportIntentRef.current = false;

        if (aiPipelineHaltedRef.current || openedForReviewRef.current) return; // STOP latch — no auto-fires until manual restart
        if (!apiKey) return;
        if (currentPage !== 'quotemaker') return;
        if (stage !== 1) return;
        if (!inspectorNotesCanAutoRefresh) return;
        if (inspectorNotesAiPaused) return;
        if (importContinuation === 'show-existing-summary') {
            // This saved summary is already fresh for the imported evidence.
            // Show its tab.
            requestCockpitTab('summary');
            return;
        }
        if (summaryRerunHeld) {
            // The import settled without new summary evidence (only unreadable
            // files, say), and the summary on screen is held: an automatic run
            // would be refused, so the summary as it is is shown.
            requestCockpitTab('summary');
            return;
        }
        if (isSummarizing) return;

        // Brief delay to let any straggler post-processing (extraction,
        // classification) flush state. 500ms is enough - by the time
        // procState.processedFiles === procState.totalFiles fires, the
        // per-file work is done. The previous 3000ms was over-defensive
        // and added unnecessary wait time before Project Summary kicked
        // off. Trimmed for end-to-end
        // speed per Peter's request 2026-04-09.
        const scheduledEvidenceFingerprint = currentProjectSummaryEvidenceFingerprint;
        const timer = setTimeout(() => {
            if (pipelineOwnerRef.current === 'batch-runner') return;
            if (aiPipelineHaltedRef.current || openedForReviewRef.current) return; // STOP latch (re-check at fire time)
            if (currentPageRef.current !== 'quotemaker' || Number(stageRef.current) !== 1) return;
            if (isLoadingProjectRef.current) return;
            if (currentProjectSummaryEvidenceFingerprintRef.current !== scheduledEvidenceFingerprint) return;
            console.log('[QM] All files processed \u2014 auto-triggering Project Summary in background');
            const summaryRun = beginAttendedAutomaticSummaryRun(
                'stage1-import-summary',
                scheduledEvidenceFingerprint,
            );
            if (!summaryRun) return;
            // autoAdvanceOnReady: once the summary is durably saved, show its
            // Job cockpit tab. The manual-entry debounce follows the
            // same navigation contract below. The tab is selected from the
            // saved Summary (onSummarySaved), never from the pre-preparation
            // fingerprint this timer was scheduled against.
            runProjectSummaryRef.current?.(summaryRun);
        }, 500);
        return () => clearTimeout(timer);
    }, [procState.totalFiles, procState.processedFiles, apiKey, currentPage, stage, inspectorNotesCanAutoRefresh, inspectorNotesAiPaused, projectSummary, projectSummaryEvidenceIsFresh, summaryRerunHeld, currentProjectSummaryEvidenceFingerprint, isSummarizing, formData.notes, documents.length, photos.length, beginAttendedAutomaticSummaryRun, requestCockpitTab]);

    // ─── Auto-trigger Project Summary for manual data entry (no import) ───
    // For users who type notes directly without importing files, the import-based
    // auto-trigger never fires. This debounced effect pre-caches the summary
    // after 2 seconds of typing inactivity when sufficient data exists.
    // (Was 5 seconds - trimmed to 2 seconds for end-to-end speed per Peter's
    // request 2026-04-09. Two seconds is still enough debounce that it doesn't
    // fire while the user is actively typing.)
    // SPEC 2026-10-21 B2: it also re-makes a summary that an edit put out of
    // date, on Stage 1, until a later stage is complete
    // (inspectorNotesCanAutoRefresh). There is no separate summary page to wait
    // for any more, so the edit itself is the only trigger.
    useEffect(() => {
        if (pipelineOwnerRef.current === 'batch-runner') return;
        if (aiPipelineHaltedRef.current || openedForReviewRef.current) return; // STOP latch \u2014 no auto-fires until manual restart
        if (currentPage !== 'quotemaker' || stage !== 1) return;
        if (!inspectorNotesCanAutoRefresh) return;
        if (inspectorNotesAiPaused) return;
        if (!apiKey) return;
        if (isSummarizing || isStage1Processing) return;
        if (projectSummaryEvidenceIsFresh) return;
        // A held summary (summaryRerunHeld) would only be refused again by the
        // success latch.
        if (summaryRerunHeld) return;
        // Typed-notes Summary (O-S9): the raw length test counted markup, so a cleared editor (`<p><br></p>` repeated) passed it.
        // C13: once a summary exists, its own admission rule decides, so short notes never leave Full AI waiting for a press.
        const hasData = canAutoSummarise({
            hasSummary: !!projectSummary,
            notes: formData.notes,
            documentCount: documents.length,
            photoCount: photos.length,
            transcriptCount: videoTranscripts.length,
            videoCount: videos.length,
        });
        if (!hasData) return;

        const scheduledEvidenceFingerprint = currentProjectSummaryEvidenceFingerprint;
        const timer = setTimeout(() => {
            if (pipelineOwnerRef.current === 'batch-runner') return;
            if (aiPipelineHaltedRef.current || openedForReviewRef.current) return; // STOP latch (re-check at fire time)
            if (currentPageRef.current !== 'quotemaker' || Number(stageRef.current) !== 1) return;
            if (isLoadingProjectRef.current) return;
            if (currentProjectSummaryEvidenceFingerprintRef.current !== scheduledEvidenceFingerprint) return;
            console.log('[QM] Sufficient data detected \u2014 auto-triggering Project Summary in background');
            const summaryRun = beginAttendedAutomaticSummaryRun(
                'stage1-manual-entry-summary',
                scheduledEvidenceFingerprint,
            );
            if (!summaryRun) return;
            runProjectSummaryRef.current?.(summaryRun);
        }, 2000);
        return () => clearTimeout(timer);
    }, [currentPage, stage, inspectorNotesCanAutoRefresh, inspectorNotesAiPaused, apiKey, model, isSummarizing, isStage1Processing, projectSummary, projectSummaryEvidenceIsFresh, summaryRerunHeld, currentProjectSummaryEvidenceFingerprint, formData.notes, documents.length, photos.length, videoTranscripts.length, videos.length, beginAttendedAutomaticSummaryRun]);

    // Recover a latched terminal summary exactly once when the active key,
    // selected model, model-lane configuration, or external provider/privacy
    // context may have changed. The latch fingerprint includes the revision;
    // an unchanged failure is retained and no further run occurs until the
    // next explicit signal or the operator presses Try Again.
    useEffect(() => {
        if (!projectSummaryFailureRef.current) return;
        if (pipelineOwnerRef.current === 'batch-runner') return;
        if (aiPipelineHaltedRef.current || openedForReviewRef.current) return; // STOP latch — no auto-fires until manual restart
        if (!apiKey || currentPage !== 'quotemaker' || stage !== 1) return;
        const scheduledEvidenceFingerprint = currentProjectSummaryEvidenceFingerprint;
        const timer = setTimeout(() => {
            if (pipelineOwnerRef.current === 'batch-runner') return;
            if (aiPipelineHaltedRef.current || openedForReviewRef.current) return; // STOP latch (re-check at fire time)
            if (currentPageRef.current !== 'quotemaker' || Number(stageRef.current) !== 1) return;
            if (isLoadingProjectRef.current) return;
            if (currentProjectSummaryEvidenceFingerprintRef.current !== scheduledEvidenceFingerprint) return;
            const summaryRun = beginAttendedAutomaticSummaryRun(
                'summary-config-recovery',
                scheduledEvidenceFingerprint,
            );
            if (!summaryRun) return;
            runProjectSummaryRef.current?.(summaryRun);
        }, 250);
        return () => clearTimeout(timer);
    }, [apiKey, model, projectSummaryRecoveryRevision, currentProjectSummaryEvidenceFingerprint, beginAttendedAutomaticSummaryRun]);

    // H2 (SPEC 2026-10-21 §14.4): Full AI on a failed summary. The paid summary
    // is tried once more by itself after a short wait, and Stage 1's header says
    // so: one more paid call, only after a failure, one per evidence
    // fingerprint, so it never loops. The wait is before the retry starts; the
    // AI call itself has no timeout. A failure the provider calls not retryable
    // (a refused key, say) would only fail again, so it is not retried. If the
    // retry fails too, the reason and Try again stay in the Summary tab. A
    // re-make of an earlier summary then goes on with that summary as it is
    // (the hold). A job with no summary stops there.
    const summaryFailureRetriedRef = useRef(new Set());
    const [summaryFailureRetryWaiting, setSummaryFailureRetryWaiting] = useState(false);
    useEffect(() => {
        if (!summaryError || isSummarizing || isFastTrackManualScope) return;
        // While Stage 1 still reads files the evidence is about to change, and
        // the import's own summary follows it: a retry now pays for a summary
        // of the evidence before the import (the 2 s remake waits the same way).
        if (isStage1Processing) return;
        if (pipelineOwnerRef.current === 'batch-runner') return;
        if (aiPipelineHaltedRef.current || openedForReviewRef.current) return;
        if (!apiKey || currentPage !== 'quotemaker' || stage !== 1) return;
        if (!inspectorNotesCanAutoRefresh || inspectorNotesAiPaused) return;
        const failure = projectSummaryFailureRef.current;
        if (!failure) return;
        const scheduledEvidenceFingerprint = currentProjectSummaryEvidenceFingerprint;
        const retryKey = `${currentProjectIdRef.current || ''}|${scheduledEvidenceFingerprint}`;
        if (failure.retryable === false || summaryFailureRetriedRef.current.has(retryKey)) {
            // No more automatic tries for this evidence. A re-make of an
            // earlier summary failed, and that summary is still on screen: Full
            // AI goes on with it as it is, the way it does with one QuoteMaker
            // refused to pay for again, and the
            // failure stays in the Summary tab with Try again.
            if (projectSummary && savedProjectSummaryEvidenceFingerprint
                && savedProjectSummaryEvidenceFingerprint !== scheduledEvidenceFingerprint) {
                const hold = {
                    projectId: String(currentProjectIdRef.current ?? currentProjectId ?? ''),
                    evidenceFingerprint: scheduledEvidenceFingerprint,
                    summaryEvidenceFingerprint: savedProjectSummaryEvidenceFingerprint,
                };
                setSummaryRerunHold(prev => (
                    prev
                    && prev.projectId === hold.projectId
                    && prev.evidenceFingerprint === hold.evidenceFingerprint
                    && prev.summaryEvidenceFingerprint === hold.summaryEvidenceFingerprint
                        ? prev
                        : hold
                ));
            }
            return;
        }
        const waitMs = Math.max(
            SUMMARY_FAILURE_RETRY_MS,
            Math.min(Number(failure.retryAfterSeconds) || 0, 60) * 1000,
        );
        setSummaryFailureRetryWaiting(true);
        const timer = setTimeout(() => {
            setSummaryFailureRetryWaiting(false);
            if (pipelineOwnerRef.current === 'batch-runner') return;
            if (aiPipelineHaltedRef.current || openedForReviewRef.current) return;
            if (currentPageRef.current !== 'quotemaker' || Number(stageRef.current) !== 1) return;
            if (isLoadingProjectRef.current) return;
            if (currentProjectSummaryEvidenceFingerprintRef.current !== scheduledEvidenceFingerprint) return;
            const summaryRun = beginAttendedAutomaticSummaryRun(
                'summary-failure-retry',
                scheduledEvidenceFingerprint,
            );
            if (!summaryRun) return;
            summaryFailureRetriedRef.current.add(retryKey);
            runProjectSummaryRef.current?.({ ...summaryRun, retryAfterFailure: true });
        }, waitMs);
        return () => {
            clearTimeout(timer);
            setSummaryFailureRetryWaiting(false);
        };
    }, [summaryError, isSummarizing, isStage1Processing, isFastTrackManualScope, apiKey, currentPage, stage, inspectorNotesCanAutoRefresh, inspectorNotesAiPaused, projectSummary, savedProjectSummaryEvidenceFingerprint, currentProjectSummaryEvidenceFingerprint, beginAttendedAutomaticSummaryRun]);

    // Scope Builder (Stage 4) data - MUST be before conditional return
    const [scope, setScope] = useState([]);
    const scopeRef = useRef(scope);
    useEffect(() => { scopeRef.current = scope; }, [scope]);
    // M3: the project-level programme narrative of an old job's Work Method.
    // It loads and saves with the project; nothing writes a new one.
    const [workMethodOverallProgramme, setWorkMethodOverallProgramme] = useState(null);
    // M2 polish: per-project applied-learning attribution entries
    // (shape: { id, section, rationale, strength, appliedBy, appliedAt, ... }).
    // Produced by semantic retrieval triage in analysis-pipeline.js; persisted
    // on projectRecord so the audit trail survives save/reload.
    const [appliedLearnings, setAppliedLearnings] = useState([]);
    // An old job's strategy and project-level Work Method (`project.wms`).
    // They load and save with the project and old jobs' Stage 4, Stage 5 and
    // Internal Pricing still read them; the old pipeline that wrote them is
    // gone (docs/old-pipeline-removal/PLAN.md).
    const [projectStrategy, setProjectStrategy] = useState(null);
    const [projectWMS, setProjectWMS] = useState(null);
    // Nothing writes a Work Method or a quote in the browser any more, so
    // neither is ever in flight. The shared busy
    // readers (the nav bar, Stage navigation, the Assistant) still take these.
    const projectWMSGenerating = false;
    const isGenerating = false;
    // stageRef mirrors the current stage so async paths can read the user's
    // CURRENT location at completion time instead of a stale closure value.
    const stageRef = useRef(1);
    // Saved with the project (`legacyBannerDismissed`): an old job keeps it.
    const [legacyBannerDismissed, setLegacyBannerDismissed] = useState(false);
    // Ref-backed latest Work Method, for the HBCF and costing readers that run
    // after an await.
    const projectWMSRef = useRef(projectWMS);
    useEffect(() => { projectWMSRef.current = projectWMS; }, [projectWMS]);
    // v5.37.16: tracks projects with an in-flight first-save Quote # allocation
    // so rapid-fire saves on the same project don't queue a second backend
    // call before the first completes. Backend is idempotent (partial unique
    // index) so a double-call is harmless, but the frontend dedup avoids the
    // wasted network roundtrip and the duplicate `[QM Quote #] Allocated ...`
    // log line. Cleared in the allocator's finally block.
    const qmQuoteNoAllocationInFlightRef = useRef(new Set());
    const clearInspectorNotesAiPause = useCallback(() => {
        inspectorNotesAiPausedRef.current = false;
        setInspectorNotesAiPaused(false);
    }, []);

    const abortActiveAiForInspectorNotes = useCallback((reason = 'inspector-notes-edit') => {
        try { projectAbortRef.current.abort(reason); } catch {}
        projectAbortRef.current = new AbortController();

        // v5.19.24 (Peter 2026-05-05): photo auto-assign is INTENTIONALLY left
        // running here. Previous behaviour (lines below, removed) aborted the
        // photo-assign call whenever the user touched the inspector notes
        // textarea. That cost users 30-60s of work mid-typing for no reason —
        // photo→scope assignment doesn't depend on the notes content, so
        // re-typing notes shouldn't kill it. Pipeline cancellation is now
        // exclusively a user-initiated action via cancelAiPipeline() (the
        // STOP-AI button) plus implicit on resetForm() / completeProjectLoad().
        isAnalyzingRef.current = false;
        isSummarizingRef.current = false;
        setIsAnalyzing(false);
        setIsSummarizing(false);
    }, []);

    // ──────────────────────────────────────────────────────────────────────
    // PHOTO AUTO-ASSIGN — App-level dispatcher (v5.19.24, Peter 2026-05-05)
    // ──────────────────────────────────────────────────────────────────────
    // Replaces:
    //   - the one-shot inline call inside runAnalysis (couldn't react to
    //     photos imported AFTER analysis finished)
    //   - Stage 4's duplicated AI call (drift risk: same prompt in two files)
    //
    // dispatchPhotoAutoAssign owns the full lifecycle:
    //   1. Skip-or-fire decision based on lastAssignedPhotoIdsRef diff vs
    //      current photo-id set (Peter: "adding a new photo might be the
    //      ONLY time it should RE-fire after it has already assigned").
    //   2. Document-photo filter + forensic-locator wiring (handled inside
    //      autoAssignPhotosToScope service).
    //   3. Apply via setScope updater that re-checks scope fingerprint to
    //      catch scope-changed-mid-call races.
    //   4. Hero photo assignment.
    //   5. Lifecycle logging at every step ([photo-auto-assign] prefix) so
    //      Peter can see in DevTools whether the pre-emptive path actually
    //      ran on his machine.
    //
    // Returns nothing; surfaces success/failure via console + the
    // photoAssignBusy state which the STOP-AI pill watches.
    // ──────────────────────────────────────────────────────────────────────
    // The paid photo matcher owns the exact semantic inputs it was dispatched
    // with. Keep forensic evidence on a live render mirror so the old async
    // closure can prove those inputs are still current before it applies.
    const photoAssignmentForensicObservationsRef = useRef(photoAnalysis?.observations || null);
    photoAssignmentForensicObservationsRef.current = photoAnalysis?.observations || null;

    const {
        retryPhotoAssignment,
        getStage5PhotoGate,
        ensureStage5PhotoGate,
    } = usePhotoAutoAssign({
        apiKey,
        model,
        photos,
        scope,
        stage,
        currentPage,
        photoAnalysis,
        projectWMS,
        projectCostingSheet,
        selectedHeroImage,
        photoAssignBusy,
        photoAssignmentReceiptRevision,
        currentProjectId,
        setScope,
        setPhotoAssignBusy,
        setSelectedHeroImage,
        setNavMessage,
        publishPhotoAssignmentReceipt,
        photoAssignmentForensicObservationsRef,
        photosRef,
        scopeRef,
        stageRef,
        projectWMSRef,
        projectCostingSheetRef,
        selectedHeroImageRef,
        currentProjectIdRef: { get current() { return currentProjectIdRef.current; }, set current(v) { currentProjectIdRef.current = v; } },
        projectLoadGenerationRef: { get current() { return projectLoadGenerationRef.current; }, set current(v) { projectLoadGenerationRef.current = v; } },
        paintedProjectLoadContextRef: { get current() { return paintedProjectLoadContextRef.current; }, set current(v) { paintedProjectLoadContextRef.current = v; } },
        projectAbortRef,
        autoAssignAbortRef,
        aiPipelineHaltedRef,
        openedForReviewRef,
        lastAssignedPhotoIdsRef,
        photoAssignBusyRef,
        photoAssignmentReceiptRef,
        photoReviewSaveInFlightRef,
        photoAutoAssignCompletionRef,
        saveCurrentProjectRef: { get current() { return saveCurrentProjectRef.current; }, set current(v) { saveCurrentProjectRef.current = v; } },
    });

    // Once a builder manually dispositions the remaining photos in Stage 4,
    // promote that exact completed scope through the same awaited durability
    // boundary as automatic assignment. The pure predicate requires evidence
    // to have changed since the terminal receipt, so a save failure cannot
    // re-arm itself and an untouched fallback result still needs explicit NEXT
    // acceptance.
    useEffect(() => {
        if (Number(stage) !== 4
            || aiPipelineHaltedRef.current
            || openedForReviewRef.current
            || photoAssignBusyRef.current
            || photoReviewSaveInFlightRef.current) return;
        const receipt = photoAssignmentReceiptRef.current;
        const assignablePhotos = selectAssignablePhotos(photos).photos;
        const coverage = assignablePhotoCoverage(assignablePhotos, scope);
        if (!canPersistCompletedStage4PhotoReview({
            receipt,
            currentProjectId: currentProjectIdRef.current,
            currentLoadGeneration: projectLoadGenerationRef.current,
            currentFingerprint: photoAssignmentReceiptFingerprint(assignablePhotos, scope),
            coverage,
        })) return;

        const timerId = setTimeout(() => {
            void ensureStage5PhotoGate({ trigger: 'stage4-manual-photo-completion' });
        }, 250);
        return () => clearTimeout(timerId);
    }, [stage, photos, scope, photoAssignmentReceiptRevision, ensureStage5PhotoGate]);

    // ──────────────────────────────────────────────────────────────────────
    // CANCEL — single user-facing entry point that aborts every in-flight
    // AI pipeline. Wired to the STOP-AI pill in the top nav. Does NOT fire
    // on stage navigation, inspector-notes edit, or other implicit user
    // activity — only on this explicit click and on resetForm() /
    // completeProjectLoad().
    // ──────────────────────────────────────────────────────────────────────
    const cancelAiPipeline = useCallback((opts = {}) => {
        const reason = opts.reason || 'user-cancelled';

        // Latch FIRST: the state flips below re-run the auto-trigger effects
        // in this same commit, and they must already see "halted" or they
        // immediately re-dispatch the next stage (the 2026-07-28 STOP bug).
        aiPipelineHaltedRef.current = true;
        deliberateImportIntentRef.current = false;
        revokeAttendedAutomaticRiskIntent();

        // A STOP during a Quote Run is an instruction about the whole run, not
        // only the project on screen. Raise the queue's own stop BEFORE the
        // project signal is aborted (dispatchEvent is synchronous, and the
        // File Explorer listener aborts the runner controller synchronously),
        // so the queue records an operator cancellation. Without this the
        // queue sees only an aborted stage, records that one project as
        // failed, and starts spending AI credit on the next project the
        // operator just tried to stop.
        if (batchActiveRef.current && typeof window !== 'undefined') {
            try {
                // serviceStopOwnedByCaller: the Quote Run sheet's run bar
                // settles the queue stop itself (A3 review round 3), so the
                // File Explorer listener aborts the runner and does not stop it twice.
                // aiStageStopped: this path aborts the job's AI stage just
                // below, so App's stop listener only marks the run as stopping
                // and does not stop it again (A3 review round 4). Without the
                // mark the listener would call back in here, for ever.
                window.dispatchEvent(new CustomEvent('qm-batch-queue-abort-requested', {
                    detail: { reason, serviceStopOwnedByCaller: opts.serviceStopOwnedByCaller === true, aiStageStopped: true },
                }));
            } catch { /* stopping the visible project still proceeds below */ }
        }

        // Master controller — analysis, summary, costing, WMS, quote gen
        // all share this signal in some form. Runs pin this signal at their
        // start, so the re-arm below only serves runs started AFTER the STOP.
        try { projectAbortRef.current.abort(reason); } catch {}
        projectAbortRef.current = new AbortController();

        // Photo auto-assign has its own controller because Peter wanted
        // the ability to keep it alive across analysis cancels in some
        // flows. Cancel it explicitly here.
        if (autoAssignAbortRef.current) {
            try { autoAssignAbortRef.current.abort(reason); } catch {}
            autoAssignAbortRef.current = null;
        }
        const cancelledPhotoReceipt = photoAssignmentReceiptRef.current;
        if (cancelledPhotoReceipt?.status === 'in-flight') {
            publishPhotoAssignmentReceipt({
                ...cancelledPhotoReceipt,
                status: 'aborted',
                reason,
            });
        }
        photoAssignBusyRef.current = false;
        setPhotoAssignBusy(false);

        // Flip every busy/streaming flag we own. Each setter is a no-op if
        // the value is already false, so it's safe to call them
        // unconditionally.
        try { PipelineMonitor.cancelRunningForProject?.(currentProjectIdRef.current, reason); } catch {}
        isAnalyzingRef.current = false;
        isSummarizingRef.current = false;
        setIsAnalyzing(false);
        setIsSummarizing(false);
        setCostingRegenerating(false);

        // 3.5s confirmation strip near the top nav. Auto-clears.
        const stamp = Date.now();
        setAiPipelineCancelToast({ at: stamp, message: 'AI pipeline cancelled' });
        setTimeout(() => {
            setAiPipelineCancelToast(prev => (prev?.at === stamp ? null : prev));
        }, 3500);

        console.info(`[photo-auto-assign] User cancelled AI pipeline (${reason})`);
    }, [publishPhotoAssignmentReceipt, revokeAttendedAutomaticRiskIntent]);
    // The Quote Run stop listener (below, empty dependency list) reads the
    // current cancelAiPipeline through this ref, so it never holds a stale one.
    const cancelAiPipelineRef = useRef(cancelAiPipeline);
    cancelAiPipelineRef.current = cancelAiPipeline;

    const pauseAiForInspectorNotes = useCallback(({ reason = 'focus' } = {}) => {
        if (currentPageRef.current !== 'quotemaker') return;
        if (stage !== 1) return;

        const isFocusOnly = reason === 'focus';
        const hasActivePipeline = isAnalyzingRef.current
            || isSummarizingRef.current
            || !!autoAssignAbortRef.current;

        // Focus alone never aborts/pauses the pipeline — only an actual edit
        // does (Peter 2026-06-05). The Stage-1 caller already no-ops on focus;
        // this is defence-in-depth against any future focus-only caller.
        if (isFocusOnly) return;
        if (!hasActivePipeline && !inspectorNotesCanAutoRefresh) return;

        const shouldAbort = hasActivePipeline;

        inspectorNotesAiPausedRef.current = true;
        setInspectorNotesAiPaused(true);
        if (!isFocusOnly && inspectorNotesCanAutoRefresh) {
            setSummaryNeedsRefresh(true);
        }

        if (shouldAbort) {
            console.info(`[QM] Inspector notes ${reason} - pausing active AI pipeline`);
            abortActiveAiForInspectorNotes(`inspector-notes-${reason}`);
        }
    }, [abortActiveAiForInspectorNotes, inspectorNotesCanAutoRefresh, stage]);

    // Leaving Stage 1 or QuoteMaker clears the pause. While the operator stays,
    // the pause also releases by itself (B4, below continueAiAfterInspectorNotes).
    useEffect(() => {
        if (!inspectorNotesAiPaused) return;
        if (currentPage !== 'quotemaker' || stage !== 1) {
            clearInspectorNotesAiPause();
        }
    }, [clearInspectorNotesAiPause, currentPage, inspectorNotesAiPaused, stage]);

    // M2 polish: retrieval mode telemetry - 'semantic' | 'legacy' | 'none'.
    // Surfaced in the analysis toast so the user can see when semantic
    // retrieval degraded to the legacy path (e.g. missing embeddings).
    const [learningsRetrievalMode, setLearningsRetrievalMode] = useState('none');

    // AI Learning tracking
    const [aiGeneratedItems, setAiGeneratedItems] = useState({});
    const [originalAiScope, setOriginalAiScope] = useState(null);

    // Per-project default margin percentage (projects.default_margin_percent).
    // Saved with the project as before; since build-up pricing step 2
    // (2026-11-03) nothing prices from it.
    const [projectDefaultMargin, setProjectDefaultMargin] = useState(15);

    // Current read-only Pricing authority for every costing freshness decision.
    // A persisted project may contain the historical snapshot used to create its
    // sheet, but only current BusinessPricing can prove that sheet is still fresh.
    const pricingPrincipal = user?.id || user?.userId || user?.sub || user?.email || null;
    const [currentBusinessPricingSnapshot, setCurrentBusinessPricingSnapshot] = useState(null);
    const [businessPricingSnapshotStatus, setBusinessPricingSnapshotStatus] = useState('loading');
    useEffect(() => {
        const accessToken = typeof sessionStorage !== 'undefined'
            ? sessionStorage.getItem('qm_access_token')
            : null;
        if (!localTenantSessionVerified || !pricingPrincipal || !accessToken) {
            setCurrentBusinessPricingSnapshot(null);
            setBusinessPricingSnapshotStatus('unavailable');
            return undefined;
        }
        let active = true;
        let requestId = 0;
        const refreshCurrentBusinessPricing = async () => {
            const thisRequest = ++requestId;
            if (active) {
                // Never keep presenting the previous rate book as current while
                // a strict refresh is in flight.
                setCurrentBusinessPricingSnapshot(null);
                setBusinessPricingSnapshotStatus('loading');
            }
            try {
                const snapshot = await BusinessPricingService.getDataReadOnly({ failIfMissing: true });
                if (!active || thisRequest !== requestId) return;
                if (snapshot && typeof snapshot === 'object') {
                    setCurrentBusinessPricingSnapshot(snapshot);
                    setBusinessPricingSnapshotStatus('ready');
                } else {
                    setCurrentBusinessPricingSnapshot(null);
                    setBusinessPricingSnapshotStatus('unavailable');
                }
            } catch {
                if (!active || thisRequest !== requestId) return;
                setCurrentBusinessPricingSnapshot(null);
                setBusinessPricingSnapshotStatus('unavailable');
            }
        };
        refreshCurrentBusinessPricing();
        const unsubscribePricingInvalidation = subscribePricingAuthorityInvalidation(
            () => refreshCurrentBusinessPricing(),
        );
        return () => {
            active = false;
            requestId += 1;
            unsubscribePricingInvalidation();
        };
    }, [pricingPrincipal, localTenantSessionVerified]);

    // ─── Defensive: clear isStage1Processing whenever we leave Stage 1 ───
    // isStage1Processing is a child-driven flag synced from Stage1DataEntry's
    // internal processing state. When that component unmounts (navigation to
    // Risk Scan, Scope Builder, Final Quote, or any non-QM
    // page), the flag can be left at `true` if the sync effect last ran during
    // active processing. Nothing else in the app resets it except resetForm().
    //
    // A stale `true` here used to hide the NEXT button on stages 2 and 3 (the
    // render gate incorrectly used `!isStage1Processing` without stage scoping).
    // The render gate has been tightened, but we also clear the flag here as
    // belt-and-braces so every other place that reads isStage1Processing gets
    // a clean value as soon as the user is no longer on Data Entry.
    useEffect(() => {
        const onDataEntry = currentPage === 'quotemaker' && stage === 1;
        if (!onDataEntry && isStage1Processing) {
            setIsStage1Processing(false);
        }
    }, [currentPage, stage, isStage1Processing]);

    // Stage 5 (Final Quote) data - MUST be before conditional return
    const [quoteData, setQuoteData] = useState(null);
    const quoteDataRef = useRef(quoteData);
    useEffect(() => { quoteDataRef.current = quoteData; }, [quoteData]);
    const [quoteNote, setQuoteNote] = useState('');
    const quoteNoteRef = useRef(null);
    // Live mirror of quoteNote for the useQuoteEditor hook's openForNavigation
    // note-precedence contract (DEC-COUNCIL-013 Hashirama F2 — the lint guard
    // at quote-editor-funnel-guard.test.js asserts both the ref declaration
    // AND this `quoteNoteRef.current = quoteNote` sync effect exist).
    useEffect(() => { quoteNoteRef.current = quoteNote; }, [quoteNote]);

    // Stage 4 and Stage 5 are peer views over the same authoritative scope.
    // Only eject an actually empty legacy Stage 5 state; a committed Stage 4
    // scope can hydrate the Stage 5 preview while the quote author completes.
    useEffect(() => {
        if (currentPage !== 'quotemaker' || !QM_STATE_KEY) return;
        if (stage !== 5) return;
        if (quoteData || (Array.isArray(scope) && scope.length > 0)) return;
        editor.reset();
        setStageDirection('back');
        setStage(4);
    }, [currentPage, stage, quoteData, scope]);

    // Scope Builder GST mode - lifted from the Scope Builder component so quote generation
    // knows whether stored amounts are inc-GST or ex-GST. Defaults to true
    // (inc-GST) matching the Scope Builder component's own default.
    const [scopeGstInclusive, setScopeGstInclusive] = useState(true);
    const scopeGstInclusiveRef = useRef(scopeGstInclusive);
    scopeGstInclusiveRef.current = scopeGstInclusive;
    // The Stage 5 quote lock. The browser's quote writer is gone, so nothing
    // takes it any more; the Stage 5 save, costing refresh and navigation
    // guards still read it.
    const quoteGenerationSingleflightRef = useRef(null);
    // Stage 5 save and replacement authoring are mutually exclusive project
    // writers. A save can await hero upload and local-folder copies for a long
    // time; without this synchronous lease a replacement run could claim the
    // quote singleflight mid-save and the older save could then overwrite it.
    const quoteEditorSaveInFlightRef = useRef(null);
    // Builder statutory decisions are one commercial transaction: formData,
    // scope, quote and Internal Price Working must cross one save boundary.
    // This synchronous owner prevents an unrelated autosave/navigation save
    // from being coalesced over the decision while its costing is prepared.
    const hbcfDecisionSaveOwnerRef = useRef(null);
    if (!quoteGenerationSingleflightRef.current) {
        quoteGenerationSingleflightRef.current = createProjectSingleflight('Final Quote');
    }
    // Nav-bar message slot is declared with the photo-assignment lifecycle
    // state above so background evidence failures can use it as well.
    const dismissNavMessage = useCallback((id) => {
        setNavMessage(prev => (prev && prev.id === id ? null : prev));
    }, []);
    useEffect(() => {
        if (navMessage?.id !== 'photo-auto-assign-selection') return undefined;
        const timerId = setTimeout(() => {
            dismissNavMessage('photo-auto-assign-selection');
        }, 5000);
        return () => clearTimeout(timerId);
    }, [dismissNavMessage, navMessage?.id]);
    // === PROJECT SYSTEM STATE ===
    const [assistantDraftSessionKey, setAssistantDraftSessionKey] = useState(() => createAssistantDraftSessionKey());
    // Ref mirror of currentProjectId so deferred callbacks (setTimeout-based
    // WMS auto-fire, async completion handlers) can read the LIVE project id
    // rather than a stale closure snapshot. Needed for nav-away detection.
    const currentProjectIdRef = useRef(null);
    useEffect(() => { currentProjectIdRef.current = currentProjectId; }, [currentProjectId]);
    // O1 (Peter, 2026-10-28; review V3): the one setScope App hands its screens (Stage 4, Stage 5, the Assistant). It does
    // nothing while the Quote Brain writes the open job's quote. Stable (no deps), so it pins nothing; loads keep setScope.
    const setScopeFromScreens = useMemo(
        () => scopeSetterRefusingWhileQuoteBrainWrites(setScope, () => currentProjectIdRef.current),
        [],
    );
    // Project summaries intentionally expose lifecycle-only aiContext. Keep
    // only the immutable original snapshot/scope subset from the full loaded
    // Project so the first ordinary save after a cold reload cannot recapture
    // an edited quote or erase its first-draft baseline.
    const loadedOriginalSnapshotContextRef = useRef({ projectId: null, context: {} });
    // Removal intent is session-only until a full, optimistic Project save
    // carries and acknowledges it. Project-keyed maps prevent an A→B switch
    // from attaching A's pending receipts to B's envelope.
    const projectMediaRetirementQueueRef = useRef(null);
    if (!projectMediaRetirementQueueRef.current) {
        projectMediaRetirementQueueRef.current = createProjectMediaRetirementQueue();
    }
    // v5.46.73 load-generation token: two rapid project opens race through
    // loadProjectUnchecked's awaited fetch, and without this the FIRST-clicked
    // project could paint LAST and win wholesale (user clicked B, sees A —
    // coherently, so no per-field guard catches it). Each open increments the
    // generation at entry; only the open still holding the LATEST generation
    // may call completeProjectLoad. completeProjectLoad itself claims a fresh
    // generation at entry so direct callers (reconnect-folder modal) also
    // invalidate any older in-flight open. Complements currentProjectIdRef,
    // which only guards AFTER a load has painted.
    const projectLoadGenerationRef = useRef(0);
    // projectLoadGenerationRef advances when an open STARTS so stale fetches
    // can be rejected. That request token must never be paired with the still-
    // painted prior project for automatic authoring. This separate context is
    // stamped only when a project actually paints (completeProjectLoad), or
    // when the current project id genuinely changes through a new-project
    // save. Same-id completed reloads are stamped by the persistence seam.
    const paintedProjectLoadContextRef = useRef({
        projectId: null,
        loadGeneration: 0,
    });
    // The Assistant's open moves to its stage only once the project painted.
    const readPaintedProjectLoad = () => paintedProjectLoadContextRef.current;
    // ...and only once QuoteMaker shows it, through the latest render's stage
    // navigation, never navigating (review 2026-09-30). Both read refs, so the
    // copies a tool call captures stay current.
    const readLivePageForAssistant = () => currentPageRef.current;
    const navigateAssistantToQuoteStage = (targetStage) => handleQuoteStageNavigateRef.current(targetStage, { onlyOnQuoteMaker: true });
    // Slow, load-owned media hydration can settle after the coarse two-second
    // guard. This exact project/load/fingerprint receipt lets that one state
    // transition become the new comparison baseline without giving a stale
    // callback permission to swallow a builder edit or another Project's work.
    const pendingProjectHydrationFingerprintRef = useRef(null);
    if (!Object.is(paintedProjectLoadContextRef.current.projectId, currentProjectId)) {
        paintedProjectLoadContextRef.current = {
            projectId: currentProjectId,
            loadGeneration: projectLoadGenerationRef.current,
        };
    }
    const paintedProjectLoadGeneration = (
        Object.is(paintedProjectLoadContextRef.current.projectId, currentProjectId)
        && Number.isFinite(paintedProjectLoadContextRef.current.loadGeneration)
    )
        ? paintedProjectLoadContextRef.current.loadGeneration
        : null;
    // Stage 4 and Stage 5 show one set of items (Peter, 2026-10-23): every
    // change to either copy is given to the other before the screen paints.
    // Stage 4 and Stage 5 show one set of items on one GST basis: a GST change
    // made by the builder or the Assistant in either stage moves the quote's
    // basis together with the converted items (review round 7: the Stage 4
    // toggle left the quote saying inc GST over ex GST amounts).
    const setGstBasisForBothStages = (next) => {
        const isGstInclusive = next !== false;
        setScopeGstInclusive(isGstInclusive);
        const quote = quoteDataRef.current;
        if (quote && typeof quote === 'object' && (quote.isGstInclusive !== false) !== isGstInclusive) {
            const nextQuote = { ...quote, isGstInclusive };
            quoteDataRef.current = nextQuote;
            setQuoteData(nextQuote);
        }
    };
    useStageRowsMirror({
        scope,
        setScope,
        scopeRef,
        quoteData,
        setQuoteData,
        quoteDataRef,
        paintedProjectLoadContextRef,
        setStage4GstInclusive: setScopeGstInclusive,
    });
    // React busy state is presentation only. The project singleflight is the
    // synchronous execution authority used by Stage 5 navigation so an old
    // quote cannot write back during Pricing/context preparation.
    const isCurrentProjectQuoteGenerationInFlight = useCallback(() => {
        const active = quoteGenerationSingleflightRef.current?.status?.();
        return isSingleflightActiveForPaintedProject({
            status: active,
            paintedContext: paintedProjectLoadContextRef.current,
            currentProjectId: currentProjectIdRef.current,
            currentLoadGeneration: projectLoadGenerationRef.current,
        });
    }, []);
    const isCurrentProjectCommercialWriteInFlight = useCallback(() => (
        hbcfDecisionSaveOwnerRef.current !== null
        || isCurrentProjectQuoteGenerationInFlight()
    ), [isCurrentProjectQuoteGenerationInFlight]);
    // B: an open that ended WITHOUT painting (its fetched result was held back because the operator kept working, the project was
    // not found, the caller declined) leaves the outgoing project on screen, but its claim moved the live load counter ahead of the
    // painted generation, and the editor's Save lease below compares the two. The persistence hook records here that this project
    // stays the owner, at the counter as it stands: the lease then runs at that generation. The counter itself is never moved (work
    // an open invalidated stays invalidated) and the painted generation the modal's load key reads is never changed (its draft and
    // Undo are not reseeded). A paint of any project, or a reload at a new generation, changes the painted context and ends this.
    const retainedProjectLeaseRef = useRef<{ projectId: unknown; paintedGeneration: number; leaseGeneration: number } | null>(null);
    const retainOutgoingProject = useCallback(() => {
        const painted = paintedProjectLoadContextRef.current;
        if (!painted || !Object.is(painted.projectId, currentProjectIdRef.current)) return;
        retainedProjectLeaseRef.current = {
            projectId: painted.projectId,
            paintedGeneration: Number(painted.loadGeneration),
            leaseGeneration: Number(projectLoadGenerationRef.current),
        };
    }, []);
    const acquireQuoteEditorSaveLease = useCallback(() => {
        const painted = paintedProjectLoadContextRef.current;
        const ownerProjectId = painted?.projectId ?? currentProjectIdRef.current ?? null;
        const paintedGeneration = Object.is(painted?.projectId, ownerProjectId)
            ? Number(painted?.loadGeneration)
            : Number(projectLoadGenerationRef.current);
        const retained = retainedProjectLeaseRef.current;
        const ownerLoadGeneration = retained
            && Object.is(retained.projectId, ownerProjectId)
            && retained.paintedGeneration === paintedGeneration
            ? retained.leaseGeneration
            : paintedGeneration;
        const runContext = captureProjectRunContext({
            projectId: ownerProjectId,
            loadGeneration: ownerLoadGeneration,
            signal: projectAbortRef.current?.signal || null,
        });
        return acquireProjectWriteLease({
            leaseRef: quoteEditorSaveInFlightRef,
            owner: {
                projectId: ownerProjectId,
                loadGeneration: ownerLoadGeneration,
                paintedGeneration,
                runContext,
            },
            isOwnerCurrent: (owner) => {
                const livePainted = paintedProjectLoadContextRef.current;
                return Object.is(livePainted?.projectId ?? null, owner.projectId ?? null)
                    && Number(livePainted?.loadGeneration) === Number(owner.paintedGeneration)
                    && isProjectRunContextCurrent(owner.runContext, {
                        projectId: currentProjectIdRef.current,
                        loadGeneration: projectLoadGenerationRef.current,
                        signal: projectAbortRef.current?.signal || null,
                    });
            },
            isConflictingOperationActive: isCurrentProjectQuoteGenerationInFlight,
            label: 'Stage 5 quote save',
        });
    }, [isCurrentProjectQuoteGenerationInFlight]);
    // The Stage 4 Quote Notes field is where an operator writes OR CLEARS the
    // client-facing Note. A clear must be recorded in the quote as well as in
    // quoteNote: persistence stores an empty quoteNote as NULL, which reads back
    // as "no Note decided", and Stage 5 then fills the Note from the quote's
    // embedded copy (use-quote-editor.js `liveNote || source.note`, the editor's
    // `quoteNote || quoteData.note`). Only this authored seam does it. The plain
    // setter also serves reset, hydration, sync and Save, which are not decisions,
    // and it writes no edit marker: those markers relax a readiness blocker
    // (quote-issue-readiness.js) and are not set here. A legacy quote whose Note
    // only the quote carries is untouched until the operator clears the field.
    //
    // The decision is recorded as meta.noteClearedByOperator (quote-note-decision.ts), because both stored lanes of an emptied Note
    // read back empty exactly like one nobody wrote, and Regenerate would fill it again from the client diagnosis. It is written only
    // when the field really held text: the Stage 4 field shows quoteNote alone, so tabbing through an empty field neither records a
    // decision nor touches a legacy Note that only the quote carries.
    const setQuoteNoteByOperator = useCallback((next) => {
        setQuoteNote(next);
        if (typeof next !== 'string' || isCurrentProjectQuoteGenerationInFlight()) return;
        const current = quoteDataRef.current;
        if (!current || typeof current !== 'object') return;
        const shownBefore = quoteNoteRef.current;
        const fieldHadText = String(shownBefore ?? '').trim() !== '';
        const meta = metaAfterNoteEdit(current.meta, next, shownBefore);
        const embeddedCleared = next === '' && fieldHadText && typeof current.note === 'string' && current.note !== '';
        if (!embeddedCleared && meta === current.meta) return;
        const updated = {
            ...current,
            ...(embeddedCleared ? { note: '' } : {}),
            ...(meta !== current.meta ? { meta } : {}),
        };
        quoteDataRef.current = updated;
        setQuoteData(updated);
    }, [isCurrentProjectQuoteGenerationInFlight]);
    const captureReviewEditIntent = useCallback((event = null) => {
        if (!openedForReviewRef.current) return;
        const paintedContext = paintedProjectLoadContextRef.current;
        const intent = createProjectReviewEditIntent({
            event,
            openedForReview: openedForReviewRef.current,
            // Resolve only after the helper has rejected non-editable events,
            // STOP, loading, and identity drift. Normal typing after review
            // therefore pays no full-project fingerprint cost.
            fingerprintBeforeEdit: () => currentProjectFingerprintRef.current(),
            isLoadingProject: isLoadingProjectRef.current,
            pipelineHalted: aiPipelineHaltedRef.current,
            currentProjectId: currentProjectIdRef.current,
            currentLoadGeneration: projectLoadGenerationRef.current,
            paintedProjectId: paintedContext.projectId,
            paintedLoadGeneration: paintedContext.loadGeneration,
        });
        if (!intent) return;
        deliberateProjectEditIntentRef.current = intent;
        // Never release review in capture phase: this surface also contains
        // non-persisted checkboxes, selects, assistant inputs, and drops. The
        // deferred probe render below consumes the token after comparing the
        // persisted project fingerprint. A rejected token cannot pair with a
        // later navigation or hydration change.
        // Chrome can flush a capture-phase update before React's bubbled
        // onChange runs, restoring the controlled input to its old value.
        // Finish this native event before probing a non-persisted edit. Real
        // project changes still consume the intent in their normal render.
        if (reviewEditProbeTimerRef.current === null) {
            reviewEditProbeTimerRef.current = setTimeout(() => {
                reviewEditProbeTimerRef.current = null;
                setReviewEditProbeRevision(revision => revision + 1);
            }, 0);
        }
    }, []);

    // ─── Optimistic-concurrency token for the open project ────────────────
    // The server `updated_at` this tab's working copy descends from: stamped
    // when a project is loaded, refreshed from the server's own response after
    // every successful save, and cleared when there is no open project.
    //
    // WHY THIS EXISTS. The full-envelope save replaces scope / form_data /
    // quote_data by id with whatever this tab holds, and roughly forty call
    // sites can fire it — most of them NOT gated on the dirty bit (the
    // nav-away autosave at use-app-routing.ts:383 says so in its own comment).
    // So a tab holding an out-of-date copy could silently revert work saved
    // from another device just by being navigated away from. That is the
    // 2026-08-14 cross-device loss in TWO-TENANT-LAUNCH-PLAN.md 0f. Sending
    // this token lets the backend refuse the write (409) instead of taking it,
    // which covers every one of those call sites at once rather than
    // re-auditing each for dirty-bit truthfulness.
    //
    // null means "do not enforce" — a new project, or a record whose version
    // we never learned. Never guess a value here: a wrong token turns every
    // save into a false conflict.
    const baseUpdatedAtRef = useRef(null);
    // A proactive remote-version receipt pauses automatic full-envelope saves
    // before they reach the expectedUpdatedAt refusal. It never advances the
    // baseline token and never reloads Project state. Only an authoritative
    // reload or the explicit post-refusal overwrite path can release it.
    const remoteUpdateBlockedRef = useRef(false);
    const blockedProjectConflictRef = useRef(null);
    const lastRemoteVersionReceiptRef = useRef(null);
    const dismissedRemoteVersionRef = useRef(null);
    const remoteVersionCheckRef = useRef(null);
    const remoteUpdateNoticeRequestRef = useRef(null);
    const assertRemoteProjectSaveReady = useCallback(() => {
        if (!remoteUpdateBlockedRef.current) return;
        remoteUpdateNoticeRequestRef.current?.();
        const error = new Error('A newer saved version needs review before this project can be saved.');
        error.code = 'PROJECT_REMOTE_UPDATE_REVIEW_REQUIRED';
        error.retryable = true;
        throw error;
    }, []);

    // The content of the server row this tab's token describes, as a comparable
    // signature. Moves in lockstep with baseUpdatedAtRef — set at load and after
    // every successful save, from the SAME record.
    //
    // It exists so a refused save can tell the two cases apart. "Somebody
    // changed this project" is an operator decision and must stay one. "Only
    // bookkeeping moved" is not a conflict at all, and on 2026-08-18 it was
    // stopping Customer Zero generating any quote: the AI cost rollup advanced
    // updated_at on every costed call, so the pipeline's own save was refused as
    // a foreign edit and the photo-forensic gate turned that into a hard stop.
    // Migration 102 stops the rollup doing it; this is the net under any writer
    // we have not found yet.
    const staleWriteBaselineRef = useRef(null);

    // The narrow lifecycle routes (complete-quote, pdf-generated,
    // print-opened, email-sent, quote-status) each bump the row's updated_at
    // server-side. Those are OUR OWN writes, so without adopting the version
    // they return, this tab's token would be spent the moment the operator
    // downloaded a PDF and the next ordinary save would report a conflict that
    // never happened. Call this with whatever row such a route hands back.
    //
    // Guarded on project identity: a lifecycle write against some OTHER
    // project must never restamp the token for the one that is open.
    //
    // Adopted only when the write landed on the version this tab holds (the
    // response's previousUpdatedAt, or `writtenOver` for a write gated on this
    // tab's token). A version that already includes another device's save
    // would let this tab's next full save overwrite that save unreported.
    // A caller that names the load generation its write started on is also
    // refused once this job was reloaded during the write.
    // The rule lives in utils/server-version-adoption.ts.
    const adoptServerProjectVersion = useCallback((projectId, persisted, writtenOver = undefined, { loadGeneration = undefined } = {}) => {
        if (!projectId || projectId !== currentProjectIdRef.current) return;
        // A narrow lifecycle response can carry a newer token without carrying
        // the newer Project body. Never let that token re-arm a stale full-row
        // editor to overwrite work the remote-version monitor already saw.
        if (remoteUpdateBlockedRef.current) return;
        const next = serverVersionToAdopt({
            projectId,
            openProjectId: currentProjectIdRef.current,
            remoteUpdateBlocked: remoteUpdateBlockedRef.current,
            heldToken: baseUpdatedAtRef.current,
            persisted,
            writtenOver,
            loadGeneration,
            openLoadGeneration: projectLoadGenerationRef.current,
        });
        if (next) baseUpdatedAtRef.current = next;
    }, []);

    // 2026-05-08 (Peter): "STOP button should only be visible when an AI pipeline
    // is firing. It's meant to cancel that." The previous gate combined 5
    // component-state booleans (isAnalyzing / isSummarizing / isGenerating /
    // photoAssignBusy / projectWMSGenerating) which can drift true after the
    // visible "AI is running" pill has gone away (e.g., projectWMSGenerating
    // sticking after a background regen). Single source of truth: PipelineMonitor.
    // Same data the LivePipelineBanner uses, same lifecycle. STOP button now
    // tracks the pill exactly.
    const [hasLivePipelineEntry, setHasLivePipelineEntry] = useState(false);
    useEffect(() => {
        const recompute = () => {
            try {
                const entries = PipelineMonitor.getEntries() || [];
                const pid = currentProjectIdRef.current;
                if (!pid) {
                    setHasLivePipelineEntry(false);
                    return;
                }
                const live = entries.some(e => isActionableRunningPipelineEntry(e, pid));
                setHasLivePipelineEntry(live);
            } catch {
                setHasLivePipelineEntry(false);
            }
        };
        recompute();
        return PipelineMonitor.subscribe(recompute);
    }, [currentProjectId]);

    const [projects, setProjects] = useState<any[]>([]);
    const [projectsLoaded, setProjectsLoaded] = useState(false);
    const [projectsLoadError, setProjectsLoadError] = useState('');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [isSavingProject, setIsSavingProject] = useState(false);

    const handleRestoreProjectFromFolder = useCallback((directoryHandle, folderMetadata = {}) => (
        restoreProjectFromDirectoryHandle(directoryHandle, folderMetadata, { setProjects, setToast })
    ), []);

    // On-demand server re-pull of the Run Sheet list (operator request
    // 2026-08-14: a second device's new quote only appeared after a hard
    // refresh). The summaries load is a plain API read done once at boot;
    // this re-runs it so another device's work shows up without reloading.
    // Empty dep array is safe here: the impl reads NO component state —
    // only the imported fetcher and the (stable) setters.
    const refreshProjectsFromServer = useCallback(async () => {
        try {
            const refreshed = await getAllProjectSummaries();
            if (!Array.isArray(refreshed)) {
                throw new Error('Project summaries returned an invalid response.');
            }
            setProjects(refreshed);
            setProjectsLoaded(true);
            setProjectsLoadError('');
            return refreshed.length;
        } catch (err) {
            // Keep the existing array as the last known good view. A failed
            // refresh must never replace it with an apparently empty tenant.
            setProjectsLoaded(true);
            setProjectsLoadError('QuoteMaker could not load the latest projects.');
            throw err;
        }
    }, []);

    // ── Client & Site identity (Stage 1 + Stage 2 CRM link) ────────────────
    const {
        clientIdentity,
        stage3MethodAssessment,
        effectiveCompletedStages,
        notRunStages,
    } = useStageCompletionAssessment({
        stage,
        currentProjectId,
        projects,
        formData,
        photos,
        documents,
        videos,
        projectSummary,
        analysis,
        photoAnalysis,
        projectWMS,
        projectStrategy,
        scope,
        quoteData,
        completedStages,
        workflowMode: projectWorkflowMode,
    });

    // Stable save wrapper for callers declared before the implementation body.
    // The implementation is assigned near saveCurrentProjectImpl below.
    const saveCurrentProjectRef = useRef<((overrides?: Record<string, unknown>, options?: ProjectSaveOptions) => Promise<any>) | null>(null);
    const saveCurrentProject = useCallback(async (overrides = {}, options = {}) => {
        const commercialOwner = hbcfDecisionSaveOwnerRef.current;
        if (commercialOwner !== null && options?.commercialDecisionOwner !== commercialOwner) {
            const error = new Error('A builder HBCF decision is being saved. Wait for it to finish before saving or navigating.');
            error.code = 'PROJECT_COMMERCIAL_DECISION_IN_FLIGHT';
            error.retryable = true;
            throw error;
        }
        assertRemoteProjectSaveReady();
        const impl = saveCurrentProjectRef.current;
        if (typeof impl !== 'function') return null;
        const savedProject = await impl(overrides, options);
        if (
            savedProject
            && Number(overrides?.currentStage) === 4
            && Array.isArray(overrides?.scope)
            && overrides?.quoteData
            && typeof overrides.quoteData === 'object'
        ) {
            const canonicalScope = Array.isArray(savedProject?.scope)
                ? savedProject.scope
                : overrides.scope;
            const canonicalQuote = savedProject?.quoteData
                || savedProject?.quote_data
                || overrides.quoteData;
            stage45PersistenceReceiptRef.current = {
                projectId: options?.runContext?.projectId ?? currentProjectIdRef.current ?? null,
                loadGeneration: options?.runContext?.loadGeneration
                    ?? projectLoadGenerationRef.current
                    ?? null,
                scopeFingerprint: fingerprintValue(canonicalScope),
                quoteFingerprint: fingerprintValue(canonicalQuote),
            };
        }
        return savedProject;
    }, [assertRemoteProjectSaveReady]);

    // Stage 1 media rows are keyed by Project ownership. A fresh intake must
    // therefore receive and persist its real Project UUID before the first
    // photo/document/video Blob enters IndexedDB or S3 registration. One
    // single-flight promise covers picker, drag/drop and folder imports that
    // arrive together; every caller receives the same canonical owner.
    const stage1ProjectOwnerPromiseRef = useRef(null);
    const ensureStage1ProjectOwnerForImport = useCallback(async () => {
        if (isCanonicalProjectUuid(currentProjectIdRef.current)) return currentProjectIdRef.current;
        if (stage1ProjectOwnerPromiseRef.current) return stage1ProjectOwnerPromiseRef.current;

        const ownerContext = captureProjectRunContext({
            projectId: currentProjectIdRef.current || null,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectPersistenceAbortRef.current?.signal || null,
        });
        let ownerPromise;
        ownerPromise = (async () => {
            const saved = await saveCurrentProject(
                { skipDuplicateProjectGuard: true, silent: true },
                { ensureProjectOwner: true, runContext: ownerContext },
            );
            const savedId = saved?.id || currentProjectIdRef.current || null;
            const ownerChanged = ownerContext.signal?.aborted
                || projectLoadGenerationRef.current !== ownerContext.loadGeneration
                || (currentProjectIdRef.current && currentProjectIdRef.current !== savedId);
            if (!savedId || ownerChanged) {
                const error = new Error('Stage 1 import lost its Project owner before media storage began.');
                error.code = 'PROJECT_RUN_STALE';
                throw error;
            }
            currentProjectIdRef.current = savedId;
            return savedId;
        })();
        stage1ProjectOwnerPromiseRef.current = ownerPromise;
        try {
            return await ownerPromise;
        } finally {
            if (stage1ProjectOwnerPromiseRef.current === ownerPromise) {
                stage1ProjectOwnerPromiseRef.current = null;
            }
        }
    }, [saveCurrentProject]);

    // ─── Stage 5 entry seam — DEC-COUNCIL-013 (renumbered from 010) ───────
    // useQuoteEditor owns all FullQuoteEditorModal open/close/sync state.
    // Keep this hook below its state/ref dependencies; an earlier integration
    // placed it above `scope`, `quoteData`, `stageRef`, and `saveCurrentProject`,
    // which booted prod into a TDZ ReferenceError.
    const editor = useQuoteEditor({
        setScope,
        setQuoteData,
        setSelectedHeroImage,
        setQuoteNote,
        setQuoteTitle,
        setTitleEditedByUser,
        setCompletedStages,
        setStageWithTransition,
        saveCurrentProject,
        stageRef,
        currentPageRef,
        currentProjectIdRef,
        projectLoadGenerationRef,
        paintedProjectLoadContextRef,
        projectAbortRef,
        scopeRef,
        quoteDataRef,
        selectedHeroImageRef,
        quoteNoteRef,
        quoteTitleRef,
        titleEditedByUserRef,
        isQuoteGenerationInFlight: isCurrentProjectCommercialWriteInFlight,
        effectiveCompletedStagesGetter: () =>
            effectiveCompletedStages instanceof Set ? effectiveCompletedStages : completedStages,
    });
    // Backward-compat aliases so existing read-sites compile unchanged.
    const quoteEditorOpen = editor.isOpen;
    const quoteEditorOpenRef = editor.openRef;
    const quoteEditorViewMode = editor.viewMode;
    const quoteEditorIsEditing = editor.isEditing;
    const quoteEditorLiveSnapshot = editor.liveSnapshot;
    const quoteEditorActionsRef = editor.actionsRef;
    // ─── Substep B (C17 / C18 / O-S2): the Stage 5 draft the operator can see is part of the project's unsaved work ───
    // The editor's actions, only while the editor is OPEN. close() (Back) leaves the last render's actions in the ref, and they
    // belong to an editor that is gone: a Save through them would send its older quote over Stage 4's newer edits.
    const liveQuoteEditorActions = useCallback(
        () => (quoteEditorOpenRef.current === true ? quoteEditorActionsRef.current : null),
        [quoteEditorOpenRef, quoteEditorActionsRef],
    );
    // Synchronous and non-consuming (the editor answers from refs): safe in a before-unload listener and a project-switch guard.
    const hasUnsavedVisibleWork = useCallback(
        () => liveQuoteEditorActions()?.hasUnsavedVisibleWork?.() === true,
        [liveQuoteEditorActions],
    );
    // Save the outgoing project's work before another opens. With the Stage 5 editor up that is the editor's own Save: it takes
    // in what is typed, sends the editor's quote, holds the project write lease, and says whether it saved. A refusal or a false
    // is { ok: false } and the caller must not go on; an error keeps what it carries (outage, conflict). With no editor it is the
    // ordinary save. Never rejects.
    const saveOutgoingVisibleWork = useCallback(async () => {
        const editorActions = liveQuoteEditorActions();
        if (typeof editorActions?.saveWithOutcome === 'function') return editorActions.saveWithOutcome();
        try {
            const saved = await saveCurrentProject();
            return saved === false ? { ok: false, error: null } : { ok: true, error: null };
        } catch (error) {
            return { ok: false, error };
        }
    }, [liveQuoteEditorActions, saveCurrentProject]);
    const discardOutgoingVisibleDrafts = useCallback(() => {
        liveQuoteEditorActions()?.discardVisibleDrafts?.();
    }, [liveQuoteEditorActions]);
    // A token for "has the operator kept working in the editor since I looked": the persistence hook records it when it decides
    // to open another project and asks again just before that project paints.
    const visibleWorkStamp = useCallback(
        () => liveQuoteEditorActions()?.visibleWorkStamp?.() ?? null,
        [liveQuoteEditorActions],
    );
    const [quoteEditorSaveState, setQuoteEditorSaveState] = useState(null);
    // Stage 5's scope once the operator edits in it (FullQuoteEditorModal
    // onObjectiveScopeChange), else null.
    const [quoteEditorObjectiveScope, setQuoteEditorObjectiveScope] = useState<any[] | null>(null);
    const setQuoteEditorIsEditing = editor.setIsEditing;
    const setQuoteEditorLiveSnapshot = editor.setLiveSnapshot;
    // The quote's ONE objective (Peter, 2026-09-29: "Stage 4 and Stage 5
    // Scope Objectives should ALWAYS match"). Computed here once per render,
    // from the stored values and the quote Stage 5 presents, and handed to
    // both stages. A generation, an edit or an item change that updates any
    // of these changes both stages in the same render. While Stage 5 holds an
    // edit, the objective is derived from Stage 5's scope: an item edit there
    // changes only the editor's scope, and Back and Save take that scope into
    // Stage 4.
    const quoteObjective = useQuoteObjective({
        formData,
        quoteData: editor.presentationQuoteData || quoteData,
        scope,
        stage5Scope: quoteEditorOpen ? quoteEditorObjectiveScope : null,
        // Before any quote, the Stage 1.5 summary's objective, which the
        // Stage 5 scope draft (use-stage-navigation) reads too.
        projectSummary,
    });
    // App's own quote says that objective too, so the saved quote, the copy
    // kept when it is sent, the revision diff, the Run Sheet and the Assistant
    // read what Stage 4, Stage 5 and the PDF show. Opening a project writes
    // nothing: the load key moves and the saved values are shown as they are.
    useQuoteObjectiveStorage({
        formData,
        setFormData,
        quoteData,
        setQuoteData,
        loadKey: `${currentProjectId ?? ''}::${paintedProjectLoadGeneration ?? ''}`,
    });

    const canNavigateToQuoteStage = useCallback((targetStage) => canNavigateQuoteStage({
        currentStage: stage,
        targetStage,
        isAnalyzing,
        riskAnalysis: analysis,
        photoAnalysis,
        completedStages: effectiveCompletedStages,
        wms: projectWMS,
        projectWMS,
        projectStrategy,
        scope,
        scopeGstInclusive,
        quoteData,
        workflowMode: projectWorkflowMode,
        // 5B: readiness reads the re-quote provenance that lives in formData (a re-quote's scope reaches Scope Builder without a recorded Method).
        formData,
    }), [stage, isAnalyzing, analysis, photoAnalysis, effectiveCompletedStages, projectWMS, projectStrategy, scope, scopeGstInclusive, quoteData, projectWorkflowMode, formData]);

    // TS-060: Assistant stage navigation state extracted to src/utils/assistant-navigation-state.ts
    const assistantStageNavigationState = useMemo(() => computeAssistantStageNavigationState({
        effectiveCompletedStages,
        currentProjectId,
        projects,
        formData,
        documents,
        photos,
        videos,
        projectSummary,
        analysis,
        photoAnalysis,
        projectWMS,
        scope,
        quoteData,
        isAnalyzing,
        canNavigateToQuoteStage,
        stage,
        currentPage,
        quoteTitle,
    }), [
        effectiveCompletedStages,
        currentProjectId,
        projects,
        formData,
        documents,
        photos,
        videos,
        projectSummary,
        analysis,
        photoAnalysis,
        projectWMS,
        scope,
        quoteData,
        isAnalyzing,
        canNavigateToQuoteStage,
        stage,
        currentPage,
        quoteTitle,
    ]);
    useEffect(() => {
        canNavigateToQuoteStageRef.current = canNavigateToQuoteStage;
    }, [canNavigateToQuoteStage]);

    // ─── Routing-logic seam — TS-060 T4 slice 6 (DEC-COUNCIL-015) ─────────
    // handleNavigate + the popstate handler BODY moved VERBATIM into
    // src/hooks/use-app-routing.ts. The hook owns NO state and NO effects —
    // ALL FOUR routing effects stay above at their original positions (the
    // initial URL sync, the pushState watcher, the replaceState seed, and
    // the popstate registration effect, now registration-only). State,
    // setters, refs, PAGE_ORDER, QM_STATE_KEY and readQmStateFromStorage
    // thread from App via this per-render deps object. Instantiated HERE —
    // directly below the useQuoteEditor alias block + canNavigateToQuoteStage
    // region — because editor/quoteEditorOpen/saveCurrentProject are the
    // last early deps the moved bodies read (TDZ). Four callees are declared
    // further down and thread as LAZY WRAPPERS keeping their original names
    // (building the arrows here does not evaluate the bindings; all four
    // fire inside handler bodies after render completes): loadProject
    // (intake bridge async IIFE), resetForm (no-active-work fast-path),
    // syncQuoteEditorBackToState (leave-quotemaker auto-save) and
    // openStage5FromCurrentScope (popstate Stage-5 target funnel).
    const { handleNavigate, handlePopState } = useAppRouting({
        currentPage,
        stage,
        isPageTransitioning,
        receivablesDisabled,
        globalFileExplorer,
        ribbonNotification,
        quoteEditorOpen,
        formData,
        photos,
        documents,
        videos,
        scope,
        quoteData,
        quoteTitle,
        titleEditedByUser,
        getLiveQuoteTitle: () => quoteTitleRef.current,
        getLiveTitleEditedByUser: () => titleEditedByUserRef.current,
        analysis,
        currentProjectId,
        PAGE_ORDER,
        QM_STATE_KEY,
        setIntakePendingSelectJobId,
        setGlobalFileExplorer,
        setRibbonNotification,
        setRibbonDismissing,
        setCurrentProjectId,
        setStage,
        setFormData,
        setToast,
        setImportPhase,
        setCurrentPage,
        setIsPageTransitioning,
        setStageDirection,
        requestCockpitTab,
        setPageNavOptions,
        setPageDirection,
        setPrevPage,
        setIsStageTransitioning,
        setPrevStage,
        isLoadingProjectRef,
        // Live mirror of currentPage (declared at App.jsx:930). handleNavigate's
        // same-page no-op guard reads this instead of the closure value so a
        // memoised caller cannot pin a stale page and swallow a real navigation
        // (the v5.47.37 Run Sheet open-swallow fix).
        currentPageRef,
        isPopstateNav,
        canNavigateToQuoteStageRef,
        // An older history entry or saved view that names the Summary asks for the Summary tab; whether a Summary
        // exists is read live, because the listener outlives renders.
        canShowSummaryView: () => !!projectSummaryRef.current || isSummarizingRef.current,
        readQmStateFromStorage,
        // "Open in QuoteMaker" seeds the intake job only into the project it
        // opened: completeProjectLoad replaces this when a project paints.
        readPaintedProjectLoad: () => paintedProjectLoadContextRef.current,
        pendingProjectOpenRef,
        revokeAttendedAutomaticRiskIntent,
        // Advanced when a navigation is ACCEPTED, so a pending re-quote
        // extraction yields to it even before the deferred page flip.
        navigationIntentEpochRef,
        operatorNavigationEpochRef,
        saveCurrentProject,
        // The route still captures a fast edit synchronously, but it only
        // writes when that exact envelope differs from the last canonical
        // Project receipt. Merely opening Run Sheet must not save the quote.
        shouldSaveProjectBeforeNavigation: (overrides) => {
            const activeProjectId = currentProjectIdRef.current || currentProjectId || null;
            if (!activeProjectId || savedFingerprintRef.current === null) return true;
            return captureProjectFingerprint(overrides || {}) !== savedFingerprintRef.current;
        },
        isQuoteGenerationInFlight: isCurrentProjectCommercialWriteInFlight,
        requestPageExit: requestAppPageExit,
        editor,
        // The reopen of the tab's saved project passes a paint check
        // (2026-09-29), so the options go through.
        loadProject: (projectSummary, options) => loadProject(projectSummary, options),
        resetForm: () => resetForm(),
        syncQuoteEditorBackToState: () => editor.syncForPageNavigation(),
        openStage5FromCurrentScope: (opts) => openStage5FromCurrentScope(opts),
        // Decision D1 (2026-09-28): the intake "Open in QuoteMaker" bridge
        // asks before it navigates, so a refused open leaves the operator on
        // the page they clicked from. Lazy wrapper, same TDZ note as above.
        refuseProjectChangeDuringQuoteRun: (change, projectId) => refuseProjectChangeDuringQuoteRun(change, projectId),
        // A3 review: opening the job the Quote Run is driving shows it (no
        // load, no seeding). Lazy wrapper, same TDZ note as above.
        showQuoteRunJobInsteadOfOpening: (projectId) => showQuoteRunJobInsteadOfOpening(projectId),
        // A3 review round 3: the running job is the project in progress even
        // before its load paints, so the QuoteMaker fast path shows it rather
        // than resetting or reopening. Lazy wrapper, same TDZ note as above.
        quoteRunJobInProgress: () => quoteRunJobInProgress(),
        // Outage lane review: a blank new project paints back a new project
        // kept on this device. Lazy wrapper, same TDZ note as above.
        restoreKeptNewProject: () => restoreKeptNewProject(),
    });

    // The Quote Run is a page of its own (use-quote-run-page.ts): the global
    // File Explorer is open exactly while /quote-run is the page.
    const openQuoteRunPage = useQuoteRunPage({
        currentPage,
        explorerOpen: globalFileExplorer.open,
        readPage: () => currentPageRef.current,
        readExplorerOpen: () => !!globalFileExplorerRef.current?.open,
        openExplorer: openGlobalFileExplorer,
        closeExplorer: closeGlobalFileExplorer,
        navigate: handleNavigate,
    });

    // Mirror projects into a ref so the listener useEffect below can look up
    // a project by id without needing `projects` in its dependency array.
    // Putting `projects` in the deps would mean re-registering seven window
    // listeners on every project save - wasteful, and (when this useEffect
    // lived earlier in the component) a TDZ crash because the deps array
    // evaluated before `projects` was declared.
    const projectsRef = useRef<any[]>([]);
    useEffect(() => { projectsRef.current = projects; }, [projects]);

    // TS-060: App event listeners seam (DEC-COUNCIL-015)
    useAppEventListeners({
        setToast,
        currentProjectIdRef,
        projectsRef,
    });

    // ─── Persist QuoteMaker view state to sessionStorage ──────────────────
    // Mirrors { stage, quoteEditorOpen, currentProjectId }
    // to sessionStorage every time any of them changes - but ONLY when the user
    // is currently on the QuoteMaker page (otherwise we'd overwrite valid state
    // with whatever is in App during navigation away).
    //
    // On the next nav back to QuoteMaker, handleNavigate reads this snapshot
    // and restores the view so the user lands exactly where they left off.
    //
    // Placed AFTER the currentProjectId declaration to
    // avoid the temporal dead zone - earlier placements crashed at render.
    useEffect(() => {
        persistQuoteMakerViewReceipt({
            key: QM_STATE_KEY,
            currentPage,
            view: {
                stage,
                // Always false: Stage 1 has one view. Still written because a
                // pre-merge bundle that loads into THIS tab later (a rollback,
                // which B1 makes the plan, or a reload served from the old
                // service-worker cache) refuses a record without this boolean
                // and reopens an empty project (SPEC 2026-10-21 R3). The record
                // is sessionStorage, so no other tab ever reads it. This
                // bundle's reader no longer needs it; stop writing it only once
                // no rollback past the merge is possible.
                summaryViewActive: false,
                quoteEditorOpen,
                importPhase,  // persist importPhase so mid-processing nav resumes correctly
                currentProjectId: currentProjectId || null,
                savedAt: Date.now(),
            },
            readStored: readQmStateFromStorage,
        });
    }, [currentPage, stage, quoteEditorOpen, importPhase, currentProjectId, QM_STATE_KEY]);

    // Load history - lightweight summaries only (no photos/videos/documents binary data)
    // Full project data is loaded on-demand when user opens a project
    useEffect(() => {
        if (user && !localTenantSessionVerified) {
            setProjectsLoaded(false);
            return;
        }
        if (!user || !sessionStorage.getItem('qm_access_token')) {
            setProjectsLoadError('');
            setProjectsLoaded(true);
            return;
        }
        const projectAccess = effectiveAccess(user);
        const mayReadProjects = [
            'quotemaker', 'quick-edit', 'project-imports',
            'analysis', 'clients', 'receivables',
        ].some(areaId => projectAccess[areaId] === true);
        if (!mayReadProjects) {
            setProjects([]);
            setProjectsLoadError('');
            setProjectsLoaded(true);
            return;
        }
        getAllProjectSummaries().then(async p => {
            if (!Array.isArray(p)) {
                throw new Error('Project summaries returned an invalid response.');
            }
            // TS-060: Legacy quote status migration extracted to src/services/legacy-quote-status-migration.ts
            try {
                const migratedProjects = await migrateLegacyQuoteStatuses(p);
                setProjects(migratedProjects);
            } catch {
                setProjects(p);
            }
            setProjectsLoadError('');
            setProjectsLoaded(true);
        }).catch(e => {
            console.error(e);
            // Do not clear `projects`: a refresh outage should leave the last
            // successfully loaded list visible and explicitly marked stale.
            setProjectsLoadError('QuoteMaker could not load the latest projects.');
            setProjectsLoaded(true);
        });
        // Clean up any video File objects leaked into storage from older saves
        cleanupProjectStorage().catch(console.error);
        // S3 (2026-08-19): resume any uploads that survived the last session.
        // The persistent upload queue only ever started its drain loop on the
        // first enqueue of a session, so rows that outlived a reload sat
        // pending forever. resumeUploadQueue starts the loop ONLY when such
        // rows exist (an empty queue spends no health probe).
        import('./services/upload-queue')
            .then(async (uq) => {
                // Confirmed rows are durable receipts, not a second media
                // library. Trim one bounded batch of old safe receipts on
                // every authenticated boot, even when there is no pending
                // work to wake the drain loop for. Cleanup failure must never
                // prevent surviving uploads from resuming.
                try {
                    await uq.cleanupStaleConfirmedUploads?.();
                } catch (err) {
                    console.warn('[UploadQueue] Confirmed receipt maintenance failed (non-fatal):', err?.message);
                }
                await uq.resumeUploadQueue?.();
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, user?.tenantId, user?.role, user?.access, localTenantSessionVerified]);

    // CRM backfills no longer run on browser boot. A fresh device must not
    // derive new client/site records or replace an operator's chosen links.
    // Unmatched work is reviewed in Clients and the quote's client matcher.

    // Cross-tab project refresh (A3, audit batch v5.32.x).
    //
    // Without this, App.jsx loads `projects` once on mount via
    // getAllProjectSummaries() and never re-fetches. Tab A's saves / deletes
    // / status changes / quote sends leave tab B's `projects` array stale
    // until tab B is reloaded — the Dashboard, AllQuotes, and BottomNavBar
    // silently drift apart across tabs.
    //
    // Fix: subscribe to the cross-tab BroadcastChannel; coalesce mutation
    // bursts, then re-fetch summaries and replace the local state. The channel
    // filters self-originated messages so we don't double-refresh after our own
    // setProjects calls. The refresh coordinator is essential for bulk delete:
    // 485 durable deletes used to become 485 overlapping multi-MB summary
    // requests in every other open tab, which amplified one busy backend into
    // the observed GET /projects?summary=true 500 storm.
    useEffect(() => {
        if (!user || !localTenantSessionVerified) return undefined;
        // v5.40.16 (Wave 16 R3): capture the auth-token at subscribe time +
        // re-check it inside the .then() so a logout-mid-fetch can't apply
        // the previous-user's projects to the new-user's state. Pre-fix the
        // sequence Tab A logout → Tab B broadcast → Tab A re-fetch (which is
        // still authenticated because the token wasn't cleared yet) →
        // logout completes mid-await → .then() runs setProjects with the
        // old user's data was theoretically possible (sub-second window).
        // Also bail if the component unmounts during the await.
        let cancelled = false;
        const refreshCoordinator = createProjectSummaryRefreshCoordinator({
            refresh: async () => {
                const subscriptionAuthToken = sessionStorage.getItem('qm_access_token');
                // Don't refresh between login forms. Returning a receipt keeps
                // this a successful no-op rather than opening the failure backoff.
                if (!subscriptionAuthToken) return { subscriptionAuthToken: null, refreshed: null };
                const refreshed = await getAllProjectSummaries();
                return { subscriptionAuthToken, refreshed };
            },
            onSuccess: ({ subscriptionAuthToken, refreshed }) => {
                if (cancelled || !subscriptionAuthToken) return;
                const currentAuthToken = sessionStorage.getItem('qm_access_token');
                if (!currentAuthToken || currentAuthToken !== subscriptionAuthToken) {
                    // Auth changed during the await — don't apply the
                    // previous-session's projects to a new (or absent) session.
                    return;
                }
                if (Array.isArray(refreshed)) setProjects(refreshed);
            },
            onError: (err) => {
                console.warn('[QM] Cross-tab project refresh failed:', err?.message || err);
            },
            debounceMs: 350,
            maxWaitMs: 5000,
            baseBackoffMs: 2000,
            maxBackoffMs: 60000,
        });
        const unsubscribe = subscribeProjectsChanged((message) => {
            refreshCoordinator.request(message);
            // v5.40.0 (UX audit Wave-1 P0 #1): two-tab silent clobber.
            // If the broadcast includes the project currently open in THIS
            // tab, surface a persistent banner so the user can reload (pull
            // the other tab's writes) instead of silently overwriting them
            // on the next save. Pre-v5.40.0 we only refreshed the LIST of
            // projects — the open project's React state stayed stale and
            // got clobbered last-write-wins on save.
            try {
                const myId = currentProjectIdRef?.current || null;
                const ids = Array.isArray(message?.projectIds) ? message.projectIds : [];
                if (myId && ids.includes(myId) && (message?.kind === 'save' || message?.kind === 'status')) {
                    // The channel is an early wake-up signal, not version
                    // authority. Ask the tenant-scoped Project version route
                    // to confirm that the server row is newer before changing
                    // the current-project UI.
                    remoteVersionCheckRef.current?.();
                }
            } catch (e) {
                console.warn('[QM] cross-tab conflict detector threw:', e?.message || e);
            }
        });
        return () => {
            cancelled = true;
            refreshCoordinator.stop();
            try { unsubscribe(); } catch { /* non-fatal */ }
        };
    }, [user, localTenantSessionVerified]);

    // Current-project remote-version state. Same-browser BroadcastChannel
    // messages wake the check quickly; a low-frequency visible-page poll also
    // sees saves from another browser or device. The cloud Project row is the
    // authority. Detection itself never reloads or changes editor state.
    const [crossTabConflict, setCrossTabConflict] = useState(null);
    const setLatchedProjectConflict = useCallback((conflict) => {
        if (conflict?.kind === 'server-stale') {
            remoteUpdateBlockedRef.current = true;
            blockedProjectConflictRef.current = conflict;
            dismissedRemoteVersionRef.current = null;
        }
        setCrossTabConflict(conflict);
    }, []);
    remoteUpdateNoticeRequestRef.current = () => {
        const conflict = blockedProjectConflictRef.current;
        if (!conflict || conflict.projectId !== currentProjectIdRef.current) return;
        setCrossTabConflict(conflict);
    };

    useEffect(() => {
        remoteVersionCheckRef.current = null;
        remoteUpdateBlockedRef.current = false;
        blockedProjectConflictRef.current = null;
        lastRemoteVersionReceiptRef.current = null;
        dismissedRemoteVersionRef.current = null;
        const tenantId = user?.tenantId || null;
        const projectId = currentProjectId || null;
        setCrossTabConflict(previous => (
            previous?.kind === 'server-stale' && previous.projectId === projectId
                ? previous
                : null
        ));
        const isCloudProjectId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
            .test(String(projectId || ''));
        if (!tenantId || !projectId || !isCloudProjectId || !localTenantSessionVerified) {
            return undefined;
        }

        const monitor = createProjectRemoteVersionMonitor({
            tenantId,
            projectId,
            getBaselineUpdatedAt: () => baseUpdatedAtRef.current,
            fetchVersion: (id, options) => DataService.projects.getVersion(id, options),
            onRemoteVersion: (receipt) => {
                if (currentProjectIdRef.current !== receipt.projectId) return;
                // Once a save has been refused, preserve that stronger recovery
                // context. A routine monitor receipt must not replace it or
                // reopen a notice the operator just dismissed.
                if (blockedProjectConflictRef.current?.kind === 'server-stale') return;
                remoteUpdateBlockedRef.current = true;
                lastRemoteVersionReceiptRef.current = receipt;
                const conflict = {
                    projectId: receipt.projectId,
                    kind: 'remote-update',
                    at: Date.now(),
                    actualUpdatedAt: receipt.updatedAt,
                };
                blockedProjectConflictRef.current = conflict;
                if (dismissedRemoteVersionRef.current === receipt.updatedAt) return;
                setCrossTabConflict(conflict);
            },
            onError: (err) => {
                // Freshness is advisory until a write. The write guard remains
                // fail-closed, so a transient monitor failure needs no alarm.
                devLog('[QM] Current Project version check failed:', err?.message || err);
            },
            // One of this tab's own saves in flight is not somebody else's edit.
            isLocalWriteInFlight: () => tabProjectWriteLock.isBusy(),
            intervalMs: 45_000,
            baseBackoffMs: 30_000,
            maxBackoffMs: 5 * 60_000,
        });
        remoteVersionCheckRef.current = monitor.requestCheck;
        return () => {
            if (remoteVersionCheckRef.current === monitor.requestCheck) {
                remoteVersionCheckRef.current = null;
            }
            monitor.stop();
        };
    }, [user?.tenantId, currentProjectId, localTenantSessionVerified]);

    const handleCrossTabReload = useCallback(async () => {
        const id = crossTabConflict?.projectId;
        if (!id) { setCrossTabConflict(null); return; }
        try {
            const fresh = await getProject(id);
            if (fresh) {
                // Reload is the operator choosing the saved version over this
                // tab's copy, so any copy of it kept on this device while the
                // server was not answering goes too. Otherwise the load below
                // would paint it straight back (outage lane, 2026-09-29).
                await clearUnsavedProjectEdits(id);
                // loadProjectUnchecked rehydrates EVERY piece of state from
                // the project record. Equivalent to picking the project from
                // Project History — guarantees the in-memory shape matches
                // what's on disk after the other tab's save.
                await loadProjectUnchecked(fresh);
                remoteUpdateBlockedRef.current = false;
                blockedProjectConflictRef.current = null;
                lastRemoteVersionReceiptRef.current = null;
                dismissedRemoteVersionRef.current = null;
                setCrossTabConflict(null);
            }
        } catch (err) {
            // A Quote Run refused the reload and has shown its own message
            // (decision D1). The conflict banner stays for after the run.
            if (isQuoteRunActiveError(err)) return;
            console.warn('[QM] cross-tab reload failed:', err?.message || err);
            setToast({ type: 'error', message: `Reload failed: ${err?.message || 'unknown error'}. Try again or switch projects.` });
        }
    }, [crossTabConflict]);

    const handleDismissRemoteUpdate = useCallback(() => {
        const receipt = lastRemoteVersionReceiptRef.current;
        dismissedRemoteVersionRef.current = crossTabConflict?.actualUpdatedAt
            || receipt?.updatedAt
            || null;
        setCrossTabConflict(null);
    }, [crossTabConflict]);

    // The other side of a server-detected stale-write refusal: the operator
    // has read the warning and wants THIS tab's copy to win anyway.
    //
    // Adopting the server's reported `actualUpdatedAt` as our token is what
    // converts a blind clobber into a deliberate one. The next save then
    // carries a token the backend accepts, so the overwrite happens because a
    // human chose it — which is the whole difference between this and the bug
    // it replaces. It is also why the token is adopted rather than cleared:
    // clearing would disable enforcement entirely and let a THIRD device's
    // write be lost silently in the same breath.
    //
    // Per-render impl behind a permanently stable wrapper (the documented
    // ref-shadowed idiom): a dep array here would pin the save chain the same
    // way the Run Sheet open-swallow bug pinned the routing chain.
    const handleConflictOverwriteImpl = async (reviewedToken?: string) => {
        const conflict = crossTabConflict;
        if (!conflict?.projectId) { setCrossTabConflict(null); return; }
        if (typeof reviewedToken === 'string' && reviewedToken) {
            // The token of the saved version the operator was shown and agreed
            // to replace (F48). A save newer than that one is still refused.
            baseUpdatedAtRef.current = reviewedToken;
        } else if (conflict.actualUpdatedAt) {
            baseUpdatedAtRef.current = conflict.actualUpdatedAt;
        } else {
            // No reported version means we cannot present an acceptable token.
            // Disable enforcement for this one save rather than send a value
            // the server never issued.
            baseUpdatedAtRef.current = null;
        }
        // Clear the latch BEFORE saving so the save is not immediately
        // re-suppressed by its own guard.
        remoteUpdateBlockedRef.current = false;
        blockedProjectConflictRef.current = null;
        lastRemoteVersionReceiptRef.current = null;
        dismissedRemoteVersionRef.current = null;
        setCrossTabConflict(null);
        try {
            await saveCurrentProject();
        } catch (err) {
            console.warn('[QM] deliberate overwrite after stale-write conflict failed:', err?.message || err);
        }
    };
    const handleConflictOverwriteRef = useRef(handleConflictOverwriteImpl);
    handleConflictOverwriteRef.current = handleConflictOverwriteImpl;
    const handleConflictOverwrite = useCallback((reviewedToken?: string) => handleConflictOverwriteRef.current(reviewedToken), []);

    // What "Replace saved version" would replace, read from the saved row (F48).
    const describeConflictOverwriteImpl = async () => {
        const id = crossTabConflict?.projectId;
        if (!id) return null;
        const saved = await getProject(id);
        return buildOverwriteReview({
            savedProject: saved,
            localScope: Array.isArray(scopeRef.current) ? scopeRef.current : [],
        });
    };
    const describeConflictOverwriteRef = useRef(describeConflictOverwriteImpl);
    describeConflictOverwriteRef.current = describeConflictOverwriteImpl;
    const describeConflictOverwrite = useCallback(() => describeConflictOverwriteRef.current(), []);

    // Outage lane review (2026-09-29), F1: the editor state of the last edit
    // made while QuoteMaker's server is not answering. The autosave depends on
    // it, so every edit during an outage is kept on this device. Before, once
    // the first edit was kept nothing the autosave depends on changed again
    // (hasUnsavedChanges was already true), so later edits were never kept and
    // a refresh lost them. Cleared for each outage and each project. Set only
    // while the server is not answering, so a healthy autosave is unchanged.
    const [unsentEditFingerprint, setUnsentEditFingerprint] = useState<string | null>(null);
    useEffect(() => {
        setUnsentEditFingerprint(null);
    }, [apiStatus.disabled, currentProjectId]);

    // Track unsaved changes - set to true whenever user modifies project data
    // Two guards:
    //   • suppressUnsavedRef - single-shot, consumed on next effect fire
    //   • projectLoadGuardUntilRef - TIME-WINDOWED, all state changes within
    //     the window count as hydration, not edits. Belt-and-braces for async
    //     stragglers during project load: photo base64→blob migration, video
    //     URL rehydration, normalizeToSnake setters, the rAF-deferred quote
    //     editor open, post-load modal useEffects that call parent setters,
    //     etc. Without this, opening a completed quote from the dashboard -
    //     even without any edits - would sometimes leave hasUnsavedChanges=
    //     true because one or more setters escaped the single-shot guard.
    //     Peter 2026-04-20: "I didn't make any changes" false-positive prompt.
    const unsavedChangesTrigger = useRef(false);
    const suppressUnsavedRef = useRef(false);
    const projectLoadGuardUntilRef = useRef(0);
    // v5.54.22 — the three latches above are a change DETECTOR, and Peter asked
    // for a comparator: "it should actually check if any meaningful updates
    // were made before suggesting user to save". They stay (they still cheaply
    // skip the hydration burst), but they no longer DECIDE. savedFingerprintRef
    // holds a normalised snapshot of the persisted state as of the last load or
    // successful save; nothing is dirty unless the current state differs from
    // it. See src/utils/project-fingerprint.ts for why this can't miss an edit.
    const savedFingerprintRef = useRef(null);
    const currentProjectFingerprint = (overrides = {}) => computeProjectFingerprint(
        overlayProjectFingerprintSaveOverrides({
            quoteTitle, titleEditedByUser, formData, stage, projectWorkflowMode,
            legacyBannerDismissed, photos, documents, videos, videoTranscripts,
            selectedHeroImage, projectSummary, analysis, photoAnalysis,
            scopeGstInclusive, scope, quoteNote, aiGeneratedItems, originalAiScope,
            quoteData, workMethodOverallProgramme, projectStrategy, projectWMS,
            appliedLearnings, learningsRetrievalMode, projectDefaultMargin,
        }, overrides),
    );
    const currentProjectFingerprintRef = useRef(currentProjectFingerprint);
    currentProjectFingerprintRef.current = currentProjectFingerprint;
    // Stable identity over a per-render impl — the ref-wrapper idiom from
    // CLAUDE.md. A dep array here would pin the persistence hook's closure over
    // the routing/load chain, which is the 2026-07-30 "project loaded, page
    // unchanged" class-of-bug.
    const captureProjectFingerprint = useCallback(
        (overrides = {}) => currentProjectFingerprintRef.current(overrides),
        [],
    );
    useEffect(() => {
        if (!unsavedChangesTrigger.current) { unsavedChangesTrigger.current = true; return; }
        const fingerprint = currentProjectFingerprint();
        const activeProjectId = currentProjectIdRef.current || currentProjectId || null;
        if (manualSaveRetryLatch?.projectId === activeProjectId
            && manualSaveRetryLatch.fingerprint !== fingerprint) {
            setManualSaveRetryLatch(null);
        }
        const paintedContext = paintedProjectLoadContextRef.current;
        const editIntent = deliberateProjectEditIntentRef.current;
        // One event gets one comparison. Consume before evaluating so a false
        // local-control intent can never be revived by later state churn.
        deliberateProjectEditIntentRef.current = null;
        if (isConfirmedProjectReviewEditIntent(editIntent, {
            isLoadingProject: isLoadingProjectRef.current,
            pipelineHalted: aiPipelineHaltedRef.current,
            currentProjectId: currentProjectIdRef.current,
            currentLoadGeneration: projectLoadGenerationRef.current,
            paintedProjectId: paintedContext.projectId,
            paintedLoadGeneration: paintedContext.loadGeneration,
            currentFingerprint: fingerprint,
        })) {
            // A deliberate edit always wins over a pending infrastructure
            // receipt, even if React happened to batch both state transitions.
            pendingProjectHydrationFingerprintRef.current = null;
            // When this is the first post-load mutation, retain the before-edit
            // fingerprint as the save baseline instead of absorbing the edit
            // into the hydration baseline.
            if (savedFingerprintRef.current === null) {
                savedFingerprintRef.current = editIntent.fingerprintBeforeEdit;
            }
            openedForReviewRef.current = false;
            if (apiStatus.disabled) setUnsentEditFingerprint(fingerprint);
            setHasUnsavedChanges(true);
            return;
        }
        const hydratedFingerprint = consumeProjectHydrationFingerprintReceipt(
            pendingProjectHydrationFingerprintRef,
            {
                projectId: activeProjectId,
                loadGeneration: paintedContext?.loadGeneration,
            },
            fingerprint,
        );
        if (hydratedFingerprint !== null) {
            savedFingerprintRef.current = hydratedFingerprint;
            // The receipt rebases only its load-owned media lane. If a builder
            // edit was already present, the current fingerprint still differs
            // and must fall through to the ordinary dirty comparator.
            if (fingerprint === hydratedFingerprint) return;
        }
        if (suppressUnsavedRef.current) {
            suppressUnsavedRef.current = false;
            // A project reopen can hydrate the same durable media receipt with
            // device-local blob URLs/ids. When React batches that load into one
            // dirty-effect pass, suppression must also adopt the hydrated
            // fingerprint. Otherwise the old S3-only baseline survives the
            // load window and the next equal scope allocation starts a
            // permanent no-op autosave loop.
            if (Date.now() < projectLoadGuardUntilRef.current) {
                savedFingerprintRef.current = fingerprint;
            }
            return;
        }
        // During the load window every settle is hydration, not an edit — keep
        // re-baselining so the LAST hydrated state becomes the thing edits are
        // measured against. This is what makes the old 2000ms race harmless:
        // a load-tail repair landing after the window is now compared, not
        // assumed to be an edit.
        if (Date.now() < projectLoadGuardUntilRef.current) {
            savedFingerprintRef.current = fingerprint;
            return;
        }
        // No baseline yet (a project that was never loaded through the normal
        // path). Adopt the current state rather than declaring it dirty.
        if (savedFingerprintRef.current === null) {
            savedFingerprintRef.current = fingerprint;
            return;
        }
        if (fingerprint === savedFingerprintRef.current) return;
        // 2026-08-19 (Peter: "auto saving when I didn't make any changes in
        // Stage 4"): every dirty flip names WHAT changed, so a phantom save
        // self-diagnoses in the console instead of being a mystery. Runs on
        // the FLIP only, never per keystroke.
        try {
            const changed = diffProjectFingerprintKeys(savedFingerprintRef.current, fingerprint);
            if (changed.length > 0) console.info(`[QM] Project marked dirty — changed: ${changed.join(', ')}`);
        } catch { /* attribution is best-effort */ }
        // Dirty state without a matching explicit edit intent can be late
        // hydration, stage/navigation persistence, or another programmatic
        // repair. Save it normally, but do not turn observation into consent
        // for paid automation. Only the confirmed branch above releases review.
        if (apiStatus.disabled) setUnsentEditFingerprint(fingerprint);
        setHasUnsavedChanges(true);
    }, [formData, photos, documents, scope, quoteNote, quoteData, analysis, selectedHeroImage, videos, videoTranscripts, photoAnalysis, aiGeneratedItems, originalAiScope,
        quoteTitle, titleEditedByUser, stage, projectWorkflowMode, legacyBannerDismissed, projectSummary, scopeGstInclusive, workMethodOverallProgramme, projectStrategy, projectWMS, appliedLearnings, learningsRetrievalMode, projectDefaultMargin, reviewEditProbeRevision,
        manualSaveRetryLatch, currentProjectId]);

    // Self-heal the dirty bit when a save settles. saveCurrentProject captures
    // its envelope up front and clears hasUnsavedChanges on success, so an edit
    // made WHILE the save was in flight was never in that envelope and would
    // otherwise sit unsaved behind a "Saved" chip until the operator happened to
    // touch something else. Comparing against the baseline the save just
    // established catches exactly that case and nothing else.
    const wasSavingProjectRef = useRef(false);
    useEffect(() => {
        const wasSaving = wasSavingProjectRef.current;
        wasSavingProjectRef.current = isSavingProject;
        if (!wasSaving || isSavingProject) return;
        if (savedFingerprintRef.current === null) return;
        const settledFingerprint = currentProjectFingerprintRef.current();
        if (settledFingerprint === savedFingerprintRef.current) return;
        try {
            const changed = diffProjectFingerprintKeys(savedFingerprintRef.current, settledFingerprint);
            if (changed.length > 0) console.info(`[QM] Dirty again after save settled — changed: ${changed.join(', ')}`);
        } catch { /* attribution is best-effort */ }
        setHasUnsavedChanges(true);
    }, [isSavingProject]);

    // AUTO-SAVE (Peter v5.41.186): the manual SAVE button was removed; project
    // updates now save automatically. Debounced ~2s so a burst of edits settles
    // into one save once the user pauses. Reuses every existing safety:
    // saveCurrentProject gates on isProjectWorthSaving, coalesces concurrent
    // saves via the 300ms queue drain, guards re-entrancy (isSavingProjectRef),
    // and on success flips hasUnsavedChanges→false so this loop settles. The
    // post-save mutations that historically re-tripped the dirty bit are already
    // suppressed (suppressUnsavedRef), so this should not thrash. Ctrl+S + the
    // save-before-switch / lifecycle saves are unchanged.
    //
    // The server-stale latch below is load-bearing, not defensive. A failed
    // save leaves hasUnsavedChanges TRUE (it is only cleared on success), and
    // this effect keys on exactly that — so a refusal the backend will keep
    // issuing, like a stale-write 409, would otherwise re-fire here every two
    // seconds for as long as the tab stays open. Retrying an envelope the
    // server has already rejected for being out of date cannot ever succeed;
    // only an operator decision (Reload, or a deliberate Overwrite) can move
    // it forward, so the loop must stop until one arrives.
    // ─── Back off when the SERVER is the thing that is failing ──────────────
    // The latch above handles a refusal that will never change its mind. This
    // handles the opposite: a backend that is struggling and would recover if
    // we stopped shouting at it.
    //
    // On 2026-08-18 the cloud backend died of an out-of-memory mid-quote and
    // every request 502'd. This effect kept firing on its 2-second timer, and
    // each fire re-sent the SAME multi-megabyte project envelope — photos and
    // all — at a process that was already out of heap. The retry was not a
    // bystander to that outage; it was feeding it.
    //
    // Exponential backoff to a 60s ceiling, reset the moment a save succeeds.
    // A transient blip still recovers in two seconds; a real outage gets
    // geometrically quieter instead of a multi-MB POST every two seconds.
    const saveRetryDelayRef = useRef(2000);
    // True once a retry timer has actually FIRED and not yet been accounted for.
    // The delay must grow per failed ATTEMPT, never per arm — see below.
    const saveRetryAttemptedRef = useRef(false);
    useEffect(() => {
        if (!isSavingProject && !hasUnsavedChanges) {
            saveRetryDelayRef.current = 2000;
            saveRetryAttemptedRef.current = false;
        }
    }, [isSavingProject, hasUnsavedChanges]);
    useEffect(() => {
        if (!hasUnsavedChanges || isSavingProject) return;
        if (remoteUpdateBlockedRef.current || crossTabConflict?.kind === 'server-stale') return;
        const activeProjectId = currentProjectIdRef.current || currentProjectId || null;
        if (isProjectSaveRetryBlocked(
            manualSaveRetryLatch,
            activeProjectId,
            currentProjectFingerprintRef.current(),
        )) return;
        // Grow per failed ATTEMPT, in the effect body, gated on a ref.
        //
        // Two ways to get this wrong, and this effect has now had both.
        //
        // (1) Growing inside the timer callback loses the growth entirely. The
        //     cleanup below clears the timer on every dep change, and
        //     isSavingProject is a dep, so ANY other save caller (the 300ms
        //     queue drain in use-project-persistence, a nav-away autosave, a
        //     stage save, Ctrl+S) flips it false->true->false: the true edge
        //     clears the armed-but-unfired timer so the growth line never runs,
        //     and the false edge re-arms at the SAME delay. That pinned the
        //     retry at 2s through the whole 2026-08-21 outage.
        //
        // (2) Growing unconditionally here fixes that but changes the unit from
        //     per-attempt to per-ARM, and those same co-callers re-arm this
        //     effect constantly. A busy pipeline run keeps the project dirty
        //     across many foreign saves, so the delay would climb to the 60s
        //     ceiling without a single retry having failed — or having been
        //     tried — and a genuinely healthy autosave would then sit idle for
        //     a minute.
        //
        // So: the timer callback sets a ref when it FIRES (a ref survives the
        // cleanup; the delay computation did not), and only a fired-and-still-
        // dirty attempt — i.e. one that failed — earns the doubling.
        if (saveRetryAttemptedRef.current) {
            saveRetryDelayRef.current = Math.min(saveRetryDelayRef.current * 2, 60000);
            saveRetryAttemptedRef.current = false;
        }
        // Outage lane (2026-09-29): while QuoteMaker's server is not answering
        // nothing is sent. Each new editor state is kept on this device once
        // (the save stops before the network) and then the autosave waits; the
        // reconnect loop flips apiStatus back and this effect saves once.
        const plan = planAutosave({
            hasUnsavedChanges,
            isSavingProject,
            blocked: false,
            backendReachable: !apiStatus.disabled,
            currentFingerprint: currentProjectFingerprintRef.current(),
            // A project never saved has no id: its edits are kept in the
            // new-project slot (outage lane review).
            keptFingerprint: getKeptUnsavedEditsFingerprint(activeProjectId || NEW_PROJECT_SLOT),
            retryDelayMs: saveRetryDelayRef.current,
        });
        if (plan.action === 'none') return;
        const t = setTimeout(() => {
            // The monitor can discover a newer cloud row after this timer was
            // armed. Re-check the ref at fire time so the stale envelope never
            // leaves the browser merely because React has not rerun the effect.
            if (remoteUpdateBlockedRef.current) return;
            if (plan.countsAsAttempt) saveRetryAttemptedRef.current = true;
            // Never let this reject into the void. saveCurrentProjectImpl
            // deliberately re-throws so awaiting callers can branch on failure
            // (use-project-persistence.ts), but this call site is fire-and-
            // forget, so without a catch every failed retry logged an
            // "Uncaught (in promise)" on top of the error the impl already
            // reported. The impl owns the user-facing reporting; this only has
            // to stop the duplicate noise.
            saveCurrentProject()?.catch?.(() => {});
        }, plan.delayMs);
        return () => clearTimeout(t);
    }, [hasUnsavedChanges, isSavingProject, saveCurrentProject, crossTabConflict, manualSaveRetryLatch, currentProjectId, apiStatus.disabled, unsentEditFingerprint]);

    const currentCrmProject = projects.find(project => project.id === currentProjectId);
    useStage1PostProcessing({
        photos,
        documents,
        videos,
        videoTranscripts,
        preprocessedFiles,
        formData,
        isStage1Processing,
        currentPage,
        stage,
        projectId: currentProjectId,
        crmIdentity: {
            companyId: currentCrmProject?.company_id ?? currentCrmProject?.companyId ?? null,
            propertyId: currentCrmProject?.property_id ?? currentCrmProject?.propertyId ?? null,
        },
        loadGeneration: projectLoadGenerationRef.current,
        // Invoked by the effect/timer after render, so the later batch ref is
        // initialized. Ref checks also cover generation started before paint.
        getLiveContext: () => ({
            projectId: currentProjectIdRef.current,
            loadGeneration: projectLoadGenerationRef.current,
            currentPage: currentPageRef.current,
            stage: stageRef.current,
            generationActive: !!(batchActiveRef.current || isSummarizingRef.current || isAnalyzingRef.current),
            formData: formDataRef.current,
        }),
        setIsPostProcessing,
        setPostProcessContext,
        setFormData,
    });

    // Note: login guard moved to the main return below so all hooks run on every render
    // (fixes "Rendered fewer hooks than expected" error when logging out)

    // ============================================
    // SUBFOLDER HELPER FUNCTIONS
    // ============================================

    // TS-060: generateProjectName extracted to src/utils/project-name-generator.ts


    // TS-060: Quote subfolder management extracted to src/hooks/use-quote-subfolders.ts
    const {
        generateSubfolderName,
        ensureQuoteSubfolder,
        ensureQmOutputSubfolder,
    } = useQuoteSubfolders({
        formData,
        parentFolder,
        quoteSubfolder,
        setQuoteSubfolder,
    });

    // ────────────────────────────────────────────────────────────────────
    // Run Project Summary (Stage 1.5 - best guess pre-analysis pass)
    // ────────────────────────────────────────────────────────────────────
    // Called when the user clicks Next on Stage 1 (Data Entry), OR auto-fired
    // in the background after import processing completes. Produces a
    // structured "best guess" interpretation of the RFQ + photos + notes
    // using deep retrieval (gold standards, CRM history, strata plan history,
    // paired training) BEFORE the heavy Risk Analysis runs.
    //
    // Options:
    //   silent - when true, runs in the background without switching the view.
    //            The user keeps seeing whatever they're currently viewing
    //            (e.g. the Import Complete page) while the AI computes.
    //            Used for the post-import auto-trigger.
    // ─── Project Summary seam — TS-060 T5 slice 1 (DEC-COUNCIL-015) ───────
    // runProjectSummary moved VERBATIM into src/hooks/use-project-summary.ts.
    // The hook owns no pipeline state — everything is threaded from App via
    // this deps object, rebuilt per render so the returned function closes
    // over the same values the original per-render closure did. Keep this
    // instantiation below every dep's declaration (TDZ — same lesson as the
    // useQuoteEditor integration above).
    const evidencePreparationLiveRef = useRef({ documents, photos, videos, videoTranscripts, preprocessedFiles });
    evidencePreparationLiveRef.current = { documents, photos, videos, videoTranscripts, preprocessedFiles };
    const prepareStage1Evidence = (options = {}) => runPrepareStage1Evidence(options, {
        getContext: () => ({
            projectId: currentProjectIdRef.current,
            loadGeneration: projectLoadGenerationRef.current,
            signal: projectAbortRef.current?.signal || null,
        }),
        getLiveEvidence: () => evidencePreparationLiveRef.current,
        getLiveForm: () => formDataRef.current,
        loadProject: id => db.table('projects').get(id),
        saveProject: (patch, ownership) => {
            const save = saveCurrentProjectRef.current;
            if (typeof save !== 'function') {
                throw Object.assign(new Error('Project saving is not ready. Retry evidence preparation.'), { code: 'PROJECT_SAVE_UNAVAILABLE', retryable: true });
            }
            return save(patch, ownership);
        },
        adoptEvidence: (verified, { formDataChanged }) => {
            const evidence = {
                documents: verified.documents || [], photos: verified.photos || [], videos: verified.videos || [],
                videoTranscripts: verified.videoTranscripts || [], preprocessedFiles: verified.preprocessedFiles || [],
            };
            // An automatic Summary owns this checkpoint's fingerprint from here,
            // before any render can show it. The notes a new transcript appended
            // are the tail of the verified notes beyond the live notes (the save
            // already refused to commit if the live form had moved).
            registerAdoptedEvidence(
                { witness: attendedSummaryWitnessRef, summarizing: isSummarizingRef },
                evidence,
                {
                    appendedNotes: formDataChanged && verified.formData
                        ? String(verified.formData.notes ?? '').slice(String(formDataRef.current?.notes ?? '').length)
                        : '',
                },
            );
            evidencePreparationLiveRef.current = evidence;
            photosRef.current = evidence.photos;
            videosRef.current = evidence.videos;
            setDocuments(evidence.documents);
            setPhotos(evidence.photos);
            setVideos(evidence.videos);
            setVideoTranscripts(evidence.videoTranscripts);
            setPreprocessedFiles(evidence.preprocessedFiles);
            // The guarded save has already merged any new transcript into the
            // latest manual form. Adopt only that saved form after readback.
            if (formDataChanged && verified.formData) {
                formDataRef.current = verified.formData;
                setFormData(verified.formData);
            }
        },
        apiKey, model, audioModel,
    });
    const { runProjectSummary } = useProjectSummary({
        apiKey,
        model,
        currentProjectId,
        projectSummaryRecoveryRevision,
        formData,
        formDataRef,
        documents,
        photos,
        videos,
        videoTranscripts,
        preprocessedFiles,
        projectSummary,
        setSettingsOpen,
        setIsSummarizing,
        setSummaryError,
        setSummaryRerunHold,
        requestCockpitTab,
        onSummaryLanded: noteSummaryLanded,
        onSummaryRunInterrupted: noteSummaryRunInterrupted,
        setProjectSummary,
        setSummaryNeedsRefresh,
        setLastSummarizedSnapshot,
        setFormData,
        isSummarizingRef,
        projectSummaryGenerationOwnerRef,
        inspectorNotesAiPausedRef,
        projectAbortRef,
        currentProjectIdRef,
        projectLoadGenerationRef,
        currentPageRef,
        stageRef,
        isLoadingProjectRef,
        lastAutoProjectFocusRef,
        projectSummaryFailureRef,
        projectSummarySuccessRef,
        resetProjectSummaryFocusRecoveryAuthority,
        aiPipelineHaltedRef,
        openedForReviewRef,
        showConfirm,
        clearInspectorNotesAiPause,
        saveCurrentProject,
        isPipelineAbort,
        prepareStage1Evidence,
    });
    runProjectSummaryRef.current = runProjectSummary;

    // v5.38.0: Re-quote shortcut (Stage 1.5 → Stage 4). When Project Summary
    // detects requote_signal.is_requote at ≥60% confidence and the user clicks
    // "Skip to Scope Builder", run the requote-scope-extractor to parse the
    // previous quote + apply the client's change-request, then load the result
    // into scope and jump to Stage 4. Stages 2 + 3 are bypassed because they
    // were already done when the original quote was issued.
    //
    // Pricing per Peter 2026-05-21: carry over from the previous quote;
    // Stage 4 surfaces a stale-pricing banner via formData.requoteContext.
    // Edits per Peter 2026-05-21: extractor applies them automatically;
    // modified/added items are tagged via requoteOrigin for the Stage 4 UI.
    const handleRequoteShortcut = async () => {
        // Skip to Scope Builder is the operator choosing the route: an automatic
        // Summary still working may not select its tab over it.
        revokeAttendedAutomaticRiskIntent();
        if (typeof window !== 'undefined' && window.qm_disable_requote_shortcut === true) {
            console.warn('[QM] requote shortcut killed by kill switch');
            return;
        }
        await executeRequoteShortcut({
            isRequoteShortcutRunning,
            apiKey,
            model,
            projectSummary,
            documents,
            formData,
            currentProjectId,
            setToast,
            setIsRequoteShortcutRunning,
            setScope,
            setFormData,
            setCompletedStages,
            setStageWithTransition,
            // Read the refs at call time: the runner compares the project it
            // started on against the project on screen after its AI await.
            getLiveRunIdentity: () => ({
                projectId: currentProjectIdRef.current,
                loadGeneration: projectLoadGenerationRef.current,
                signal: projectAbortRef.current?.signal || null,
            }),
            runOwnerRef: requoteRunOwnerRef,
            // Back, a pill, another page or the Summary sub-view moving while
            // the extraction runs is a navigation choice the result must not
            // override; scope the operator authored meanwhile is not overwritten.
            getNavigationIntent: () => navigationIntentEpochRef.current,
            getLiveScope: () => scopeRef.current,
        });
    };

    // W0 (src/utils/quote-brain-project.ts): under a business whose Quote Brain
    // is on, a job with no old-pipeline work is the Quote Brain's to write. The
    // rule's inputs (the business's mode, jobs put back on the old pipeline)
    // land after the page does, so App renders again when they change.
    useSyncExternalStore(subscribeQuoteBrainJobRules, getQuoteBrainJobRulesVersion);
    const quoteBrainWritesThisJob = isQuoteBrainNewJob(currentProjectId, { scope, wms: projectWMS, riskAnalysis: analysis });
    // Stage 1's forward button on a Quote Brain job: hidden while the brain writes, Scope once it has written.
    const quoteBrainRoute = quoteBrainStage1Route(currentProjectId, { scope, wms: projectWMS, riskAnalysis: analysis });

    // ─── Phase 14: Project Work Method Statement handler ─────────────────────
    const continueAiAfterInspectorNotes = useCallback(() => {
        clearInspectorNotesAiPause();
        if (!inspectorNotesCanAutoRefresh) return;

        const notesText = String(formData.notes || '');
        // O-S9: Continue after a notes pause resumes a Summary only for notes that show something (C13: the summary's own
        // rule once one exists).
        const hasSummaryInput = canAutoSummarise({
            hasSummary: !!projectSummary,
            notes: notesText,
            documentCount: documents.length,
            photoCount: photos.length,
            transcriptCount: videoTranscripts.length,
            videoCount: videos.length,
        });
        const scheduledEvidenceFingerprint = currentProjectSummaryEvidenceFingerprintRef.current;

        window.setTimeout(() => {
            if (currentPageRef.current !== 'quotemaker') return;
            // STOP latch: finishing a notes edit is an auto-RESUME path, not a
            // restart request — after a STOP the pipeline stays down until a
            // deliberate run (button / assistant), which clears the latch.
            if (aiPipelineHaltedRef.current || openedForReviewRef.current) return;
            if (currentProjectSummaryEvidenceFingerprintRef.current !== scheduledEvidenceFingerprint) return;
            if (stageRef.current === 1 && hasSummaryInput && (!projectSummary || summaryNeedsRefresh) && !isSummarizingRef.current) {
                const summaryRun = beginAttendedAutomaticSummaryRun(
                    'inspector-notes-summary-resume',
                    scheduledEvidenceFingerprint,
                );
                if (!summaryRun) return;
                runProjectSummaryRef.current?.(summaryRun);
            }
            // The Risk report this used to resume went with the old pipeline (docs/old-pipeline-removal/PLAN.md).
        }, 0);
    }, [
        beginAttendedAutomaticSummaryRun,
        clearInspectorNotesAiPause,
        currentProjectSummaryEvidenceFingerprint,
        documents.length,
        formData.notes,
        inspectorNotesCanAutoRefresh,
        photos.length,
        projectSummary,
        runProjectSummary,
        summaryNeedsRefresh,
        videoTranscripts.length,
        videos.length,
    ]);
    // B4: the notes pause releases by itself, when focus leaves the notes editor
    // for somewhere else in the app, or 10 s after the last keystroke once the
    // caret is out of the notes (a minute with it still there). Every keystroke
    // changes formData.notes, which restarts the wait. A window switch does not
    // release it (the operator comes back to the caret). Releasing only lifts
    // the pause and starts no run.
    // The 2 s summary watcher then re-makes an out-of-date summary. A blur
    // caused by pressing NEXT or a menu item lets that press decide, and
    // leaving Stage 1 buys nothing.
    const releaseInspectorNotesPause = useCallback(() => {
        if (!inspectorNotesAiPausedRef.current) return;
        clearInspectorNotesAiPause();
    }, [clearInspectorNotesAiPause]);
    useInspectorNotesPauseRelease({
        paused: inspectorNotesAiPaused,
        notes: formData.notes,
        onRelease: releaseInspectorNotesPause,
        // The caret still in the site notes: a pause in typing, not the end of the edit.
        isEditing: () => typeof document !== 'undefined'
            && !!(document.activeElement as Element | null)?.closest?.('[data-surface="stage1-site-notes"]'),
    });

    // ─── Phase 16: Project Costing Sheet handlers ────────────────────────────
    // Deterministic consolidation - no AI call. Returns instantly. Preserves
    // admin-entered actuals across regens by passing the previous sheet in.
    //
    // v5.12.7 (Peter 2026-04-22): accept `scopeOverride` + `silent` params
    // so auto-fire callers can pass a fresh scope snapshot. Without this
    // the 600ms-post-analysis auto-fire read `scope` from closure before
    // the analysis result's `setScope(...)` had committed, so the check
    // saw an empty array and toast-fired "Cannot generate Costing Sheet
    // with an empty scope" on every quote. The WMS sibling handler already
    // does this via `scopeOverride`. `silent` suppresses the user-facing
    // toast for background auto-fires — manual button clicks still toast
    // as before.
    // TS-060 (DEC-COUNCIL-015): handleGenerateProjectCostingSheet and
    // handleSetBuilderHbcfDecision moved behind useCostingGeneration hook.
    const {
        handleGenerateProjectCostingSheet,
        handleSetBuilderHbcfDecision,
    } = useCostingGeneration({
        scope,
        scopeGstInclusive,
        formData,
        projectWMS,
        projectStrategy,
        workMethodOverallProgramme,
        analysis,
        quoteData,
        projectCostingSheet,
        projectDefaultMargin,
        quoteEditorOpen,
        quoteEditorLiveSnapshot,
        currentProjectId,
        businessPricingSnapshotStatus,
        currentBusinessPricingSnapshot,
        canPersistInternalPricing: effectiveAccess(user)['business-training'] === true,

        setScope,
        setFormData,
        setProjectWMS,
        setQuoteData,
        setProjectCostingSheet,
        setToast,
        setCostingRegenerating,
        setQuoteEditorLiveSnapshot,

        projectWMSRef,
        projectCostingSheetRef,
        scopeRef,
        quoteDataRef,
        currentProjectIdRef,
        projectLoadGenerationRef,
        projectAbortRef,
        costingGenerationOwnerRef,
        hbcfDecisionSaveOwnerRef,

        isSavingProjectRef: { get current() { return isSavingProjectRef.current; } },
        pendingSaveRef: { get current() { return pendingSaveRef.current; } },
        saveDrainTimerRef: { get current() { return saveDrainTimerRef.current; } },

        saveCurrentProject,
        isCurrentProjectQuoteGenerationInFlight,
    });

    // The HBCF notice (Peter, 2026-10-23). HBCF is decided when the items are
    // built; afterwards only an Assistant change across the threshold, or a
    // quote the pricing check refuses for a missing HBCF line, brings this
    // notice. Its one button applies the HBCF rule through the builder's own
    // HBCF decision (not waived), which adds or removes the HBCF lines in
    // both stages and saves them. The button is pressed some renders later, so
    // it calls the handler of the latest render (the handler reads formData
    // and the quote from its closure).
    //
    // A notice belongs to the job and load that raised it: it is dropped when
    // another job opens, and its button does nothing on another job. The press
    // re-reads the job, so a waiver or "carried in the items" set since, or
    // items that no longer need what the label says, are never undone by an
    // old notice (review, 2026-10-23).
    const handleSetBuilderHbcfDecisionRef = useRef(handleSetBuilderHbcfDecision);
    handleSetBuilderHbcfDecisionRef.current = handleSetBuilderHbcfDecision;
    const currentBusinessPricingSnapshotRef = useRef(currentBusinessPricingSnapshot);
    currentBusinessPricingSnapshotRef.current = currentBusinessPricingSnapshot;
    // The rule's check for a notice's button, on the job as it is now.
    const hbcfNoticeRefusalNow = (actionLabel) => {
        const costingConfig = currentBusinessPricingSnapshotRef.current?.costingConfig
            || currentBusinessPricingSnapshotRef.current?.costing_config
            || null;
        const structured = projectWMSRef.current?.structured || {};
        return hbcfNoticeRefusal(Array.isArray(scopeRef.current) ? scopeRef.current : [], actionLabel, {
            costingConfig,
            formData: formDataRef.current,
            isGstInclusive: scopeGstInclusiveRef.current !== false,
            compliancePosture: structured.compliance_posture || structured.compliancePosture || null,
        });
    };
    const showHbcfNotice = ({ message, actionLabel, projectId = undefined, change = null }) => {
        const raisedFor = { ...(paintedProjectLoadContextRef.current || {}) };
        // Raised for a job that is no longer on screen: say so, with no
        // button that would act on the job now open.
        if (projectId !== undefined
            && !isSameHbcfNoticeJob({ projectId, loadGeneration: raisedFor.loadGeneration }, raisedFor)) {
            setToast({ message: `${message} (This was for a job that is no longer open.)`, type: 'warning', sticky: true });
            return;
        }
        setToast({
            message,
            type: 'warning',
            sticky: true,
            hbcfNoticeFor: raisedFor,
            // The Assistant's change the notice is about (review round 4).
            hbcfChange: change,
            action: {
                label: actionLabel,
                onClick: () => {
                    const painted = paintedProjectLoadContextRef.current || {};
                    if (!isSameHbcfNoticeJob(raisedFor, painted)) {
                        setToast({ message: 'That HBCF notice was for another job, so nothing was changed.', type: 'warning' });
                        return;
                    }
                    if (!scopeCarriesAssistantChange(scopeRef.current, change)) {
                        setToast({ message: "The items no longer hold the Assistant's change this notice was about, so HBCF was not changed.", type: 'info' });
                        return;
                    }
                    setToast(null);
                    const refusal = hbcfNoticeRefusalNow(actionLabel);
                    if (refusal) {
                        setToast({ message: refusal, type: 'info' });
                        return;
                    }
                    void (async () => {
                        try {
                            const applied = await handleSetBuilderHbcfDecisionRef.current(false);
                            if (!applied) {
                                setToast({ message: 'A quote is being written for this job, so HBCF was not changed. Try again when it finishes.', type: 'warning' });
                            }
                        } catch (error) {
                            setToast({ message: error?.message || 'HBCF was not changed.', type: 'error' });
                        }
                    })();
                },
            },
        });
    };

    // A notice on screen goes once the items stop holding the Assistant's
    // change it describes: the builder kept the other version, or changed
    // those items themselves (review round 4).
    useEffect(() => {
        setToast((prev) => (prev?.hbcfChange && !scopeCarriesAssistantChange(scope, prev.hbcfChange) ? null : prev));
    }, [scope]);

    // An HBCF notice from another job (or an earlier load of this one) goes
    // when a job opens.
    useEffect(() => {
        setToast((prev) => hbcfNoticeAfterJobChange(prev, {
            projectId: currentProjectId,
            loadGeneration: paintedProjectLoadGeneration,
        }));
    }, [currentProjectId, paintedProjectLoadGeneration]);

    // A costing refresh that came due while Stage 5 was open runs once it
    // closes, for the same job and load (review round 4).
    // Only a change to the items or the GST basis while Stage 5 is open
    // counts: looking at a job that loaded into Stage 5 and closing it arms
    // nothing, and a refresh never runs while a quote is being written
    // (review round 5).
    // A refresh is owed when the items or GST basis differ from the ones last
    // costed: the ones the load painted, or the last refresh's (review round
    // 6: a change made just before Next opened Stage 5 was never refreshed).
    const costingRefreshDeferredRef = useRef(null);
    const lastCostedKeyRef = useRef(null);
    const [costingRefreshNudge, setCostingRefreshNudge] = useState(0);
    const costingScopeKey = (rows, gstInclusive) => `${gstInclusive !== false}:${scopeFingerprintForAutoDraft(Array.isArray(rows) ? rows : [])}`;
    const costingRefreshOwed = (projectId, loadGeneration) => {
        const costed = lastCostedKeyRef.current;
        return !costed
            || costed.projectId !== projectId
            || costed.loadGeneration !== loadGeneration
            || costed.key !== costingScopeKey(scopeRef.current, scopeGstInclusiveRef.current);
    };
    // The loaded items count as costed only when the loaded costing sheet is
    // for them (review round 7: a Stage 5 change saved before a Revise or
    // Reload was counted as costed and never refreshed).
    useEffect(() => {
        const sheet = projectCostingSheetRef.current;
        const loadedSheetIsFresh = !!sheet?.scope_hash && !isCostingStale(sheet, scope);
        lastCostedKeyRef.current = loadedSheetIsFresh
            ? {
                projectId: currentProjectId,
                loadGeneration: paintedProjectLoadGeneration,
                key: costingScopeKey(scope, scopeGstInclusive),
            }
            : null;
    }, [paintedProjectLoadGeneration]);
    useEffect(() => {
        if (quoteEditorOpen) return;
        const deferred = costingRefreshDeferredRef.current;
        if (!deferred) return;
        costingRefreshDeferredRef.current = null;
        if (deferred.projectId !== currentProjectIdRef.current
            || deferred.loadGeneration !== projectLoadGenerationRef.current) return;
        if (!costingRefreshOwed(deferred.projectId, deferred.loadGeneration)) return;
        setCostingRefreshNudge((n) => n + 1);
    }, [quoteEditorOpen]);

    // Auto-refresh the deterministic project costing sheet whenever the
    // scope or GST mode changes in Stage 4. Stage 5 is a saved delivery view,
    // so opening it or editing its presentation never silently rebuilds
    // costings. Without this Stage 4 changes (QM Assistant tools,
    // manual edits, scope morph, refresh-from-WMS) leave the saved
    // costing sheet stale until WMS regen, quote-gen, or a manual open
    // of the Internal Costings modal. Debounced 800ms; gated by the
    // existing `qm_disable_costing` kill switch.
    useEffect(() => {
        if (!currentProjectId || !Number.isFinite(paintedProjectLoadGeneration)) return;
        if (!Array.isArray(scope) || scope.length === 0) return;
        const scheduledProjectId = currentProjectId;
        const scheduledLoadGeneration = paintedProjectLoadGeneration;
        const t = setTimeout(() => {
            // Stage 4 and Stage 5 are peer views. Opening/closing Stage 5 is
            // presentation-only, so quoteEditorOpen is deliberately NOT an
            // effect dependency. Read the live ref at fire time instead: an
            // already-armed Stage 4 timer becomes harmless if Stage 5 opens,
            // while closing an untouched Stage 5 cannot arm a new refresh.
            // A refresh that comes due while Stage 5 is open waits for it to
            // close: a Stage 5 edit now reaches Stage 4 at once, so Back no
            // longer changes the scope that would re-arm it (review round 4).
            if (quoteEditorOpenRef.current) {
                if (costingRefreshOwed(scheduledProjectId, scheduledLoadGeneration)) {
                    costingRefreshDeferredRef.current = { projectId: scheduledProjectId, loadGeneration: scheduledLoadGeneration };
                }
                return;
            }
            if (isCurrentProjectQuoteGenerationInFlight()) {
                // Wakes when the quote run's lock settles, whether or not the
                // run ever set isGenerating (review round 6: a run refused at
                // its pricing check left the refresh parked for good).
                quoteGenerationSingleflightRef.current?.settled?.().then(() => {
                    if (currentProjectIdRef.current !== scheduledProjectId
                        || projectLoadGenerationRef.current !== scheduledLoadGeneration) return;
                    setCostingRefreshNudge((n) => n + 1);
                });
                return;
            }
            if (currentProjectIdRef.current !== scheduledProjectId) return;
            if (projectLoadGenerationRef.current !== scheduledLoadGeneration) return;
            if (typeof localStorage !== 'undefined'
                && localStorage.getItem('qm_disable_costing') === 'true') {
                return;
            }
            const costedKey = costingScopeKey(scopeRef.current, scopeGstInclusiveRef.current);
            handleGenerateProjectCostingSheet({
                source: 'auto-scope-change',
                silent: true,
                expectedProjectId: scheduledProjectId,
                runContext: captureProjectRunContext({
                    projectId: scheduledProjectId,
                    loadGeneration: scheduledLoadGeneration,
                    signal: projectAbortRef.current?.signal || null,
                }),
            }).then((sheet) => {
                // A refresh that wrote no sheet (refused, offline, no Pricing)
                // leaves the refresh owed (review round 7).
                if (!sheet) return;
                if (currentProjectIdRef.current !== scheduledProjectId
                    || projectLoadGenerationRef.current !== scheduledLoadGeneration) return;
                lastCostedKeyRef.current = { projectId: scheduledProjectId, loadGeneration: scheduledLoadGeneration, key: costedKey };
            }).catch((e) => console.warn('[Costing] Auto-refresh on scope change failed:', e?.message));
        }, 800);
        return () => clearTimeout(t);
    }, [
        scope,
        scopeGstInclusive,
        currentProjectId,
        paintedProjectLoadGeneration,
        costingRefreshNudge,
    ]);
    // Open full editor
    const openFullEditor = () => {
        if (quoteData) {
            // DEC-COUNCIL-013 Naruto Cond 3: same-project view-only open uses peek().
            editor.peek();
        }
    };

    // ============================================
    // PROJECT SAVE / LOAD
    // ============================================

    // Pending save queue - prevents lost edits when saves are in-flight.
    // Queued saves must keep their override payload (quoteData, formData,
    // costingSheet, etc.). A bare "save again" flag loses Stage 5 quote editor
    // updates whenever the user clicks Save while a project save is still
    // running.
    // (T5 slice 7 relocation: these five queue-ref declarations moved up
    // VERBATIM from their old spot beside the queue helpers — the helper
    // bodies now live in useProjectPersistence, but the refs stay App-owned
    // per the stateless-hook rule and thread in as deps.)
    const pendingSaveRef = useRef(false);
    const pendingSaveOverridesRef = useRef(null);
    const pendingSaveEnsureProjectOwnerRef = useRef(false);
    const pendingSaveDeferredsRef = useRef([]);
    const isSavingProjectRef = useRef(false);
    const saveDrainTimerRef = useRef(null);
    // v5.16.24 CRM-log dedupe map (decl relocated from beside _crmShouldLog,
    // whose body + full comment now live in use-project-persistence.ts).
    const _crmLogDedupeRef = useRef(new Map());
    // v5.17.1 batch-mode flag (decl relocated from the appHandlersRef block
    // below, where its full comment remains) — read by the hook's duplicate
    // guard and completeProjectLoad's WMS-timer cancel skip.
    const batchActiveRef = useRef(false);
    // The same run as state, for render: the client prompt (ProjectClientLink)
    // must never open over a job the run drives, and must re-render when the
    // run starts and ends (2026-10-04 UAT F5). Set only by setBatchActive.
    const [quoteRunDriving, setQuoteRunDriving] = useState(false);
    // Decision D1, A3 review. The project the active Quote Run is driving (set
    // by the queue's own load in use-project-persistence.ts) and whether a
    // stop of the run has been raised. Both are cleared when a run starts or
    // ends (setBatchActive below). Opening the driven project shows it; a
    // refused open while a stop is pending says the run is stopping.
    const quoteRunProjectIdRef = useRef(null);
    const quoteRunStopRequestedRef = useRef(false);
    // Every stop source (the STOP pill, the Quote Run drawer's Stop and Clear,
    // the Assistant, sign-out, "Stop the run" in the refusal, "Stop the run"
    // in the Quote Run sheet's run bar) raises the queue's one stop event.
    // During a run the listener marks the run as stopping and, unless the
    // event came from cancelAiPipeline itself, ends the job's AI stage through
    // cancelAiPipeline, so every stop ends the run as promptly as the STOP pill
    // (A3 review round 4). Refs only, so the empty dependency list pins nothing.
    useEffect(() => {
        const onQuoteRunStopRequested = createQuoteRunStopListener({
            isRunActive: () => batchActiveRef.current === true,
            markStopRequested: () => { quoteRunStopRequestedRef.current = true; },
            stopAiStage: (options) => cancelAiPipelineRef.current(options),
        });
        window.addEventListener(QUOTE_RUN_STOP_EVENT, onQuoteRunStopRequested);
        return () => window.removeEventListener(QUOTE_RUN_STOP_EVENT, onQuoteRunStopRequested);
    }, []);
    // Distinct from batchActiveRef's save/navigation semantics: this declares
    // which orchestrator owns automatic stage transitions. UI effects must
    // stand down while the explicit runner owns the pipeline.
    const pipelineOwnerRef = useRef('ui-auto');
    // openQuoteForProject pending-open flags (decls relocated from the
    // openQuoteForProject block below, where the full comment remains) —
    // completeProjectLoad reads/clears them inside the hook.
    const pendingOpenQuoteRef = useRef(false);
    const autoOpenLoadedQuoteProjectRef = useRef(null);

    // ─── T5 slice 7 — PROJECT PERSISTENCE seam (TS-060 / DEC-COUNCIL-015) ───
    // resetForm, the save-queue machinery, saveCurrentProjectImpl,
    // loadProject, loadProjectUnchecked and completeProjectLoad moved
    // VERBATIM into useProjectPersistence. The hook owns NO state and NO
    // refs — everything threads from App via this per-render deps object.
    // Instantiated HERE (where resetForm previously sat): every dep below is
    // declared above this line, so all 153 thread DIRECTLY (no lazy
    // wrappers). The queue/dedupe/batch/pending-open refs directly above
    // were relocated within App for exactly that reason. App KEEPS the
    // render-time saveCurrentProjectRef assignment right after the
    // destructure — same position in the render sequence as before the move.
    const {
        resetForm,
        saveCurrentProjectImpl,
        loadProject,
        loadProjectUnchecked,
        completeProjectLoad,
        refuseProjectChangeDuringQuoteRun,
        showQuoteRunJobInsteadOfOpening,
        quoteRunJobInProgress,
        restoreKeptNewProject,
        markRecoveredQuoteOnScreen,
        discardKeptNewProject,
    } = useProjectPersistence({
        user,
        model,
        currentProjectId,
        formData,
        quoteData,
        scope,
        originalAiScope,
        quoteNote,
        analysis,
        photoAnalysis,
        diagnosticResult,
        riskReport,
        photos,
        documents,
        videos,
        videoTranscripts,
        projectSummary,
        projectWMS,
        projectStrategy,
        completedStages,
        stage,
        titleEditedByUser,
        quoteTitle,
        projectWorkflowMode,
        legacyBannerDismissed,
        scopeGstInclusive,
        selectedHeroImage,
        parentFolder,
        quoteSubfolder,
        folderFiles,
        projects,
        hasUnsavedChanges,
        // B: the open Stage 5 editor's own unsaved work, read now; the save that goes with a switch; the explicit discard.
        hasUnsavedVisibleWork,
        saveOutgoingVisibleWork,
        discardOutgoingVisibleDrafts,
        visibleWorkStamp,
        retainOutgoingProject,
        hasUnsavedChangesLive: () => hasUnsavedChangesRef.current === true,
        // C29: Save & open compares the parent's content with the saved baseline once the state the save adopted is committed.
        // `waitForRevisionFilesCommit` is declared further down; this arrow only runs when a Save & open continues, long after render.
        waitForCommit: () => waitForRevisionFilesCommit(),
        aiGeneratedItems,
        projectDefaultMargin,
        appliedLearnings,
        learningsRetrievalMode,
        projectCostingSheet,
        workMethodOverallProgramme,
        setAiGeneratedItems,
        setAnalysis,
        setAppliedLearnings,
        setAssistantDraftSessionKey,
        setCompletedStages,
        setCostingRegenerating,
        setCurrentProjectId,
        setDiagnosticResult,
        setRiskReport,
        setDocuments,
        setExtractedFacts,
        setFolderFiles,
        setFormData,
        setHasUnsavedChanges,
        setHasViewedRationale,
        setImportPhase,
        setInspectorNotesAiPaused,
        setIsAnalyzing,
        setIsSavingProject,
        setIsStage1Processing,
        setLastSavedAt,
        setLastSummarizedSnapshot,
        setLearningsRetrievalMode,
        setLegacyBannerDismissed,
        setMediaRecoveryNeeded,
        setMissingFiles,
        setOcrApplying,
        setOcrResults,
        setOcrScanning,
        setOriginalAiScope,
        setParentFolder,
        setPhotoAnalysis,
        setPhotoAssignBusy,
        setPhotos,
        setPostProcessContext,
        setPreprocessedFiles,
        setProjectCostingSheet,
        setProjectDefaultMargin,
        setProjectStrategy,
        setProjectSummary,
        setProjectWMS,
        setProjectWorkflowMode,
        setProjects,
        setQuoteData,
        setQuoteNote,
        setQuoteSubfolder,
        setQuoteTitle,
        setResetCounter,
        setRibbonDismissing,
        setRibbonNotification,
        setSaveError,
        setManualSaveRetryLatch,
        setSaveStatus,
        setScope,
        setScopeGstInclusive,
        setSelectedHeroImage,
        setStage,
        setSummaryError,
        setSummaryRerunHold,
        setSummaryNeedsRefresh,
        setTitleEditedByUser,
        setToast,
        setVideoTranscripts,
        setVideos,
        setWorkMethodOverallProgramme,
        autoAssignAbortRef,
        autoOpenLoadedQuoteProjectRef,
        batchActiveRef,
        quoteRunProjectIdRef,
        quoteRunStopRequestedRef,
        currentProjectIdRef,
        photosRef,
        videosRef,
        quoteDataRef,
        selectedHeroImageRef,
        scopeRef,
        importAbortRef,
        inspectorNotesAiPausedRef,
        isAnalyzingRef,
        isLoadingProjectRef,
        isSavingProjectRef,
        isSummarizingRef,
        lastAssignedPhotoIdsRef,
        lastAutoProjectFocusRef,
        pendingOpenQuoteRef,
        pendingSaveDeferredsRef,
        pendingSaveEnsureProjectOwnerRef,
        pendingSaveOverridesRef,
        baseUpdatedAtRef,
        staleWriteBaselineRef,
        loadedOriginalSnapshotContextRef,
        pendingSaveRef,
        photoAssignBusyRef,
        prevFilesProcessedRef,
        projectAbortRef,
        projectPersistenceAbortRef,
        aiPipelineHaltedRef,
        openedForReviewRef,
        projectLoadGenerationRef,
        paintedProjectLoadContextRef,
        // Every load that lands QuoteMaker records itself here while it
        // fetches, so Menu > QuoteMaker waits for it (2026-09-30).
        pendingProjectOpenRef,
        pendingProjectHydrationFingerprintRef,
        projectLoadGuardUntilRef,
        savedFingerprintRef,
        captureProjectFingerprint,
        qmQuoteNoAllocationInFlightRef,
        saveCurrentProjectRef,
        saveDrainTimerRef,
        saveErrorRef,
        suppressUnsavedRef,
        titleSourceRef,
        _crmLogDedupeRef,
        QM_STATE_KEY,
        showConfirm,
        classifySaveError,
        setCrossTabConflict: setLatchedProjectConflict,
        saveCurrentProject,
        generateQuoteTitle,
        handleNavigate,
        // "Stop the run" in the Quote Run refusal is the STOP pill: the queue
        // stop first, then the job's AI stage is aborted (A3 review).
        stopQuoteRun: (options) => cancelAiPipeline(options),
        editor,
        procDispatch,
        getPendingProjectMediaRetirementReceipts: (projectId) => (
            pendingProjectMediaRetirementReceipts(projectMediaRetirementQueueRef.current, projectId)
        ),
        acknowledgeProjectMediaRetirementReceipts: (projectId, receipts) => (
            acknowledgeProjectMediaRetirementReceipts(
                projectMediaRetirementQueueRef.current,
                projectId,
                receipts,
            )
        ),
    });

    // Ref always holds the latest implementation (avoids stale closures) and
    // enforces the same commercial-transaction owner for legacy direct-ref
    // callers. Without this second boundary, nav/close autosaves could bypass
    // the public wrapper and coalesce an older scope over an HBCF decision.
    saveCurrentProjectRef.current = (overrides = {}, options = {}) => {
        const commercialOwner = hbcfDecisionSaveOwnerRef.current;
        if (commercialOwner !== null && options?.commercialDecisionOwner !== commercialOwner) {
            const error = new Error('A builder HBCF decision is being saved. Wait for it to finish before saving or navigating.');
            error.code = 'PROJECT_COMMERCIAL_DECISION_IN_FLIGHT';
            error.retryable = true;
            throw error;
        }
        assertRemoteProjectSaveReady();
        return saveCurrentProjectImpl(overrides, options);
    };

    // TS-060: Global dashboard drag-and-drop seam (DEC-COUNCIL-015)
    const { globalDragActive, stage1Ref } = useGlobalDashboardDrop({
        currentPage,
        stage,
        resetForm,
        handleNavigate,
    });

    // W0 reads the business's Quote Brain mode. The run banner refreshes it for
    // each job it opens; this reads it once per sign-in, so a Quote Run started
    // from any page (the banner is not mounted there) already has it.
    useEffect(() => {
        if (!user?.id) return undefined;
        let alive = true;
        getQuoteBrainStatus()
            .then((status) => { if (alive) noteQuoteBrainBusinessMode(status?.mode, status?.ready); })
            .catch(() => { /* the banner reads it again when a job opens */ });
        return () => { alive = false; };
    }, [user?.id]);

    // v5.37.27 H1 fix: ref-shadowed hasUnsavedChanges so the express
    // generate-quote path can read FRESH state from inside a stale closure.
    // The express path is invoked from a toast onClick captured during the
    // pre-process flow — between capture + click, hasUnsavedChanges may have
    // flipped, but the closure has the old value. Reading from this ref
    // sidesteps the stale-closure problem.
    const hasUnsavedChangesRef = useRef(false);
    hasUnsavedChangesRef.current = hasUnsavedChanges;

    // openQuoteForProject - load a project AND immediately open the quote
    // editor in view mode. Wired to the Run Sheet's row actions (Open quote /
    // Follow up / Open) and the Analysis evidence drawer. Uses
    // pendingOpenQuoteRef so the editor flags fire AFTER completeProjectLoad
    // has finished hydrating state (otherwise the modal would open on the
    // previous project's data briefly during the load).
    // (pendingOpenQuoteRef / autoOpenLoadedQuoteProjectRef decls relocated
    // above the useProjectPersistence instantiation — T5 slice 7.)
    //
    // ── v5.47.37 — the Run Sheet open-swallow fix ──────────────────────────
    // This was `useCallback(fn, [currentProjectId, quoteData, scope,
    // completedStages])`, a dep list that omits `loadProject`. loadProject,
    // completeProjectLoad and handleNavigate are all plain per-render arrows,
    // so the memo pinned that whole closure chain from whichever render last
    // changed one of those four values. Navigating AWAY from QuoteMaker mutates
    // quoteData/scope (syncQuoteEditorBackToState + editor.reset, inside
    // handleNavigate) on the render where currentPage is STILL 'quotemaker' —
    // setCurrentPage is deferred 50ms. So the pinned handleNavigate read
    // currentPage === 'quotemaker' from then on, and its same-page no-op
    // silently swallowed completeProjectLoad's closing
    // handleNavigate('quotemaker'): the project hydrated in full while the
    // operator was left standing on the Run Sheet. Clicking a row did nothing
    // visible, and the loaded project drifted away from the row the operator
    // believed they had opened — which is how a bottom-nav title edit renamed
    // a DIFFERENT quote (operator report 2026-07-30). The guard itself is
    // fixed in use-app-routing.ts; this site removes the stale pin.
    //
    // The impl is rebuilt every render (so every value and function it closes
    // over is fresh) behind a permanently stable wrapper. There is no dep array
    // left to get wrong. Same ref-shadowed idiom as saveCurrentProjectRef above.
    const openQuoteForProjectImpl = (projectSummary) => {
        if (!projectSummary?.id) return;
        // A3 review: the job a Quote Run is driving is shown where the run
        // has it. Never the editor branch below (its writes would reach the
        // running job), never the refusal, never the pending Stage 5 open.
        if (showQuoteRunJobInsteadOfOpening(projectSummary.id)) return;
        const sameProjectHasCompletedQuote = projectSummary.id === currentProjectId
            && projectHasCompletedQuote({
                quoteData,
                scope,
                currentStage: stageRef.current,
                completedStages,
            });
        const sameProjectHasStage5Presentation = projectSummary.id === currentProjectId
            && projectHasStage5Presentation({ quoteData, scope });
        // If we're already on the same project with a quote, just open the editor.
        if (sameProjectHasCompletedQuote || sameProjectHasStage5Presentation) {
            // Land the operator FIRST. Every caller of this branch is a
            // non-QuoteMaker page (Run Sheet, Analysis drawer, All Quotes), the
            // modal is gated on `currentPage === 'quotemaker' &&
            // quoteEditorOpen`, and this branch deliberately skips loadProject
            // — so it also skips completeProjectLoad's own navigate and has to
            // do it itself. Without this the click was a permanent no-op: the
            // right project was already loaded, so peek() flipped a flag whose
            // modal could never mount.
            //
            // Order is load-bearing. handleNavigate's session-restore block
            // runs synchronously and can call editor.restoreFromSession({
            // open: false }) plus setStage(<stored stage>) off a stale
            // snapshot. Navigating AFTER peek() would close the modal we just
            // opened; navigating first lets the asserts below win the batch.
            //
            // force: this branch skips loadProject, so isLoadingProjectRef is
            // false and the mid-transition block would otherwise drop the call
            // whenever the click lands inside another navigation's 450ms window
            // (arriving at the Run Sheet, or browser Back onto it, then clicking
            // fast). There is no retry tail here — the writes below run anyway —
            // so a dropped navigate leaves stage 5 and an open editor on a page
            // that cannot mount the modal. It must land.
            handleNavigate('quotemaker', { force: true });
            if (isCurrentProjectQuoteGenerationInFlight()) {
                // The prior complete quote remains a useful delivery snapshot
                // while its replacement is authored. Open it read-only through
                // the no-merge/no-save view seam; the shared write lock keeps
                // edits from crossing into the replacement run while Print,
                // PDF and Email remain available for the visible snapshot.
                editor.peek();
                setStage(5);
                setToast({
                    message: 'Showing the complete saved Stage 5 quote while its replacement continues. Print, PDF and Email use the version currently visible; editing remains locked until generation finishes.',
                    type: 'info',
                });
                return;
            }
            if (quoteData) {
                // The already-loaded Project is canonical. Rebuild only the
                // view-only Stage 5 presentation shadow so opening a Run Sheet
                // row cannot reallocate scope, reprocess the hero, or dispatch
                // a project/media save. Real Stage 4 edits were persisted on
                // navigation away and are merged here for display only.
                return editor.openForNavigation(quoteData, {
                    transitionToStage5: true,
                    persistStage: false,
                    viewOnlyNavigation: true,
                });
            }
            // Same-project view-only open after caller-managed save.
            editor.peek();
            return;
        }
        // Decision D1: a Quote Run refuses the open (and shows its message).
        // Ask before arming the pending Stage 5 open: a refused load would
        // leave it armed, and the queue's next job would open in the editor.
        if (refuseProjectChangeDuringQuoteRun('open', projectSummary.id)) return;
        pendingOpenQuoteRef.current = true;
        loadProject(projectSummary);
    };
    const openQuoteForProjectRef = useRef(openQuoteForProjectImpl);
    openQuoteForProjectRef.current = openQuoteForProjectImpl;
    const openQuoteForProject = useCallback((projectSummary) => (
        openQuoteForProjectRef.current(projectSummary)
    ), []);

    // ── Jobs: Revise, Split and the open quote's Job (migration 120) ─────────
    // Every dependency is a getter or a ref read at call time, never a captured
    // value: Revise and Split end in a reload through loadProjectUnchecked, and
    // a memoised closure here would pin a stale load chain (CLAUDE.md HARD RULE
    // "a memoised callback must never pin the routing/load chain").
    // A New revision saves straight after adding its files; the save reads the
    // committed state, so the add waits for React to commit them first.
    const waitForRevisionFilesCommit = useWaitForCommit();
    const quoteJobActions = useQuoteJobActions({
        getCurrentProjectId: () => currentProjectIdRef.current,
        getSaveToken: () => baseUpdatedAtRef.current,
        hasUnsavedChanges: () => hasUnsavedChangesRef.current,
        saveCurrentProject: () => saveCurrentProject(),
        getProject: (id) => getProject(id),
        // Decision D1, A3 review round 2: Revise and Split refuse before the
        // server while a Quote Run is active, because the reload below would
        // be refused after the server had already made the change. A ref read
        // at call time, never a captured value.
        isQuoteRunActive: () => batchActiveRef.current === true,
        // The operator was in the Stage 5 editor; land them back in it on the
        // revised quote or the new half, not on the Stage 4 scope builder.
        reloadProject: (project) => {
            // Decision D1: a Quote Run refuses the reload. Never arm the
            // pending Stage 5 open for it, or the queue's next job inherits it.
            if (refuseProjectChangeDuringQuoteRun('open', project?.id)) {
                return Promise.reject(createQuoteRunActiveError('open', project?.id));
            }
            pendingOpenQuoteRef.current = true;
            return loadProjectUnchecked(project);
        },
        refreshProjects: () => refreshProjectsFromServer(),
        notify: ({ kind, message }) => {
            // Both setters are loosely typed useState(null) in this file.
            if (kind === 'error') setToast({ type: 'error', message } as any);
            else setTopNotice({ kind, message } as any);
        },
        newId: () => crypto.randomUUID(),
        fingerprintScope: (items) => scopeFingerprintForQuoteAuthoring(items),
        // A New revision's PDFs and photos, after the reload, through the one
        // Stage 5 reference-file path (JOBS-UX-SPEC 3.3 step 6). A plain
        // per-render arrow read through depsRef, never a pinned closure.
        // runRevise saves right after this resolves, so it resolves only once
        // React has committed the added photos and documents.
        addRevisionFiles: async (files, origin) => {
            const outcome = await handleAddReferenceFiles(files, origin);
            await waitForRevisionFilesCommit();
            return outcome;
        },
    }, { userKey: user?.tenantId ? `${user.tenantId}:${user?.id || ''}` : null });
    const currentProjectSummaryForJobs = currentProjectId
        ? (projects || []).find((project) => project?.id === currentProjectId) || null
        : null;
    const openQuoteJobState = useOpenQuoteJob({
        enabled: quoteJobActions.enabled,
        projectId: currentProjectId,
        quoteJobId: currentProjectSummaryForJobs?.quoteJobId || null,
        refreshKey: currentProjectSummaryForJobs?.updatedAt ? String(currentProjectSummaryForJobs.updatedAt) : null,
    });
    const quoteJobEditorProp = buildQuoteJobEditorProp({
        enabled: quoteJobActions.enabled,
        busy: quoteJobActions.busy,
        currentProjectId,
        quoteData,
        summary: currentProjectSummaryForJobs,
        jobView: openQuoteJobState.jobView,
        revisions: openQuoteJobState.revisions,
        formatLabel: formatQuoteNumberLabel,
        onRevise: (input) => quoteJobActions.revise(input),
        onSplit: (input) => quoteJobActions.split({ ...input, scope: scopeRef.current || [] }),
        onOpenQuote: (projectId) => {
            const summary = (projects || []).find((project) => project?.id === projectId);
            openQuoteForProject(summary || { id: projectId });
        },
        loadRevision: (id) => getQuoteRevision(id),
    });
    // Import (C1): the new quote is not in `projects` yet, so refresh the list
    // and open it through the guarded load. Per-render impl behind a stable ref
    // (CLAUDE.md: a memoised callback must never pin the routing/load chain).
    const openImportedQuoteImpl = (projectId: string) => {
        if (!projectId) return;
        void refreshProjectsFromServer();
        openQuoteForProject({ id: projectId });
    };
    const openImportedQuoteRef = useRef(openImportedQuoteImpl);
    openImportedQuoteRef.current = openImportedQuoteImpl;
    const openImportedQuote = useCallback((projectId: string) => openImportedQuoteRef.current(projectId), []);

    // Row-open for the LIST surfaces (Run Sheet, Analysis drawer, Tasks, All
    // Quotes). A rejected open used to be a silent dead click, visually
    // identical to the swallowed-navigation bug above: loadProject rethrows a
    // failed getProject (use-project-persistence.ts, the non-batch branch) and
    // every list page discards the promise its row handler returns, so a 404 or
    // a dropped backend left no toast, no console line the operator would see,
    // and no page change.
    //
    // The catch belongs HERE and not inside loadProject, because loadProject's
    // rejecting contract is load-bearing for two other callers: useAppRouting's
    // session-resume fallback branches on it to clear the stale receipt and
    // reset the form, and the intake "Open in QuoteMaker" bridge branches on it
    // to toast the failing project id. Swallowing it there would break both.
    //
    // Plain per-render arrow on purpose — no dep array to go stale, and each
    // list page's own row handler already lists this prop in its deps.
    const openProjectFromList = (projectSummary) => (
        Promise.resolve(loadProject(projectSummary)).catch((err) => {
            // A Quote Run refused the open and has shown its own message
            // (decision D1); an error toast here would contradict it.
            if (isQuoteRunActiveError(err)) return;
            // Auth failure ≠ project failure (reload-resume race, 2026-08-05).
            // When the session has died every open fails the same way, and a
            // per-open error toast + console.error just stacks noise on top of
            // the sticky qm:session-expired toast apiFetch already triggered —
            // which carries the actual remedy (Log in). Warn for the debug
            // trail and let that toast own the messaging.
            if (err?.status === 401 || err?.status === 403) {
                console.warn('[QM] Opening a project from a list surface hit an auth failure (session expired):', err?.message || err);
                return;
            }
            console.error('[QM] Opening a project from a list surface failed:', err);
            const name = projectSummary?.title || projectSummary?.name || 'that project';
            setToast({
                message: `Could not open "${name}": ${err?.message || 'unknown error'}`,
                type: 'error',
                duration: 8000,
            });
        })
    );

    useEffect(() => {
        if (currentPage !== 'quotemaker') return;
        if (!autoOpenLoadedQuoteProjectRef.current) return;
        if (autoOpenLoadedQuoteProjectRef.current !== currentProjectId) return;
        if (Number(stage) !== 5 || !(
            projectHasCompletedQuote({
                currentStage: stage,
                completedStages,
                quoteData,
                scope,
            })
            || projectHasStage5Presentation({ quoteData, scope })
        )) return;

        autoOpenLoadedQuoteProjectRef.current = null;
        projectLoadGuardUntilRef.current = Date.now() + 2000;
        // Auto-open-after-load: project freshly hydrated at Stage 5 → restore the modal.
        editor.peek();
    }, [currentPage, currentProjectId, stage, completedStages, quoteData, scope]);

    // Delete a project - also cleans up associated browser-local artifacts.
    // The delete CHAIN, extracted from handleDeleteProject so the Run Sheet's
    // bulk delete reuses it verbatim instead of a second copy that drifts out of
    // step. It reports via a return value rather than a toast: a 12-project bulk
    // run with three failures should raise ONE summary, not three stacked
    // 8-second toasts. Each caller owns its own reporting and its own confirm.
    const deleteProjectNow = async (id, { broadcastChange = true } = {}) => {
        const project = projects.find(p => p.id === id);
        const label = project?.title || project?.address || 'Unknown';
        // Retire every re-runnable queue row BEFORE deleting. A live
        // processing row blocks the delete because its trailing save
        // could otherwise recreate the project after the backend DELETE.
        try {
            const { retireProjectJobs } = await import('./services/batch-queue');
            await retireProjectJobs(id, { reason: 'project-deleted' });
        } catch (err) {
            return {
                ok: false,
                label,
                reason: err?.code === 'PROJECT_DELETE_QUEUE_ACTIVE'
                    ? 'still processing — stop or finish its queue job first'
                    : `queue work could not be retired: ${err?.message || 'unknown error'}`,
            };
        }
        // ── Primary delete (PostgreSQL cascades to project_learnings, scope_items, project_files) ──
        try {
            await deleteProject(id);
        } catch (err) {
            return {
                ok: false,
                label,
                reason: `${err?.message || 'unknown error'} (its queue work remains safely stopped; retry when the backend is available)`,
            };
        }

        // ── The row leaves the list HERE, the moment the delete is durable ──
        //
        // Operator report 2026-08-20: "even after I delete, the row persists,
        // making me want to click delete again" — and clicking delete twice on
        // a list that has not moved is how the wrong thing gets deleted.
        //
        // The delete above has already succeeded on the backend, so the row is
        // gone as a FACT; everything below this point is housekeeping (local
        // artifacts), wrapped in try/catch and marked non-fatal. Removing the
        // row after that chain made the UI wait on it, and the wait is longer
        // on the cloud because the round trips are longer there.
        //
        // So the list obeys the durable delete immediately, and the
        // housekeeping runs after. If a housekeeping step fails the row still
        // stays gone, which is correct: the project IS deleted.
        setProjects(prev => prev.filter(p => p.id !== id));
        // A cloud mirror of this job waiting out a quota refusal would send
        // its retry for a project that no longer exists (s3-sync.ts).
        requestCloudMirrorRetryCancel(id);
        // A3: notify other tabs so their lists drop the deleted project. Bulk
        // deletion suppresses these per-row signals and sends one aggregate
        // after the loop; otherwise 485 deletes become 485 multi-MB refreshes
        // in every other open tab.
        if (broadcastChange) {
            broadcastProjectsChanged({ kind: 'delete', projectIds: [id] });
        }
        // Reset all state if deleting the currently active project -
        // without this, the deleted project's data stays on screen and
        // could accidentally be re-saved as a new project.
        if (id === currentProjectId) {
            suppressUnsavedRef.current = true;
            // A Quote Run refuses the reset and shows its message (decision
            // D1). The durable delete above stands, and the local cleanup
            // below must still run.
            try { resetForm(); } catch (err) { ignoreQuoteRunActive(err); }
        }

        // ── Clean up every browser-local artifact owned by this project ──
        // The backend delete is already durable, so this stays non-fatal. The
        // unified sweep prevents long-running tabs accumulating photo, video,
        // document and upload-queue bytes after projects leave the history.
        try {
            const { cleanupProjectArtifacts } = await import('./services/project-artifact-cleanup');
            const cleanup = await cleanupProjectArtifacts(id);
            if (cleanup.counts.total > 0) {
                console.log(`[QM] Cleaned up ${cleanup.counts.total} local artifact(s) for deleted project ${id}`, cleanup.counts);
            }
            if (!cleanup.ok) {
                console.warn('[QM] Project artifact cleanup partially failed (non-fatal):', cleanup.failures);
            }
        } catch (err) {
            console.warn('[QM] Project artifact cleanup failed (non-fatal):', err?.message);
        }

        ActivityLogService.log('delete', { type: 'project', description: `Deleted project: ${label}` });
        return { ok: true, label };
    };

    const handleDeleteProject = (id) => {
        const project = projects.find(p => p.id === id);
        // Copy pass 2026-07-31 (operator disliked the wording). Sentence case,
        // and the title names the ACTION while the message names the thing and
        // what goes with it — Title Case questions and a bare "Delete" verb read
        // like a system alert rather than a decision the operator is making.
        // The button repeats the noun so it still makes sense when it is the
        // only thing being read.
        const label = project?.title || project?.address || 'This project';
        showConfirm({
            title: 'Delete this project?',
            message: `${label} will be removed from your history, along with its quote, scope, photos and documents.`,
            confirmText: 'Delete project',
            cancelText: 'Keep it',
            confirmColor: 'red',
            icon: 'danger',
            onConfirm: async () => {
                const res = await deleteProjectNow(id);
                if (!res.ok) {
                    setToast({ type: 'error', message: `Project was not deleted: ${res.reason}`, duration: 8000 });
                }
            },
        });
    };

    // Bulk delete for the Run Sheet's selection (operator 2026-07-30).
    // ONE confirm for the whole set — a per-project confirm would defeat the
    // point of selecting them together — and the confirm NAMES what is going,
    // because "delete 9 projects" is not something to agree to blind.
    const handleDeleteProjects = (ids, { totalValue = 0 } = {}) => {
        const list = (Array.isArray(ids) ? ids : []).filter(Boolean);
        if (!list.length) return;
        if (list.length === 1) return handleDeleteProject(list[0]);

        const named = list
            .map(id => projects.find(p => p.id === id))
            .map(p => p?.title || p?.address || 'Untitled project');
        // Name the first few in full, then count the rest. A 40-project list
        // would push the buttons off a laptop screen.
        const preview = named.slice(0, 6).map(n => `• ${n}`).join('\n');
        const more = named.length > 6 ? `\n…and ${named.length - 6} more` : '';

        // TWO warnings for a bulk delete (operator 2026-07-31). The first names
        // the jobs so the set can be checked; the second states the size and the
        // money, which is the fact that actually changes minds. A single click
        // should not be able to remove nine jobs, and the two dialogs
        // deliberately ask different questions rather than the same one twice.
        // totalValue arrives from the Run Sheet's own selection total. Deriving
        // it here from the summary rows produced 0 while the bulk bar showed
        // $100k for the same three projects, because the sheet values a quote
        // through quoteValueExGst and the raw quote_total column is not the same
        // number. Two figures for one set is worse than no figure.
        const money = totalValue >= 1
            ? ` worth ${totalValue.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 })} of quoted work`
            : '';

        showConfirm({
            title: `Delete these ${list.length} projects?`,
            message: `They will be removed from your history, along with their quotes, scope, photos and documents:\n\n${preview}${more}`,
            confirmText: 'Continue',
            cancelText: 'Keep them',
            confirmColor: 'red',
            icon: 'danger',
            // Deferred by a tick, and it MUST be. ConfirmModal's confirm button
            // is `onClick={() => { onConfirm(); onClose(); }}`
            // (ConfirmModal.tsx:206), so opening the second dialog synchronously
            // inside onConfirm sets isOpen:true and then the parent's own
            // onClose sets it back to false in the same batch — the second
            // warning renders for zero frames and the operator sees the first
            // dialog simply vanish. Verified in a browser: the chain silently
            // did nothing. Deferring lets the close land first.
            onConfirm: () => setTimeout(() => showConfirm({
                title: 'Delete them for good?',
                message: `You are about to permanently delete ${list.length} projects${money}. Their quotes, photos and documents go with them.`,
                confirmText: `Yes, delete ${list.length}`,
                cancelText: 'Keep them',
                confirmColor: 'red',
                icon: 'danger',
                onConfirm: async () => {
                // Sequential, not Promise.all: each delete mutates shared React
                // state and retires queue rows, and a parallel burst across a
                // large selection is exactly how you get interleaved setProjects
                // races and a half-updated list.
                const failures = [];
                const successfulIds = [];
                let done = 0;
                for (const id of list) {
                    const res = await deleteProjectNow(id, { broadcastChange: false });
                    if (res.ok) {
                        done += 1;
                        successfulIds.push(id);
                    } else failures.push(res);
                }
                if (successfulIds.length > 0) {
                    broadcastProjectsChanged({ kind: 'delete', projectIds: successfulIds });
                }
                if (!failures.length) {
                    setToast({ type: 'success', message: `Deleted ${done} project${done === 1 ? '' : 's'}.`, duration: 5000 });
                } else {
                    const detail = failures.slice(0, 3).map(f => `${f.label} (${f.reason})`).join('; ');
                    setToast({
                        type: failures.length === list.length ? 'error' : 'warning',
                        message: `Deleted ${done} of ${list.length}. Failed: ${detail}${failures.length > 3 ? `; and ${failures.length - 3} more` : ''}`,
                        duration: 10000,
                    });
                }
                },
            }), 0),
        });
    };

    // Duplicate a project
    const handleDuplicateProject = async (projectSummary) => {
        let sourceProjectId = projectSummary.id;
        let newId = null;
        let duplicated = null;
        let finalizeDuplicate = null;
        let duplicateSaveStarted = false;

        try {

        // Re-hydrate the FULL record before duplicating. The project-history list
        // (AllQuotesPage) hands us a LEAN summary — GET /projects?summary=true drops
        // photos, quote_data, wms, costing and the full scope to keep the list
        // payload small. Spreading that summary straight into the copy would
        // silently create a gutted duplicate (no quote, no photos, lean scope).
        // Mirror loadProjectUnchecked: fetch by id via the backend-proxied path,
        // fall back to raw Dexie for projects that exist locally but were never
        // pushed, and only as a last resort keep the object we were handed.
        let project = projectSummary;
        try {
            const full = await getProject(projectSummary.id);
            if (full && typeof full === 'object') project = full;
        } catch {
            try {
                const dbMod = await import('./services/db');
                const raw = await dbMod.default.projects.get(projectSummary.id);
                if (raw) project = raw;
            } catch { /* keep the passed summary as a last resort */ }
        }

        // Clone the source project's photo blobs into NEW blob rows owned by the
        // duplicate. photo_blobs ids are a global Dexie auto-increment, so
        // spreading `...project` would otherwise make the copy SHARE (and, once
        // the source is deleted, LOSE) the original's photo bytes: the
        // cross-project "wrong / missing photos on reopen" hazard. cloneProjectBlobs
        // remaps each photo to a fresh, target-owned blob row.
        sourceProjectId = project.id || projectSummary.id;
        // A response can be lost after PostgreSQL commits. Keep one stable
        // target UUID until success or a provable pre-commit refusal so retry
        // reconciles/replays that same aggregate instead of minting a hidden
        // second copy.
        newId = claimProjectDuplicateAttempt(sourceProjectId);
        const now = new Date().toISOString();
        const clonedPhotos = Array.isArray(project.photos)
            ? await cloneProjectBlobs(project.photos, sourceProjectId, newId)
            : project.photos;
        // Documents and videos use the same global-id/owner contract. Remap
        // their local rows too, and let the clone helpers discard source-bound
        // Blob URLs so opening the copy mints target-owned previews.
        const clonedDocuments = Array.isArray(project.documents)
            ? await cloneProjectDocumentBlobs(project.documents, sourceProjectId, newId)
            : project.documents;
        const clonedVideos = Array.isArray(project.videos)
            ? await cloneProjectVideoBlobs(project.videos, sourceProjectId, newId)
            : project.videos;

        // A duplicate is a brand-new quote, so it must NOT carry the source's
        // unique identifiers. The projects table has two partial unique indexes
        // — projects(dexie_id) and projects(tenant_id, qm_quote_no) — so
        // spreading the source's dexie_id / qm_quote_no into the copy makes the
        // INSERT collide ("Project with this dexie_id already exists" — the
        // reported "can't duplicate" failure; projects.save sends the spread
        // dexie_id which the backend upserts on, ahead of the fresh dexieId:id).
        // Clear both casings: dexie_id is re-stamped fresh from the new id by
        // the save (data-service-api sets dexieId:payload.id); qm_quote_no stays
        // unset until the copy is actually sent, when it allocates its own.
        // Also strip the QM quote number mirrored inside the quote blob's meta
        // so the copy neither displays the source's number nor short-circuits
        // its own allocation (allocateQmQuoteNo skips when meta.qmQuoteNo is set).
        const stripBlobQuoteNo = (blob) => {
            if (!blob || typeof blob !== 'object' || !blob.meta || typeof blob.meta !== 'object') return blob;
            // A copy is a draft: the source's completion receipt and readiness
            // review describe the source's issued quote, not the copy.
            const { qmQuoteNo, qm_quote_no, completion, readinessReview, ...metaRest } = blob.meta;
            return { ...blob, meta: metaRest };
        };
        duplicated = {
            ...project,
            id: newId,
            photos: clonedPhotos,
            documents: clonedDocuments,
            videos: clonedVideos,
            created_at: now,
            updated_at: now,
            title: `${project.title || project.address || 'Project'} (Copy)`,
            projectFolder: null,
            pdfGenerated: false,
            pdfGeneratedAt: null,
            emailSent: false,
            emailSentAt: null,
            status: 'draft',
            // Drop the source's unique identifiers so the copy gets its own.
            dexie_id: undefined,
            dexieId: undefined,
            qm_quote_no: undefined,
            qmQuoteNo: undefined,
            quoteData: stripBlobQuoteNo(project.quoteData),
            quote_data: stripBlobQuoteNo(project.quote_data),
            // Saved costing is kept, minus the source's QM number.
            ...(project.costingSheet
                ? { costingSheet: withoutCostingReceiptQmQuoteNo(project.costingSheet) }
                : {}),
            ...(project.costing_sheet
                ? { costing_sheet: withoutCostingReceiptQmQuoteNo(project.costing_sheet) }
                : {}),
        };

            finalizeDuplicate = async (savedDuplicate) => {
                // Cloud saves can add durable document/media receipts while
                // intentionally stripping browser-only recovery fields. Open
                // the canonical receipt merged with target-owned local metadata.
                const duplicateForOpen = savedDuplicate && typeof savedDuplicate === 'object'
                    ? await mergeProjectSaveResultForDexieOwned(duplicated, savedDuplicate)
                    : duplicated;
                clearProjectDuplicateAttempt(sourceProjectId, newId);
                setProjects(prev => [duplicateForOpen, ...prev.filter(p => p.id !== newId)]);
                broadcastProjectsChanged({ kind: 'duplicate', projectIds: [newId] });
                // Decision D1: a Quote Run refuses the open (and shows its
                // message). The copy is saved either way; say only what happened.
                const openRefused = refuseProjectChangeDuringQuoteRun('open', newId);
                if (!openRefused) loadProject(duplicateForOpen);
                setTopNotice({
                    kind: 'success',
                    message: openRefused
                        ? `Quote duplicated as "${duplicateForOpen.title}". Open it once the Quote Run has stopped.`
                        : `Quote duplicated. Opened the copy: "${duplicateForOpen.title}".`,
                });
                return duplicateForOpen;
            };

            duplicateSaveStarted = true;
            const savedDuplicate = await DataService.projects.duplicate(
                sourceProjectId,
                duplicated,
                { expectedUpdatedAt: project.updatedAt || project.updated_at },
            );
            await finalizeDuplicate(savedDuplicate);
        } catch (err) {
            let recovery = { disposition: 'uncertain' };
            if (newId) {
                recovery = await reconcileProjectDuplicateFailure({
                    sourceProjectId,
                    targetProjectId: newId,
                    duplicateSaveStarted,
                    error: err,
                    lookupTarget: targetId => DataService.projects.getById(targetId),
                    cleanupTarget: async (targetId) => {
                        const { cleanupProjectArtifacts } = await import('./services/project-artifact-cleanup');
                        return cleanupProjectArtifacts(targetId);
                    },
                    clearAttempt: clearProjectDuplicateAttempt,
                });
            }
            if (recovery.disposition === 'committed' && finalizeDuplicate) {
                console.warn('[QM] Duplicate POST response was lost; reconciled the committed target by UUID.', { projectId: newId });
                await finalizeDuplicate(recovery.project);
                return;
            }
            if (recovery.disposition === 'rolled_back' && recovery.cleanup?.ok === false) {
                console.warn('[QM] Duplicate rollback partially failed:', recovery.cleanup.failures);
            } else if (recovery.disposition === 'uncertain' && newId) {
                console.warn('[QM] Duplicate save outcome is uncertain; retained the stable target id and target-owned local media for reconciliation.', { projectId: newId });
            }
            console.error('Failed to duplicate project:', err);
            showConfirm({ title: 'Duplication Failed', message: 'Could not duplicate the project. Please try again.', confirmText: 'OK', cancelText: null, confirmColor: 'red', icon: 'warning', onConfirm: () => {} });
        }
    };

    // Update a project's quote outcome status (won/lost/pending)
    const handleUpdateQuoteStatus = async (projectId, quoteStatus) => {
        try {
            const persisted = await DataService.projects.updateQuoteStatus(projectId, quoteStatus);
            // This route bumps updated_at server-side; adopt the new version so
            // marking the OPEN project won/lost does not spend its save token.
            adoptServerProjectVersion(projectId, persisted);
        } catch (err) {
            console.warn('[QM] Failed to persist quote status:', err.message);
        }
        // Update local state immediately regardless of backend result
        setProjects(prev => prev.map(p => p.id === projectId ? { ...p, quoteStatus } : p));
        // A3: notify other tabs so their Dashboard / AllQuotes views refresh.
        broadcastProjectsChanged({ kind: 'status', projectIds: [projectId] });
    };

    // Analysis evidence-drawer inline outcomes (SPEC-analysis-page S4). The
    // drawer itself performs the durable write (recordOutcome) and the
    // cross-tab broadcast; broadcastProjectsChanged deliberately ignores its
    // own tab, so without this echo THIS tab's `projects` array would keep
    // contradicting the decision just recorded until a reload. Mirrors the
    // setProjects patch shape of handleMarkWon / handleMarkLost. It is ONLY
    // the state echo: the loaded-project enrichment chain (CRM activity log,
    // work-order draft) does NOT
    // fire from the drawer — that chain spends AI money and generates work
    // orders, which a rapid list-triage surface must not trigger implicitly.
    // Deliberate scope decision, recorded in the analysis-upgrade-s1 handoff.
    const handleAnalysisOutcomeRecorded = (projectId, status, persisted) => {
        // PATCH /:id/outcome is a CONTENT write — it assigns status/quote_status,
        // which an ordinary full save also assigns — so it advances the row's
        // version. If the project the drawer just marked is the one open here,
        // adopt that version, or this tab holds a spent save token and its next
        // ordinary save is refused as somebody else's edit. See the 'won' path.
        // The call is identity-guarded, so a drawer action on another project
        // is a no-op.
        adoptServerProjectVersion(projectId, persisted);
        const now = persisted?.outcomeAt || persisted?.wonAt || persisted?.lostAt || new Date().toISOString();
        const updatedAt = persisted?.updatedAt || now;
        setProjects(prev => prev.map(p => p.id === projectId ? {
            ...p,
            status,
            quoteStatus: status,
            wonAt: status === 'won' ? now : null,
            lostAt: status === 'lost' ? now : null,
            lostReason: status === 'lost' ? (persisted?.lostReason || '') : null,
            updatedAt,
            updated_at: updatedAt,
            aiContext: { ...(p.aiContext || {}), ...(persisted?.aiContext || {}) },
        } : p));
    };

    // View a project's quote in the editor
    const handleViewProject = (project) => {
        // Clear any stored QM session state so the sessionRestore in
        // handleNavigate('quotemaker') doesn't override the stage that
        // completeProjectLoad will set from this project's stored
        // currentStage (including the legacy-bump soft-migration).
        try { if (QM_STATE_KEY) sessionStorage.removeItem(QM_STATE_KEY); } catch {}
        if (project.quoteData) {
            loadProject(project);
            // After loadProject hydrates state, view-only open via peek().
            editor.peek();
        } else {
            // No quote yet - just load the project at its current stage
            loadProject(project);
        }
    };

    // ============================================
    // POST-SEND ACTIONS - Costings sent, Mark Won/Lost, Follow-up
    // ============================================
    // All handlers below use functional setProjects to avoid stale-snapshot races,
    // and ALL CRM activity logs read address from the looked-up project, not outer formData.

    // BQ-CP-5: appHandlers ref kept fresh on every render so the
    // PipelineRunner (instantiated in FileExplorer) always pulls the LATEST
    // closures. React re-creates closures every render; capturing them once
    // at runner-construction time would freeze formData/scope reads.
    // Direct ref assignment (not useEffect) so the ref is current
    // synchronously during render — the runner can pull fresh handlers
    // even on the same tick a render commits.
    //
    // batchActiveRef tracks whether the queue runner is driving a project
    // through Stage 1→5. The batch runner invokes its own Stage 5 authoring
    // path; the attended deterministic-projection effect exits while this
    // owner is active so it cannot become a second quote producer.
    // (T5 slice 7: batchActiveRef decl RELOCATED above the
    // useProjectPersistence instantiation — the hook's save/load bodies
    // read it. The comment above still describes its semantics.)
    const appHandlersRef = useRef(null);
    appHandlersRef.current = {
        // An app-level toast, for a message about a page the operator may have
        // left: the Stage 1 covering-email question withdrawn because its import
        // stopped (Stage 1's own toasts unmount with it).
        toast: (message, type = 'warning') => setToast({ message, type }),
        // v5.37.21 audit fix: expose saveCurrentProject so child components
        // (e.g. IntakeImportBanner's Detach flow) can persist state changes
        // immediately without waiting for the next normal save cycle.
        // Fire-and-forget — caller doesn't need to await.
        saveCurrentProject: (overrides) => {
            try {
                return saveCurrentProjectRef.current?.(overrides);
            } catch (err) {
                console.warn('[appHandlers] saveCurrentProject failed:', err?.message || err);
                return null;
            }
        },
        // Batch-safe loadProject: bypass the unsaved-changes prompt by
        // calling the unchecked path directly. Operator clicked Process
        // Queue intentionally; mid-batch project switches are expected.
        // v5.19.0 (DEC-BQ-022): also bypass the reconnect-folder modal
        // by passing batchMode:true. The modal needs a user click which
        // never arrives in batch mode → would hang the orchestrator.
        loadProject: (projectSummary, options = {}) => loadProjectUnchecked(projectSummary, { batchMode: true, ...options }),
        // Interactive openProject: checked loader for user clicks (e.g. "Open the draft")
        openProject: (projectSummary, options = {}) => loadProject(projectSummary, options),
        // Quote Run renews its server claim with the version this tab's own
        // saves produced. Only for the open Project, and never while a project
        // write holds the lock: its new version is not adopted yet.
        getProjectSaveToken: (projectId) => {
            if (!projectId || String(currentProjectIdRef.current || '') !== String(projectId)) return null;
            if (tabProjectWriteLock.isBusy()) return null;
            return baseUpdatedAtRef.current || null;
        },
        prepareStage1Evidence,
        // Quote Run schedules the same durable Project Summary owner used by
        // attended Stage 1. The runner re-reads the saved summary/form pair, so
        // no React render timing can substitute a stale pre-summary closure.
        runProjectSummary: (opts = {}) => {
            const expectedProjectId = opts.expectedProjectId ?? null;
            if (expectedProjectId
                && String(currentProjectIdRef.current || '') !== String(expectedProjectId)) {
                const error = new Error('Project Summary stopped because Quote Run changed projects before dispatch.');
                error.code = 'PROJECT_SUMMARY_PROJECT_CHANGED';
                error.retryable = true;
                throw error;
            }
            return runProjectSummary({
                ...opts,
                silent: true,
                autoAdvanceOnReady: false,
                automatic: false,
            });
        },
        currentProjectIdRef,
        // v5.36.30: expose handleNavigate so Stage 1 components can route
        // back to /intake (or any other page) via the same auth-aware path
        // App.jsx itself uses. Used by IntakeContextChip to jump from a
        // QM project back to its source intake job.
        navigate: (page, opts) => handleNavigate(page, opts),
        // Quote Run builder-decision stops load the project first, then use
        // the same guarded stage navigation as the stage progress controls.
        // The ref avoids freezing this early appHandlers object onto a stale
        // project closure while preserving the existing navigation owner.
        navigateToQuoteStage: (targetStage) => handleQuoteStageNavigateRef.current(targetStage),
        // FileExplorer owns explicit Stage 3/4/5 scheduling while batch work
        // is active. Every stage still calls the same App handlers as attended
        // QuoteMaker; the queue only replaces the button timing.
        setBatchActive: (active) => {
            batchActiveRef.current = !!active;
            pipelineOwnerRef.current = active ? 'batch-runner' : 'ui-auto';
            setQuoteRunDriving(!!active);
            // A new run has no job and no stop yet; an ended run has neither.
            quoteRunProjectIdRef.current = null;
            quoteRunStopRequestedRef.current = false;
        },
        // The Quote Run sheet's run bar "Stop the run" (A3 review round 3) is
        // the STOP pill: the queue stop event (which marks the run as
        // stopping, so a retried open says so instead of asking again) and
        // then the job's AI stage, so the run ends promptly. The run bar
        // settles the queue stop itself, hence serviceStopOwnedByCaller. Only
        // while a run is active: otherwise cancelAiPipeline would abort the
        // work on screen. Returns false so the run bar raises the event alone.
        stopQuoteRun: (opts = {}) => {
            if (!batchActiveRef.current) return false;
            cancelAiPipeline({ ...opts, serviceStopOwnedByCaller: true });
            return true;
        },
    };

    // Called by CostingsModal after the user clicks "Email Admin"
    const handleCostingsSent = async (info) => executeCostingsSent(info, {
        currentProjectId,
        projects,
        setProjects,
        adoptServerProjectVersion,
        setToast,
        apiKey,
        model,
    });

    // Mark project as Won - updates project + logs CRM activity + propagates to linked Gold Standards
    const handleMarkWon = async () => {
        // Won is an outcome, not proof
        return executeMarkWon({
            currentProjectId,
            projects,
            setProjects,
            adoptServerProjectVersion,
            setToast,
            apiKey,
            model,
        });
    };

    // Mark project as Lost
    const handleMarkLost = async (reason) => executeMarkLost(reason, {
        currentProjectId,
        projects,
        setProjects,
        adoptServerProjectVersion,
        setToast,
        apiKey,
        model,
    });

    // Set a follow-up reminder - creates a CRM activity with due_date AND
    // updates the project's nextFollowUp field so UI surfaces the reminder
    const handleSetFollowUp = async (dueDate, note) => executeSetFollowUp(dueDate, note, {
        currentProjectId,
        projects,
        setProjects,
        adoptServerProjectVersion,
        setToast,
        apiKey,
        model,
    });

    // Derive admin email for current project - primary contact OR default
    const getAdminEmailForProject = () => {
        const proj = projects.find(p => p.id === currentProjectId);
        const projAdmin = proj?.adminEmail || proj?.formData?.adminEmail;
        if (projAdmin) return projAdmin;
        // Fall back to a sensible default - handled inside CostingsModal
        return null;
    };

    // ============================================
    // SAVE TRIGGERS
    // ============================================

    // One Save press (the nav SAVE disk and Ctrl+S). While QuoteMaker's server
    // is not answering, the save keeps the edits on this device and says
    // nothing itself, because the server banner owns the status. With the
    // banner dismissed, the press used to change nothing on screen ("cant
    // save"). It now answers, and never leaves an uncaught rejection.
    // saveCurrentProject is the stable ref wrapper, so this pins nothing.
    //
    // With the Stage 5 editor up, Save is the editor's own (C17): the plain save below sends the parent's copy of the quote, which
    // does not have what is typed or edited in the editor. The editor's Save also answers for itself (the outage notice, the
    // save-error banner), so this does not add a second message. It never rejects.
    const handleManualProjectSave = useCallback(() => {
        const saveWithOutcome = liveQuoteEditorActions()?.saveWithOutcome;
        if (typeof saveWithOutcome === 'function') {
            return saveWithOutcome().then((outcome) => outcome.ok === true, () => false);
        }
        return runManualSave(
            () => saveCurrentProject(),
            (message) => setToast({ message, type: 'warning', duration: 8000 }),
        );
    }, [saveCurrentProject, liveQuoteEditorActions]);

    // Ctrl+S / Cmd+S to save project
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                if (currentPage === 'quotemaker') {
                    if (isCurrentProjectQuoteGenerationInFlight()) {
                        // A warning, not info: the notification policy hides
                        // info, and Full AI's background author holds this
                        // after every run (DEC-098).
                        setToast({
                            message: 'A quote is being written for this job, so Save waits until it is finished. Press STOP to stop it and save now.',
                            type: 'warning',
                        });
                        return;
                    }
                    handleManualProjectSave();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentPage, handleManualProjectSave, isCurrentProjectQuoteGenerationInFlight]);

    // Warn on browser close if unsaved changes - guards ALL editable pages, not just quotemaker
    useEffect(() => {
        const handler = (e) => {
            // The editor's own unsaved work is read NOW, from its refs: its state-based flag lags one render, and text typed a
            // moment ago is not in any state yet (C18).
            if (hasUnsavedChanges || settingsDirtyCount > 0 || siteVisitsExitRiskCount > 0
                || liveQuoteEditorActions()?.hasUnsavedVisibleWork?.() === true) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasUnsavedChanges, settingsDirtyCount, siteVisitsExitRiskCount, liveQuoteEditorActions]);

    // ─── Stage-navigation seam — TS-060 T5 slice 5 (DEC-COUNCIL-015) ──────
    // canGoNext, openStage5FromCurrentScope, handleNext and handleBack moved
    // VERBATIM into src/hooks/use-stage-navigation.ts. The hook owns no
    // navigation state; state/setters/refs/helpers thread from App via this
    // per-render deps object. The celebration-effect predicate mirror (it
    // CALLS the returned canGoNext),
    // the DEC-COUNCIL-013 quote-editor aliases (openFinalQuoteEditor /
    // closeQuoteEditor) and the QuoteMakerNavBar onSelectStage inline
    // handler all STAY in App. openStage5FromCurrentScope was a HOISTED
    // `function` declaration; the destructured const below exists only from
    // this line — audited: the only reference above this point is the
    // useAppRouting lazy wrapper at the T4 slice-6 routing-seam
    // instantiation (an arrow built at deps-build time, only CALLED from
    // the popstate handler body in use-app-routing.ts after render
    // completes); every other reference is below. Keep this
    // instantiation below every dep declaration (TDZ) — with two sanctioned
    // exceptions threaded as LAZY WRAPPERS because they are the useQuoteEditor
    // aliases declared further down: openFinalQuoteEditor (only called as
    // openStage5FromCurrentScope's terminal delegate) and closeQuoteEditor
    // (only called in handleBack's stage-5 branch). Building the arrows here
    // does not evaluate those bindings; both fire inside handler bodies
    // after render completes.
    //
    // R4 (docs/old-pipeline-removal/PLAN.md): a job written by hand gets its
    // HBCF on its first quote (Peter, 2026-10-23: "This should be done on the
    // first run"). With the old quote writer gone its first quote is the
    // Stage 4 to 5 draft, so opening Stage 5 first adds HBCF through the
    // builder's own HBCF decision when the rule needs it (the decision saves
    // the items, the quote and the form together, and records the first run),
    // or records that the first run needed none. Every job typed in Stage 4
    // has this first run, whatever its workflow mode. Read at the press;
    // returns a promise only when it is adding HBCF.
    const applyFirstRunHbcfBeforeStage5 = () => {
        const costingConfig = currentBusinessPricingSnapshotRef.current?.costingConfig
            || currentBusinessPricingSnapshotRef.current?.costing_config
            || null;
        const structured = projectWMSRef.current?.structured || {};
        const step = firstRunHbcfStepAtStage5(Array.isArray(scopeRef.current) ? scopeRef.current : [], {
            // Only the old Stage 3 run wrote a Work Method; its build decided HBCF.
            itemsBuiltByWorkMethod: !!projectWMSRef.current && typeof projectWMSRef.current === 'object',
            pricingReady: businessPricingSnapshotStatus === 'ready' && !!currentBusinessPricingSnapshotRef.current,
            quoteData: quoteDataRef.current,
            costingConfig,
            formData: formDataRef.current,
            isGstInclusive: scopeGstInclusiveRef.current !== false,
            compliancePosture: structured.compliance_posture || structured.compliancePosture || null,
        });
        // The first run has happened once HBCF was decided, so a later edit
        // that removes it is never undone on the next open.
        const recordDecided = () => {
            const decidedAt = new Date().toISOString();
            const markDecided = (prev) => (prev?.hbcfFirstRunDecidedAt ? prev : { ...prev, hbcfFirstRunDecidedAt: decidedAt });
            formDataRef.current = markDecided(formDataRef.current);
            setFormData(markDecided);
        };
        if (step === 'record') {
            recordDecided();
            return null;
        }
        if (step !== 'add') return null;
        return Promise.resolve()
            .then(() => handleSetBuilderHbcfDecisionRef.current(false))
            .then((applied) => {
                if (applied) {
                    recordDecided();
                    return;
                }
                setToast({ message: 'HBCF could not be added because another change to this job is being saved, so Stage 5 opened without it. Add it from the HBCF control.', type: 'warning' });
            })
            .catch((error) => {
                setToast({ message: error?.message || 'HBCF could not be added, so Stage 5 opened without it.', type: 'error' });
            });
    };

    // B5: the one Stage 1 forward press. NEXT asks for this when the summary is
    // missing or out of date: it runs the summary as the operator's own run,
    // then the effect below presses NEXT again once the run has settled.
    const handleNextRef = useRef(null);
    const stage1ContinuationRef = useRef(null);
    const [stage1ContinuationRevision, setStage1ContinuationRevision] = useState(0);
    const runProjectSummaryThenNext = async () => {
        const request = {
            projectId: currentProjectIdRef.current || null,
            loadGeneration: Number(projectLoadGenerationRef.current),
            // What was on screen when NEXT was pressed, to tell a summary that
            // landed out of date by itself from one an edit made out of date.
            summaryBefore: projectSummaryRef.current,
            evidenceFingerprint: currentProjectSummaryEvidenceFingerprintRef.current,
            settled: false,
        };
        stage1ContinuationRef.current = request;
        try {
            await runProjectSummaryRef.current?.();
        } finally {
            if (stage1ContinuationRef.current === request) {
                request.settled = true;
                setStage1ContinuationRevision(revision => revision + 1);
            }
        }
    };

    const { canGoNext, openStage5FromCurrentScope, handleNext, handleBack } = useStageNavigation({
        stage,
        isStage1Processing,
        isSummarizing,
        projectSummary,
        summaryError,
        formData,
        documents,
        photos,
        videos,
        videoTranscripts,
        analysis,
        projectWMS,
        scope,
        quoteData,
        quoteNote,
        scopeGstInclusive,
        quoteTitle,
        selectedHeroImage,
        completedStages,
        summaryNeedsRefresh,
        summaryRerunHeld,
        projectWorkflowMode,
        setProjectWorkflowMode,
        pricingWorktableActive,
        requestCockpitTab,
        runProjectSummaryThenNext,
        setCompletedStages,
        setStageWithTransition,
        setToast,
        setPricingWorktableActive,
        setImportPhase,
        scopeRef,
        quoteDataRef,
        quoteNoteRef,
        batchActiveRef,
        stageRef,
        currentPageRef,
        currentProjectIdRef,
        projectLoadGenerationRef,
        projectAbortRef,
        stage45PersistenceReceiptRef,
        showConfirm,
        saveCurrentProject,
        runProjectSummary,
        isQuoteGenerationInFlight: isCurrentProjectCommercialWriteInFlight,
        applyFirstRunHbcf: applyFirstRunHbcfBeforeStage5,
        openFinalQuoteEditor: (source, opts) => openFinalQuoteEditor(source, opts),
        closeQuoteEditor: () => closeQuoteEditor(),
    });
    handleNextRef.current = handleNext;
    // B5: one Stage 1 forward press. When NEXT finds the summary missing or out
    // of date it starts the summary here and presses NEXT again once that run
    // settles with a fresh summary for the same project (the effect below,
    // after the render that shows it). A run that published a new summary which
    // is out of date on arrival, with nothing edited since NEXT, made the same
    // answer a new run would make, so NEXT goes on with it as it is rather than
    // paying for it again on every press. Any other outcome drops the request.
    useEffect(() => {
        const request = stage1ContinuationRef.current;
        if (!request || !request.settled) return;
        stage1ContinuationRef.current = null;
        if (currentPageRef.current !== 'quotemaker' || Number(stageRef.current) !== 1) return;
        if ((currentProjectIdRef.current || null) !== request.projectId && request.projectId !== null) return;
        if (Number(projectLoadGenerationRef.current) !== request.loadGeneration) return;
        if (!projectSummary || summaryError || isSummarizing) return;
        if (projectSummaryEvidenceIsFresh) {
            handleNextRef.current?.();
            return;
        }
        const landedOutOfDate = projectSummary !== request.summaryBefore
            && currentProjectSummaryEvidenceFingerprintRef.current === request.evidenceFingerprint;
        if (landedOutOfDate) handleNextRef.current?.({ useSummaryAsIs: true });
    }, [stage1ContinuationRevision]);

    // ──────────────────────────────────────────────────────────────────────
    // First-time-ready celebration on the NEXT button (v5.19.23, Peter
    // 2026-05-05). The NEXT button morphs while AI work runs ("Analysing",
    // "Generating", spinner, greyed-out) and finally becomes a clickable
    // green arrow. The first time that flip happens for a stage in a given
    // project session, the button plays a distinctive eye-drawing burst
    // (elastic overshoot + double brand-coloured shockwave + lingering
    // aura). Subsequent visits to the same stage do NOT replay it. The
    // animation respects prefers-reduced-motion via a quieter fallback in
    // components.css.
    //
    // Tracked per (projectId, celebrationKey) tuple so switching projects
    // re-arms; the seen-set persists for the life of the SPA so jumping
    // BACK to a stage doesn't replay.
    // ──────────────────────────────────────────────────────────────────────
    const [readyAnimationKey, setReadyAnimationKey] = useState(null);
    const seenReadyByProjectRef = useRef(new Map()); // Map<projectKey, Set<celebrationKey>>
    const prevNextDisabledRef = useRef(new Map());   // Map<`${projectKey}::${celebrationKey}`, bool>

    // The Stage 1 pulse fires when a summary lands (S17): a landing in this
    // session, for the project on screen, while NEXT is ready. Never on project
    // open, reload or a return to Stage 1 (no landing), and once per project.
    // The landing on screen when a project opens is not news (the same rule as
    // the voice, B10), so the landing seq at the open is remembered.
    const summaryLandingSeq = summaryLanding?.seq ?? 0;
    const landingSeqAtOpenRef = useRef({ projectId: currentProjectId, seq: summaryLandingSeq });
    if (landingSeqAtOpenRef.current.projectId !== currentProjectId) {
        landingSeqAtOpenRef.current = { projectId: currentProjectId, seq: summaryLandingSeq };
    }
    const summaryLandedHere = stage1SummaryLandedThisSession({
        landingSeq: summaryLandingSeq,
        seqAtOpen: landingSeqAtOpenRef.current.seq,
        landingSummary: summaryLanding?.summary ?? null,
        projectSummary,
    });
    useEffect(() => {
        if (!summaryLandedHere || currentPage !== 'quotemaker' || stage !== 1) return;
        if (isSummarizing || isAnalyzing || isStage1Processing || summaryError) return;
        const projectKey = currentProjectId || '__no_project__';
        let seen = seenReadyByProjectRef.current.get(projectKey);
        if (!seen) {
            seen = new Set();
            seenReadyByProjectRef.current.set(projectKey, seen);
        }
        if (seen.has('summary')) return;
        seen.add('summary');
        setReadyAnimationKey('summary');
        const t = setTimeout(() => {
            setReadyAnimationKey(prev => (prev === 'summary' ? null : prev));
        }, 3200);
        return () => clearTimeout(t);
    }, [summaryLandedHere, currentPage, stage, isSummarizing, isAnalyzing, isStage1Processing, summaryError, currentProjectId]);

    const currentCelebrationKey = useMemo(() => {
        if (currentPage !== 'quotemaker') return null;
        // Stage 1's pulse is the summary landing above, not a NEXT transition.
        if (stage === 1) return 'summary';
        if (stage >= 2 && stage <= 4) return `stage${stage}`;
        return null;
    }, [currentPage, stage]);

    useEffect(() => {
        if (!currentCelebrationKey || currentCelebrationKey === 'summary') return undefined;

        // Mirror of the inline disabled predicate on the NEXT button (see the
        // <button className="btn-next-stage"> render block). Kept in sync by
        // hand because the original lives inside JSX and isn't reusable as-is.
        //
        // Stage 4 now opens Stage 5 from the live scope, so quote generation
        // can run in the background without making the navigation CTA disabled.
        // Keep this mirror in lockstep with the JSX disabled rule below.
        const cgn = canGoNext();
        const currentDisabled = (
            (stage === 2 && isAnalyzing && !projectWMS?.structured) ||
            (!cgn && !(stage === 1 && (isAnalyzing || analysis))) ||
            (isSummarizing && stage === 1)
        );

        const projectKey = currentProjectId || '__no_project__';
        const refKey = `${projectKey}::${currentCelebrationKey}`;
        const prevDisabled = prevNextDisabledRef.current.get(refKey);

        let seen = seenReadyByProjectRef.current.get(projectKey);
        if (!seen) {
            seen = new Set();
            seenReadyByProjectRef.current.set(projectKey, seen);
        }

        // Fire ONLY on a clean false→true transition (i.e. user was waiting
        // and the button just became clickable) AND only if we haven't yet
        // celebrated this stage for this project. First-mount with the
        // button already enabled (revisit, RE-ANALYSE while data exists)
        // sees prevDisabled === undefined and won't fire.
        if (prevDisabled === true && currentDisabled === false && !seen.has(currentCelebrationKey)) {
            seen.add(currentCelebrationKey);
            const firedKey = currentCelebrationKey;
            setReadyAnimationKey(firedKey);
            const t = setTimeout(() => {
                setReadyAnimationKey(prev => (prev === firedKey ? null : prev));
            }, 3200);
            prevNextDisabledRef.current.set(refKey, currentDisabled);
            return () => clearTimeout(t);
        }

        prevNextDisabledRef.current.set(refKey, currentDisabled);
        return undefined;
    }, [currentCelebrationKey, stage, isAnalyzing, projectWMS, projectWMSGenerating, analysis, projectSummary, summaryError, scope, projectCostingSheet, isGenerating, isSummarizing, isStage1Processing, currentProjectId, quoteData]);

    // Switching project drops any in-flight celebration so it doesn't bleed
    // visually onto the freshly-loaded project's nav bar.
    useEffect(() => {
        setReadyAnimationKey(null);
    }, [currentProjectId]);

    const isNextStageJustReady = readyAnimationKey != null && readyAnimationKey === currentCelebrationKey;

    // K11: the interrupted-run note lasts until the next summary run starts, the
    // summary is fresh again, or another project opens.
    useEffect(() => {
        if (isSummarizing || (projectSummary && projectSummaryEvidenceIsFresh)) setSummaryRunInterrupted(false);
    }, [isSummarizing, projectSummary, projectSummaryEvidenceIsFresh]);
    useEffect(() => {
        setSummaryRunInterrupted(false);
    }, [currentProjectId]);
    // Stage 1's header sentence: why it waits (K11). Never a stuck state: each
    // wait is one QuoteMaker ends itself.
    // The retry wait of a failed summary is not a notice: the cockpit's line
    // says it, and the chip only where no line shows (H1, stage1Status).
    const stage1SummaryWaitingNotice = stage1WaitingNotice({
            fastTrack: isFastTrackManualScope,
            canAutoRefresh: inspectorNotesCanAutoRefresh
                && !!apiKey
                && !aiPipelineHaltedRef.current
                && !openedForReviewRef.current,
            hasSummary: !!projectSummary,
            summaryStale: summaryNeedsRefresh && !summaryRerunHeld,
            runInterrupted: summaryRunInterrupted,
            notesPaused: inspectorNotesAiPaused,
            busy: isSummarizing || isAnalyzing,
        });
    const stage1Notice = stage1SummaryWaitingNotice;


    // DEC-COUNCIL-013 migration: these 3 inline helpers were literal mirrors
    // of the hook's syncBack/openForNavigation/close methods. Replaced with
    // aliases so existing callers keep working unchanged. The helper bodies
    // moved verbatim into src/hooks/use-quote-editor.js — see that file's
    // openForNavigation / close / syncBack for the implementation.
    const syncQuoteEditorBackToState = editor.syncBack;
    const openFinalQuoteEditor = editor.openForNavigation;
    const closeQuoteEditor = editor.close;

// TS-060: File Explorer callbacks extracted to useGlobalFileExplorer


    // v5.37.25 (Peter 2026-05-20): UNIFIED "Pre-process N files" handler.
    //
    // Replaces the v5.37.21-24 split between "Import" (heavy — fires the full
    // Stage 1→5 AI cascade) and "Queue for pre-process" (lightweight — but
    // only worked for already-synced projects). Peter's call: "What is the
    // point of pre-processing when you need to import first?" The split
    // conflated two concerns: "bring files into QM" vs "fire the AI pipeline".
    //
    // New workflow:
    //   1. Pre-process (this handler) — materialises files into a Dexie
    //      project record + runs the deterministic preprocessor (text from
    //      documents, photos persisted to blob store). No AI.
    //   2. The project lands in your queue (= AllQuotesPage) marked
    //      ready_for_quote.
    //   3. Generate quote — explicit later step that fires the AI cascade.
    //
    // Branches:
    //   - project.id is a UUID (synced project already in Dexie):
    //       Re-run preprocessProject on the existing row with the optional
    //       selectedFileIds scope filter. Idempotent — docs already with
    //       extracted text are skipped.
    //   - project.id is a folder name (unsynced or fresh-scan):
    //       Materialise the File handles, build documents[] + photos[],
    //       create a fresh Dexie row, then run preprocessProject.
    //

    // v5.37.25 (Peter 2026-05-20): open a pre-processed project from the File
    // Explorer ("Open the job now", the post-pre-process toast). It saves the
    // job on screen first, then loads this one at Stage 1, which writes its
    // summary; the summary starts QuoteMaker on the server. The express Stage 1
    // to 5 run it used to fire went with the old pipeline
    // (docs/old-pipeline-removal/PLAN.md).
    const generateQuoteForPreprocessedProject = useCallback(async (projectId, projectName) => {
        try {
            // Decision D1: a Quote Run refuses the open and shows its message.
            // Ask before the navigation and the save-and-switch below. The
            // job the run is driving is already being quoted: show it.
            if (showQuoteRunJobInsteadOfOpening(projectId)) return;
            if (refuseProjectChangeDuringQuoteRun('open', projectId)) return;
            const summary = {
                id: projectId,
                title: projectName || 'Untitled project',
            };
            // The load lands the page: completeProjectLoad navigates to
            // QuoteMaker as its last statement. This used to navigate
            // first (2026-09-29): with nothing open that showed
            // an empty new project, and a failed load left it on screen. The
            // deps below still name handleNavigate, a per-render closure, so
            // this callback is rebuilt every render and never pins the load
            // chain (CLAUDE.md HARD RULE).

            // v5.37.27 H1 fix: the legacy `loadProject` opens an unsaved-changes
            // confirm modal then returns IMMEDIATELY without awaiting the modal.
            // Save and switch form one fail-closed boundary. If persistence is
            // unavailable or rejects, the loader is never invoked and the
            // current project's unsaved state remains on screen.
            await switchExpressProjectAfterSaving({
                hasUnsavedChanges: hasUnsavedChangesRef.current,
                saveCurrentProject: saveCurrentProjectRef.current,
                loadProject: () => loadProjectUnchecked(summary),
            });
        } catch (err) {
            if (isQuoteRunActiveError(err)) return; // refused; its message is already shown
            const saveBlocked = err?.code === 'EXPRESS_PROJECT_SWITCH_SAVE_FAILED';
            console.warn(
                saveBlocked
                    ? '[App] Generate-quote project switch blocked by save failure:'
                    : '[App] Generate-quote failed to load pre-processed project:',
                err?.message,
            );
            setToast({
                message: saveBlocked
                    ? `Could not start "${projectName}" because the current project did not save. Your current project remains open. ${err?.cause?.message || ''}`.trim()
                    : `Could not open "${projectName}": ${err?.message || 'unknown error'}`,
                type: 'error',
                // Stays until dismissed: a failed open now leaves the operator
                // on the page they clicked from, so this is the only sign.
                sticky: true,
            });
        }
    }, [currentPage, handleNavigate]);

    const handleQuoteEditorBack = () => {
        closeQuoteEditor();
    };

    // `confirmCopy` overrides the "Start New Project?" wording for callers that
    // clear the working context for a reason other than starting a blank job —
    // Quick Edit opens a RECOVERED quote and needs the same save-then-reset
    // guarantees, but telling the operator he is starting a new project would
    // be a lie about what the button does.
    const handleStartNewProject = ({ then, workflowMode, confirmCopy, announceWorkflow = true } = {}) => {
        // Decision D1 (2026-09-28): a Quote Run refuses the reset and shows its
        // message. Ask before any prompt, save or `then` callback runs.
        if (refuseProjectChangeDuringQuoteRun('reset')) return;
        // 2026-05-23 (Ask 2): accept an optional `then` callback so the
        // Dashboard + Project History "+ NEW PROJECT" cards can chain a
        // navigation after the (possibly-async) reset, WITHOUT bypassing
        // the styled "Cancel Import?" confirm. Pre-fix those callers wired
        // a bare `() => { resetForm(); handleNavigate('quotemaker'); }`
        // which fired the cancel side-effect with zero warning while a
        // 60-second photo/Whisper extraction was mid-flight.
        const hasData = formData.notes?.trim()
            || formData.address?.trim()
            || formData.clientName?.trim()
            || scope.length > 0
            || photos.length > 0
            || documents.length > 0
            || videos.length > 0
            || quoteData;
        const nextWorkflowMode = normalizeProjectWorkflowMode(workflowMode);
        const applyNewProjectWorkflowMode = () => {
            setProjectWorkflowMode(nextWorkflowMode);
            // v5.41.38: confirm the choice took effect (was silent before). Nothing to confirm when nobody chose.
            if (announceWorkflow) setToast(workflowModeToast(nextWorkflowMode));
        };
        const runThen = () => { try { then?.(); } catch (err) { console.warn('[handleStartNewProject] then() callback failed:', err); } };
        const runReset = async () => {
            if (isCurrentProjectQuoteGenerationInFlight()) {
                // A warning, not info (the notification policy hides info);
                // Full AI's background author holds this after every run.
                setToast({
                    message: 'A quote is being written for this job. Wait for it to finish, or press STOP, before starting another project.',
                    type: 'warning',
                });
                return;
            }
            const synced = quoteEditorOpen ? syncQuoteEditorBackToState() : null;
            // The chosen mode goes on after runThen: see start-new-project-sequence.ts.
            const start = () => startNewProjectWithChosenWorkflow({
                resetEditor: () => editor.reset(),
                resetForm,
                applyWorkflowMode: applyNewProjectWorkflowMode,
                then: runThen,
            });
            if (!hasUnsavedChanges && !synced) {
                start();
                return;
            }
            // A save the server could not take used to reject here with no
            // catch: the click did nothing and said nothing (review of the
            // outage lane, 2026-09-30). saveThenStartNew tells the operator
            // and lets them go on or stay.
            await saveThenStartNew({
                // Stage 4 as syncBack left it (the live rows while a version
                // choice waits), not the quote's rows (review round 7).
                save: () => saveCurrentProject(synced ? { quoteData: synced, scope: scopeRef.current, currentStage: 5 } : {}),
                start,
                discardOnScreen: () => {
                    if (currentProjectIdRef.current) void clearUnsavedProjectEdits(currentProjectIdRef.current);
                    discardKeptNewProject();
                },
                showConfirm,
                projectId: () => currentProjectIdRef.current,
                projectTitle: () => String(
                    (Array.isArray(projects) ? projects : []).find(p => p?.id === currentProjectIdRef.current)?.title
                    || formData?.address
                    || 'the current project',
                ),
                actionText: confirmCopy?.confirmText || 'Start New',
            });
        };
        if (isStage1Processing) {
            showConfirm({
                title: 'Cancel Import?',
                message: 'A file import is currently being processed. Starting a new project will cancel the import and clear any partial results.',
                confirmText: 'Cancel & Start New',
                confirmColor: 'orange',
                icon: 'warning',
                // The new project it clears goes from this device too, or the
                // next blank QuoteMaker page paints it back. Before the reset,
                // which releases it in this tab.
                onConfirm: () => {
                    discardKeptNewProject();
                    startNewProjectWithChosenWorkflow({ resetForm, applyWorkflowMode: applyNewProjectWorkflowMode, then: runThen });
                }
            });
        } else if (hasData) {
            const willSave = hasUnsavedChanges || quoteEditorOpen;
            showConfirm({
                title: confirmCopy?.title || 'Start New Project?',
                message: confirmCopy
                    ? (willSave ? confirmCopy.messageWithSave : confirmCopy.message)
                    : (willSave
                        ? 'Current project will be saved before starting fresh. Notes, scope items, quote edits, and uploaded files will be cleared.'
                        : 'All current progress will be cleared including notes, scope items, quote edits, and uploaded files.'),
                confirmText: confirmCopy?.confirmText || 'Start New',
                confirmColor: 'orange',
                icon: 'warning',
                onConfirm: () => { runReset(); }
            });
        } else {
            runReset();
        }
    };

    // Quick Edit hands back a quote recovered from a stored artefact or read off
    // an uploaded PDF. It belongs to no loaded project — and often to no project
    // at all, since an artefact outlives the project it was produced under.
    //
    // Routed through handleStartNewProject on purpose. That saves whatever is
    // currently open (or asks first), then clears the working context, so two
    // things hold: the loaded job cannot be overwritten by the recovered one,
    // and with no currentProjectId a later save mints a NEW project — which is
    // how a quote that went missing from history gets a record again.
    // WHY A PENDING REF INSTEAD OF OPENING THE EDITOR DIRECTLY. handleNavigate
    // applies the page switch inside a setTimeout(50), and on arrival at
    // 'quotemaker' it re-resolves the target stage from the `stage` value its
    // closure captured BEFORE we ran. Opening the editor first therefore had
    // its Stage 5 overwritten 50ms later and the operator landed on Stage 1
    // looking at an empty import screen. Caught in a browser smoke; no unit
    // test could see it, because the defect lives in the 50ms gap.
    //
    // So: navigate FIRST, open once the page has actually changed. Same shape
    // as pendingOpenQuoteRef on the openQuoteForProject path above, which
    // exists for the same reason.
    const pendingRecoveredQuoteRef = useRef(null);

    // Quote Corpus "Rebuild this quote" (migration 089 follow-on). Clears the
    // working context through the SAME guarded reset every new-project path
    // uses (unsaved work saves first, mid-import gets its named confirm), then
    // stages the historical quote: Stage 1 form patched, Stage 4 scope
    // pre-seeded, provenance kept in aiContext.corpusSource. The operator
    // lands on Stage 1 and drives the normal pipeline — regeneration runs in
    // the app's own voice with the original retrievable as an exemplar.
    const rebuildFromCorpusEntry = useCallback((entry) => {
        let draft;
        try {
            draft = buildDraftFromCorpusEntry(entry);
        } catch (err) {
            console.warn('[QM] corpus rebuild parse failed:', err?.message);
            setToast({ kind: 'error', message: 'Could not parse that corpus entry into a project.' });
            return;
        }
        handleStartNewProject({
            confirmCopy: {
                title: 'Rebuild this historical quote?',
                message: `A new project will be staged from ${draft.corpusSource.quoteRef || 'the corpus entry'} — address, client and ${draft.scope.length} scope item${draft.scope.length === 1 ? '' : 's'} pre-filled. The corpus entry itself is not changed.`,
                messageWithSave: `Your current project will be saved first. Then a new project is staged from ${draft.corpusSource.quoteRef || 'the corpus entry'} — address, client and ${draft.scope.length} scope item${draft.scope.length === 1 ? '' : 's'} pre-filled.`,
                confirmText: 'Rebuild',
            },
            then: () => {
                setFormData(prev => ({ ...prev, ...draft.formData }));
                setScope(ensureItemIds(draft.scope));
                // resetForm's counter bump makes Stage 1's [resetCounter]
                // effect snap the view to IMPORT in the post-commit pass —
                // AFTER any same-batch setImportPhase('form') here. A rebuild
                // arrives with the form already filled, so land on the FORM:
                // defer one macrotask so this write follows that effect.
                setTimeout(() => setImportPhase('form'), 0);
                // prefill: the staged formData/scope from THIS batch are not
                // visible inside handleNavigate's closure, so without the
                // flag the no-active-work fast-path would resetForm() the
                // draft (the documented completeProjectLoad race).
                handleNavigate('quotemaker', { prefill: true });
            },
        });
    }, [handleStartNewProject, handleNavigate]);

    const openRecoveredQuote = useCallback((recoveredQuoteData) => {
        if (!recoveredQuoteData) return;
        handleStartNewProject({
            confirmCopy: {
                title: 'Open the recovered quote?',
                message: 'The job currently open will be cleared to make room for it. '
                    + 'Nothing that has been saved is affected.',
                messageWithSave: 'The job currently open will be saved, then cleared to make room '
                    + 'for the recovered quote.',
                confirmText: 'Open it',
            },
            then: () => {
                pendingRecoveredQuoteRef.current = recoveredQuoteData;
                // force: same must-land class as the openQuoteForProject peek
                // branch. The ref above is drained ONLY by the effect below,
                // which keys on currentPage — so a navigate dropped by the
                // mid-transition block silently discards the recovered quote
                // with no error and no retry.
                handleNavigate('quotemaker', { force: true });
            },
        });
    }, [handleStartNewProject, handleNavigate]);

    // Drains the ref once the page has actually switched. Runs after the
    // navigation's own stage resolution has committed, so openRecovered's
    // Stage 5 is the last write rather than the overwritten one.
    useEffect(() => {
        if (currentPage !== 'quotemaker') return;
        const recovered = pendingRecoveredQuoteRef.current;
        if (!recovered) return;
        pendingRecoveredQuoteRef.current = null;
        // The forced navigation's blank-page path has started bringing back a
        // new project kept on this device. Marking the screen first makes that
        // restore decline, whether its read lands before or after this
        // (review 7, item 2: it painted the kept form, photos and scope over
        // the recovered quote and autosaved the mix).
        markRecoveredQuoteOnScreen();
        editor.openRecovered(recovered);
    }, [currentPage, editor]);

    // New project opens the file drop at once: there is nothing to choose (Peter's O1, 2026-10-31, and the old
    // pipeline's removal, docs/old-pipeline-removal/PLAN.md). QuoteMaker writes the job, and writing the scope by hand
    // is a choice on the job (the import screen's strip and the Quote Brain bar).
    const openNewProjectWorkflowChooser = (options = {}) => {
        handleStartNewProject({
            then: typeof options.then === 'function' ? options.then : undefined,
            workflowMode: PROJECT_WORKFLOW_MODES.FULL_AI,
            announceWorkflow: false,
        });
    };

    // v5.41.38: in-place workflow-mode switch from the Stage-1 mode banner.
    // Unlike the chooser (which resets the project), this just flips the mode +
    // confirms via toast. Safe at Stage 1 because nothing is computed yet — it
    // only decides whether Risk + Work Method run AFTER the summary. The mode
    // persists with the project on the next save (it rides every save payload).
    const handleSwitchProjectWorkflowMode = () => {
        const next = projectWorkflowMode === PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE
            ? PROJECT_WORKFLOW_MODES.FULL_AI
            : PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE;
        setProjectWorkflowMode(next);
        setToast(workflowModeToast(next, { quoteBrain: quoteBrainWritesNewJobs() }));
    };

    // The Quote Brain bar's own switch (O1): the builder writes this job's scope by hand, or hands it back to the Quote
    // Brain. Saved at once, because the server's start reads the job's workflow mode. False when the save failed.
    const handleQuoteBrainWriterChange = async (mode) => {
        const next = normalizeProjectWorkflowMode(mode);
        setProjectWorkflowMode(next);
        try {
            await saveCurrentProject({ workflowMode: next });
        } catch {
            return false;
        }
        setToast(workflowModeToast(next, { quoteBrain: true }));
        return true;
    };

    const scopeDisplayTotalsForNav = deriveQuoteDisplayTotalsFromScope(scope, {
        isGstInclusive: scopeGstInclusive,
        wholeDollars: true,
    });
    const scopeTotalForNav = scopeDisplayTotalsForNav.displayTotal;
    const quoteDataForNav = quoteEditorOpen && quoteEditorLiveSnapshot ? quoteEditorLiveSnapshot : quoteData;
    const quoteScopeForNav = Array.isArray(quoteDataForNav?.scope) && quoteDataForNav.scope.length > 0 ? quoteDataForNav.scope : scope;
    const quoteGstInclusiveForNav = quoteDataForNav?.isGstInclusive ?? scopeGstInclusive;
    const quoteScopeTotalsForNav = deriveQuoteDisplayTotalsFromScope(quoteScopeForNav, {
        isGstInclusive: quoteGstInclusiveForNav,
        wholeDollars: true,
    });
    const quoteScopeTotalForNav = quoteScopeTotalsForNav.totalIncGst;
    const savedQuoteTotalForNav = resolveSavedQuoteTotalForNav(quoteDataForNav);
    const quoteTotalForNav = savedQuoteTotalForNav
        || quoteScopeTotalForNav
        || scopeTotalForNav;
    const stageNavContext = computeStageNavContext({
        stage,
        projectWMS,
        projectWMSGenerating,
        stage3MethodAssessment,
    });

    const handleQuoteStageNavigate = (targetStage, options) => {
        if (typeof targetStage !== 'number') return { ok: false, reason: 'Invalid stage number.' };
        // The Assistant's stage move after an open (review 2026-09-30) never
        // navigates: its own move to QuoteMaker replaced an open's landing
        // held behind "Leave with unsaved settings?". It changes the stage
        // only while QuoteMaker is on screen.
        if (options?.onlyOnQuoteMaker === true && currentPage !== 'quotemaker') {
            return { ok: false, reason: 'QuoteMaker is not on screen, so the stage was not changed.', targetStage };
        }
        if (isStageTransitioning) return { ok: false, reason: 'A stage transition is already running.' };
        if (targetStage === stage && targetStage !== 5 && currentPage === 'quotemaker') {
            return { ok: true, alreadyThere: true, targetStage };
        }

        const canNav = canNavigateToQuoteStage(targetStage);
        if (!canNav) return { ok: false, reason: 'That stage is blocked by the current project readiness rules.', targetStage };

        if (currentPage !== 'quotemaker') {
            handleNavigate('quotemaker');
        }

        const runStageNavigation = () => {
            if (targetStage === 5) {
                openStage5FromCurrentScope({ persistStage: true });
                return;
            }

            if (quoteEditorOpen) {
                // The caller owns the destination stage. Close through the
                // modal receipt boundary so setup-only normalization remains
                // local and a genuine edit is persisted exactly once.
                editor.close({ transitionToStage4: false });
            }
            if (targetStage === 1) {
                revokeAttendedAutomaticRiskIntent();
                if (formData.notes || documents.length > 0 || photos.length > 0) {
                    setImportPhase('form');
                }
            }
            setStageWithTransition(targetStage);
        };

        if (currentPage !== 'quotemaker') {
            setTimeout(runStageNavigation, 120);
            return { ok: true, pending: true, targetStage };
        }
        runStageNavigation();
        return { ok: true, targetStage };
    };
    handleQuoteStageNavigateRef.current = handleQuoteStageNavigate;

    // ─── The Assistant checks Full AI's request source at Stages 4 and 5 ─────
    // Full AI took its best reading of which document is the request and
    // recorded the doubt (rfq-source-reading.ts). On arrival at Stage 4 and at
    // Stage 5 the Assistant is brought forward once with that check.
    const rfqSourceDoubt = useRfqSourceDoubt({
        projectId: currentProjectId,
        currentPage,
        quoteEditorOpen,
        analysis,
        projectWMS,
        formData,
        assistantAvailable: !isSiteVisitRole(user?.role) && !!currentProjectId,
        raiseInCockpit: () => requestCockpitAssistant(),
        openFloatingAssistant: () => setGlobalAiAssistantOpen(true),
    });

    // ─── QuoteMaker hands the quote it wrote to the Assistant ────────────────
    // Peter, 2026-11-01: when QuoteMaker writes a quote, the builder is taken to
    // the Assistant at Stages 4 and 5 to make the final edits before it goes out.
    // The Assistant is brought forward once per written version with QuoteMaker's
    // review (use-quotemaker-review.ts); a quote written for another job brings a
    // notice with Review it, which opens that job (use-quotemaker-written-notices.ts).
    const quoteMakerReview = useQuoteMakerReview({
        projectId: currentProjectId,
        currentPage,
        stage,
        quoteEditorOpen,
        quoteData,
        scope,
        writing: !!currentProjectId && isQuoteBrainRunLive(currentProjectId),
        byHand: isFastTrackManualScope,
        assistantAvailable: !isSiteVisitRole(user?.role) && !!currentProjectId,
        raiseInCockpit: () => requestCockpitAssistant(),
        openFloatingAssistant: () => setGlobalAiAssistantOpen(true),
    });
    const quoteMakerWritten = useQuoteMakerWrittenNotices({
        enabled: !!user && !isSiteVisitRole(user?.role),
        currentProjectId: currentProjectId || null,
    });
    // Keep: the operator's answer is saved on the project so no device asks
    // again ("Keep <request>" the way the Stage 1 import saves "use the attached
    // request", so a later import keeps that request; any other reading by its
    // id); then this device's prompt ledger.
    const keepRfqSourceFromAssistant = async (reading) => {
        const projectIdAtClick = currentProjectIdRef.current || null;
        const loadGenerationAtClick = projectLoadGenerationRef.current;
        const formAtClick = formDataRef.current;
        const kept = planKeepAttachedRequest({
            reading,
            formData: formAtClick,
            documents: evidencePreparationLiveRef.current.documents,
            decidedAt: new Date().toISOString(),
        });
        if (kept) {
            try {
                // A save that fails keeps no copy on this device: the answer is shown only once saved.
                const saved = await saveCurrentProject({ formData: kept }, { keepUnsavedEdits: false });
                if (!saved) throw new Error('The project save returned no saved project.');
            } catch (error) {
                setToast({ message: `Your answer could not be saved, so the check stays open. ${error?.message || ''}`.trim(), type: 'warning' });
                return;
            }
            if ((currentProjectIdRef.current || null) !== projectIdAtClick
                || projectLoadGenerationRef.current !== loadGenerationAtClick) return;
            // Only the answer's own fields, on the form as it is now: an edit made while it saved stays.
            const withAnswer = (form) => withRfqSourceAnswer(form, formAtClick, kept);
            formDataRef.current = withAnswer(formDataRef.current);
            setFormData(withAnswer);
        }
        rfqSourceDoubt.keep(reading);
    };
    // "Use the email instead": the email becomes the request (the operator's
    // answer is kept the way the Stage 1 import keeps it). The Work Method that
    // a new request used to rebuild went with the old pipeline
    // (docs/old-pipeline-removal/PLAN.md).
    const switchRfqSourceFromAssistant = async (reading) => {
        const action = describeRfqSourceDoubt(reading).switchAction;
        if (action?.kind === 'open-stage-1') {
            handleQuoteStageNavigate(1);
            return;
        }
        if (action?.kind !== 'use-set-aside') return;
        const projectIdAtClick = currentProjectIdRef.current || null;
        const loadGenerationAtClick = projectLoadGenerationRef.current;
        const formAtClick = formDataRef.current;
        const plan = planSwitchToSetAsideRequest({
            reading,
            formData: formAtClick,
            documents: evidencePreparationLiveRef.current.documents,
            preprocessedFiles: evidencePreparationLiveRef.current.preprocessedFiles,
            decidedAt: new Date().toISOString(),
        });
        if (!plan.ok) {
            setToast({ message: plan.why, type: 'warning' });
            return;
        }
        try {
            // A save that fails keeps no copy on this device, so nothing is switched on a later open either.
            const saved = await saveCurrentProject({ formData: plan.formData }, { keepUnsavedEdits: false });
            if (!saved) throw new Error('The project save returned no saved project.');
        } catch (error) {
            setToast({ message: `The email could not be saved as the request, so nothing was switched. ${error?.message || ''}`.trim(), type: 'warning' });
            return;
        }
        if ((currentProjectIdRef.current || null) !== projectIdAtClick
            || projectLoadGenerationRef.current !== loadGenerationAtClick) return;
        // Only the answer's own fields, on the form as it is now: an edit made while it saved stays.
        const withAnswer = (form) => withRfqSourceAnswer(form, formAtClick, plan.formData);
        formDataRef.current = withAnswer(formDataRef.current);
        setFormData(withAnswer);
        setToast({ message: 'The email is now the request.', type: 'info' });
    };
    // Rolling compatibility: an older access token has no rotation claim. Hold
    // every application surface while /auth/me resolves it. Failure above drops
    // the local session to LoginPage, so unknown can never become implicit false.
    if (user && !canRenderAuthenticatedApp(passwordRotationBootState)
        && passwordRotationBootState !== 'required') {
        return sessionCheckOutage ? (
            <ServerUnreachableScreen nextRetryAt={sessionCheckOutage.nextRetryAt} onTryNow={retrySessionCheck} />
        ) : <PageLoadingShim />;
    }

    // The JWT chooses only a pending local namespace. Keep every authenticated
    // application surface unmounted until /auth/me verifies the immutable
    // tenant and user binding and Dexie opens that exact namespace. When the
    // check could not reach the server, say so and keep trying: the session
    // and the work on this device are kept.
    if (user && passwordRotationBootState !== 'required' && !localTenantSessionVerified) {
        return sessionCheckOutage ? (
            <ServerUnreachableScreen nextRetryAt={sessionCheckOutage.nextRetryAt} onTryNow={retrySessionCheck} />
        ) : <PageLoadingShim />;
    }

    // ─── Temporary password rotation gate ───────────────────────────────────
    // This precedes every other signed-in surface, including invitation links.
    // A signed true claim therefore gates on the first paint without waiting for
    // /auth/me or letting another route render around it.
    if (user && passwordRotationBootState === 'required') {
        return (
            <Suspense fallback={<PageLoadingShim />}>
                <ForcePasswordChangeGate
                    user={user}
                    onComplete={() => setUser(prev => (prev ? {
                        ...prev,
                        mustChangePassword: false,
                        passwordRotationBootState: 'clear',
                    } : prev))}
                    onLogout={handleLogout}
                />
            </Suspense>
        );
    }

    // ─── Invitation acceptance (pre-account, 2026-08-05) ────────────────────
    // Checked before the sign-in wall because the person holding the link has
    // no account yet by definition. A signed-in user with a normal password may
    // also inspect a colleague's link; a forced-rotation session is gated above.
    const invitationToken = (() => {
        try {
            if (!window.location.pathname.startsWith('/accept-invitation')) return null;
            return new URLSearchParams(window.location.search).get('token') || '';
        } catch { return null; }
    })();
    if (invitationToken !== null) {
        return (
            <Suspense fallback={<PageLoadingShim />}>
                <AcceptInvitationPage token={invitationToken} onAccepted={setUser} />
            </Suspense>
        );
    }

    // If not logged in, show login page (must come after all hooks are declared)
    if (!user) {
        return <LoginPage onLogin={setUser} />;
    }

    const appAreaAccess = effectiveAccess(user);
    const canAccessIntake = appAreaAccess.intake === true;
    const canAccessSiteVisits = appAreaAccess['site-visits'] === true;
    // Developer Tools may inspect runtime metadata and pipeline state. Unlike
    // ordinary first-paint menu guidance, its render boundary waits for the
    // authoritative /auth/me map. A JWT role estimate must never mount it.
    const canAccessDeveloper = user?.access?.developer === true;

    return (
        <OperationalFieldBoundary>
        <ProjectClientLink
            tenantId={user?.tenantId}
            project={currentCrmProject || { id: currentProjectId }}
            formData={formData}
            active={currentPage === 'quotemaker' || quoteEditorOpen}
            busy={isStage1Processing || isLoadingProjectRef.current || isSavingProject || isAnalyzing || isSummarizing}
            quoteRunActive={quoteRunDriving || batchActiveRef.current}
            openKey={projectLoadGenerationRef.current}
            onLinked={(projectId, link) => {
                // A link with no propertyId key keeps the row's site. A null
                // propertyId clears it: the job has no site on the server.
                const site = link.propertyId === undefined ? null : { propertyId: link.propertyId, property_id: link.propertyId };
                setProjects(previous => previous.map(p => p.id === projectId
                    ? { ...p, companyId: link.companyId, company_id: link.companyId, ...(site || {}) }
                    : p));
                setClientRefreshTrigger(value => value + 1);
            }}
            // The prompt's "File it under its firm in Tidy up" note (SPEC 2026-10-08 D4).
            onOpenTidy={() => handleNavigate('clients', { pageNavOptions: { crmView: 'duplicates' } })}
        >
        <div className="flex flex-col h-screen w-full">
            {/* ─── Server status banner (v5.12.0; outage lane 2026-09-29) ────
                On while QuoteMaker's server is not answering (no answer, a
                gateway 502/503/504, or a proxy write that failed that way).
                Nothing is sent meanwhile: a project save keeps its envelope on
                this device (unsaved-project-edits) and background work is held.
                The reconnect loop probes /api/health on a backoff and clears
                it, and the autosave then saves once. It is the only status:
                the save path adds no second banner or toast per attempt. */}
            {apiStatus.disabled && !apiBannerDismissed && (
                <BackendOfflineBanner
                    retryMessage={apiRetryMessage}
                    retrying={apiRetrying}
                    onRetry={handleApiRetry}
                    onDismiss={() => setApiBannerDismissed(true)}
                    hasUnsavedWork={hasUnsavedChanges}
                    workKeptOnDevice={isLatestEditKeptOnDevice(currentProjectId, unsentEditFingerprint)}
                />
            )}
            {/* Bootstrap-credential nudge: the session was opened with the
                install password (LoginPage persisted the backend's
                usingBootstrapPassword flag). Routes to the existing
                Settings → Account → Change password flow. */}
            {bootstrapPwLogin && (
                <BootstrapPasswordBanner
                    onOpenSettings={() => openSettings('account')}
                    onDismiss={() => {
                        sessionStorage.removeItem('qm_bootstrap_pw_login');
                        setBootstrapPwLogin(false);
                    }}
                />
            )}
            {/* Quote Brain alerts (slice LIVE): only for the one account the
                server lets read the report; nothing renders for anyone else. */}
            <QuoteBrainAlertBanner
                alerts={quoteBrainReport.alerts}
                mail={quoteBrainReport.mail}
                onOpenReport={() => handleNavigate('quote-brain-report')}
                onDismiss={(id) => { quoteBrainReport.acknowledge(id).catch(() => {}); }}
            />
            {/* Install invitation (2026-09-22 field hardening): phone-only,
                Site Visits only, hidden once running as an installed PWA.
                Self-gating; renders null everywhere else. */}
            <InstallAppPrompt currentPage={currentPage} />
            {/* ─── AI Completion Ribbon Notification ─────────────────────────── */}
            {ribbonNotification && (
                <AiCompletionRibbon
                    notification={ribbonNotification}
                    dismissing={ribbonDismissing}
                    onDismiss={dismissRibbon}
                    onViewResult={() => {
                        const target = ribbonNotification.targetStage;
                        setRibbonNotification(null); setRibbonDismissing(false);
                        // The destination is applied only once the navigation is ACCEPTED. From
                        // another page that is when handleNavigate passes its page-exit guard
                        // (immediately, or later if the guard holds it and the operator confirms);
                        // a held or refused exit never applies it, so it neither changes the
                        // stage under the page the operator is still on nor counts as navigation.
                        const showDestination = () => {
                            if (target === 5) {
                                setTimeout(() => {
                                    openStage5FromCurrentScope({ persistStage: true });
                                }, 120);
                            } else {
                                setTimeout(() => setStageWithTransition(target), 100);
                            }
                        };
                        if ((currentPageRef.current || currentPage) === 'quotemaker') {
                            // Already on QuoteMaker: there is no exit to hold, and the stage setter
                            // below only runs after its delay. The operator's choice of a DIFFERENT
                            // stage is recorded here, at the click, so a Risk or Work Method hand-off
                            // that settles inside that gap cannot take the view first. A request
                            // for the stage already shown records nothing.
                            if (Number(stage) !== Number(target)) {
                                navigationIntentEpochRef.current += 1;
                                operatorNavigationEpochRef.current += 1;
                            }
                            showDestination();
                            return;
                        }
                        handleNavigate('quotemaker', {
                            onAccepted: () => {
                                showDestination();
                            },
                        });
                    }}
                />
            )}

            {/* Main Content Area with iOS-style Page Transitions */}
            <div className="flex-1 flex overflow-hidden relative">
                {/* Portal Pages with Animated Transitions */}
                <div
                    className={`absolute inset-0 overflow-hidden ${pageDirection === 'forward' ? 'page-enter' : 'page-enter-back'}`}
                    key={currentPage}
                    style={{
                        willChange: 'transform, opacity',
                        // RFQ section navigation scrolls its own detail pane.
                        // Unlike hidden, clip cannot become a second scroll
                        // container when a nested control receives focus.
                        ...(currentPage === 'intake' ? { overflow: 'clip' } : {}),
                    }}
                >
                  {/* Suspense fallback covers every lazy route. Dashboard is
                      eager so it renders synchronously; Suspense only kicks
                      in when the user nav-es to a lazy page (Quotes, Training,
                      Clients, etc.) and its chunk is still fetching. */}
                  <Suspense fallback={<PageLoadingShim />}>
                    {currentPage === 'dashboard' && (
                        <DashboardPage
                            projects={projects}
                            projectsLoaded={projectsLoaded}
                            projectsLoadError={projectsLoadError}
                            onRefreshProjects={refreshProjectsFromServer}
                            onNavigate={handleNavigate}
                            user={user}
                            onNewProject={() => openNewProjectWorkflowChooser({ then: () => handleNavigate('quotemaker') })}
                            onLoadProject={openProjectFromList}
                            onOpenQuote={openQuoteForProject}
                            pageNavOptions={pageNavOptions}
                            // Both of these already existed and were only wired
                            // to AllQuotesPage. The run sheet needs them: the
                            // second creation path (restore a job folder), and
                            // the express Stage 1-5 cascade for imported drafts
                            // that are still sitting unstarted.
                            onOpenFileExplorer={() => openGlobalFileExplorer()}
                            // Operator 2026-07-30: "I need a way to delete
                            // Projects from here." Same handler Project History
                            // already uses — it owns the confirm, retires queue
                            // work before deleting so a trailing save cannot
                            // resurrect the row, and resets the form when the
                            // deleted project is the one currently open.
                            onDeleteProject={handleDeleteProject}
                            onDeleteProjects={handleDeleteProjects}
                            // Review labels (migration 088). The Run Sheet
                            // writes the label optimistically and then hands
                            // back the confirmed patch, so the summaries it
                            // renders from stop disagreeing with the server.
                            // A local map rather than a full
                            // getAllProjectSummaries() refetch, deliberately:
                            // re-pulling ~200 lean summaries to record one
                            // annotation is the kind of cost that turns a
                            // pleasant control into a laggy one, and this is the
                            // same idiom the costings/outcome handlers above
                            // already use. Only the ID is carried — the sheet
                            // resolves the name and tone from the live
                            // definition list, so a rename never has to be
                            // chased through this array.
                            onProjectPatched={(id, patch) => {
                                if (!id || !patch) return;
                                setProjects(prev => prev.map(p => (p.id === id ? { ...p, ...patch } : p)));
                            }}
                            // Passing openSettings (rather than letting the page
                            // navigate itself) is deliberate: the Settings tab
                            // arrives via settingsInitialTab, which this
                            // function sets BEFORE navigating, and which also
                            // captures the page to come back to. A
                            // handleNavigate('settings') with a pageNavOptions
                            // payload would land on nothing.
                            onOpenSettings={openSettings}
                        />
                    )}
                                        {currentPage === 'analysis' && (
                        <AnalysisPage
                            projects={projects}
                            projectsLoaded={projectsLoaded}
                            projectsLoadError={projectsLoadError}
                            onRefreshProjects={refreshProjectsFromServer}
                            onNavigate={handleNavigate}
                            // The evidence drawer lists real jobs, so its rows
                            // open them. Same two handlers the Run Sheet uses, so
                            // a row opens identically from either surface.
                            onLoadProject={openProjectFromList}
                            onOpenQuote={openQuoteForProject}
                            // S4: same-tab state echo after the drawer records a
                            // won/lost answer, so the board reflects the decision
                            // without a reload. The write + broadcast live in the
                            // drawer; this only patches `projects`.
                            onOutcomeRecorded={handleAnalysisOutcomeRecorded}
                        />
                    )}
                    {currentPage === 'quick-edit' && (
                        <QuickEditPage
                            onNavigate={handleNavigate}
                            onOpenRecoveredQuote={openRecoveredQuote}
                        />
                    )}
                    {/* Import a quote as a Job (migration 120). Same access as
                        Quick Edit; the server also refuses a business without Jobs. */}
                    {currentPage === 'import-quote' && appAreaAccess['quick-edit'] === true && (
                        <ImportQuotePage
                            onOpenQuote={openImportedQuote}
                            onBack={() => handleNavigate('dashboard')}
                            // The costing configuration the Stage 5 editor
                            // prints with, so the import stores its totals.
                            costingConfig={businessPricingSnapshotStatus === 'ready'
                                ? ((currentBusinessPricingSnapshot as { costingConfig?: Record<string, unknown> } | null)?.costingConfig || null)
                                : null}
                        />
                    )}
                    {/* The Quote Run page draws itself over this slot
                        (AppFileExplorerContainer). Shown only when it is not
                        open: a folder pick was cancelled, or it is opening. */}
                    {currentPage === 'quote-run' && !globalFileExplorer.open && (
                        <div className="qm-quote-run-page-closed" style={{ padding: '32px 24px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12 }}>
                            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Quote Run</h1>
                            <p style={{ margin: 0, fontSize: 13, color: '#52525b' }}>Quote Run reads your sync folder. Open it to pick projects and run their quotes.</p>
                            <button type="button" className="qm-btn qm-btn--primary" onClick={() => { void openGlobalFileExplorer(); }}>Open Quote Run</button>
                        </div>
                    )}
                    {currentPage === 'tasks' && (
                        <TasksPage
                            projects={projects}
                            projectsLoaded={projectsLoaded}
                            onNavigate={handleNavigate}
                            // A bound task's Enter and its job chip use the Run
                            // Sheet's openProject contract verbatim, so a job
                            // opens identically from either surface.
                            onLoadProject={openProjectFromList}
                            onOpenQuote={openQuoteForProject}
                            user={user}
                        />
                    )}
                    {/* The site assessor's surface. Wrapped in the intake
                        dialog provider because the components it composes
                        (VisitEvidenceSection, SideRequestLane) use
                        useIntakeDialogs, which is a HARD RULE in this module —
                        without the provider they soft-fall to native dialogs. */}
                    {currentPage === 'site-visits' && canAccessSiteVisits && (
                        <IntakeDialogProvider>
                            <SiteVisitsPage onExitGuardStateChange={handleSiteVisitsExitGuardStateChange} />
                        </IntakeDialogProvider>
                    )}
                    {currentPage === 'site-visits' && !canAccessSiteVisits && <PageLoadingShim />}
                    {currentPage === 'quotes' && (
                        <AllQuotesPage
                            projects={projects}
                            // 2026-05-23 (Ask 6A): pass loading state so
                            // AllQuotesPage renders a skeleton list instead
                            // of the empty-state card while the projects
                            // fetch is still in flight.
                            projectsLoaded={projectsLoaded}
                            projectsLoadError={projectsLoadError}
                            onRefreshProjects={refreshProjectsFromServer}
                            onLoadProject={openProjectFromList}
                            onDeleteProject={handleDeleteProject}
                            onDuplicateProject={handleDuplicateProject}
                            onUpdateQuoteStatus={handleUpdateQuoteStatus}
                            onNavigate={handleNavigate}
                            // v5.47.3: persistent express Stage 1→5 action for
                            // imported/pre-processed drafts still at S1 — the
                            // toast-only affordance vanished with the toast.
                            // v5.40.4 (UX audit Wave-5 #1): styled-confirm
                            // for the destructive LOST status transition.
                            onShowConfirm={showConfirm}
                            // 2026-05-23 (Ask 2): route through
                            // Open the central workflow chooser, then route
                            // through handleStartNewProject so the "Cancel
                            // Import?" confirm still fires when Stage 1
                            // processing is mid-flight instead of silently
                            // dropping 60s of photo/Whisper extraction.
                            onNewProject={() => openNewProjectWorkflowChooser({ then: () => handleNavigate('quotemaker') })}
                            onOpenFileExplorer={() => openGlobalFileExplorer()}
                            filterStatus={quotesFilter}
                            onFilterStatusChange={setQuotesFilter}
                        />
                    )}
                    {currentPage === 'developer' && canAccessDeveloper && (
                        <DeveloperPage
                            apiKey={apiKey}
                            model={model}
                            onNavigate={handleNavigate}
                            onOpenProjectImports={openGlobalFileExplorer}
                            currentScope={scope}
                            currentProjectStrategy={projectStrategy}
                            currentProjectWMS={projectWMS}
                            currentCostingSheet={projectCostingSheet}
                            showConfirm={showConfirm}
                            onRebuildFromCorpus={rebuildFromCorpusEntry}
                        />
                    )}
                    {currentPage === 'developer' && !canAccessDeveloper && <PageLoadingShim />}
                    <AppSecondaryPagesRouter
                        currentPage={currentPage}
                        handleNavigate={handleNavigate}
                        pageNavOptions={pageNavOptions}
                        showConfirm={showConfirm}
                        apiKey={apiKey}
                        model={model}
                        audioModel={audioModel}
                        user={user}
                        projects={projects}
                        projectsLoaded={projectsLoaded}
                        clientRefreshTrigger={clientRefreshTrigger}
                        onRefreshProjects={refreshProjectsFromServer}
                        receivablesDisabled={receivablesDisabled}
                        openProjectFromList={openProjectFromList}
                    />
                    {currentPage === 'intake' && canAccessIntake && (
                        // v5.39.4 (Peter 2026-05-21): IntakePageV2 fork removed.
                        // The original IntakePage is the only render; the
                        // alternative design has been deleted entirely along
                        // with its 12 supporting components. PortalChromeHeader
                        // is added inside IntakePage to bring its top chrome
                        // in line with the rest of the QM pages.
                        <IntakePage
                            canManagePeople={appAreaAccess.users === true}
                            onNavigate={handleNavigate}
                            pendingSelectJobId={intakePendingSelectJobId}
                            onPendingSelectConsumed={() => setIntakePendingSelectJobId(null)}
                        />
                    )}
                    {currentPage === 'intake' && !canAccessIntake && <PageLoadingShim />}
                    {/* Settings v2 (v5.34.0, 2026-05-18) — true full page.
                        Renders the same SettingsModal component (which has
                        been retrofitted to page mode internally) inside
                        the page transition portal. onClose navigates back
                        to wherever the user came from (settingsReturnPage). */}
                    {currentPage === 'settings' && (
                        <SettingsModal
                            onClose={handleLeaveSettings}
                            onSave={(message, type) => setToast({ message, type })}
                            model={model}
                            setModel={setModel}
                            audioModel={audioModel}
                            setAudioModel={setAudioModel}
                            onRestoreFromFolder={handleRestoreProjectFromFolder}
                            user={user}
                            onLogout={handleSettingsLogout}
                            onNavigate={handleNavigate}
                            initialTab={settingsInitialTab}
                            onDirtyStateChange={handleSettingsDirtyStateChange}
                        />
                    )}
                  </Suspense>
                </div>

                {/* QuoteMaker (main tool) */}
                {currentPage === 'quotemaker' && (
                    <>
                        {/* The working set, docked down the LEFT (operator
                            drawing 2026-08-19; moved here 2026-08-20 after a
                            bottom shelf proved unable to show full job names —
                            stacked rows each get the rail's whole 236px, where
                            ten horizontal tabs would get ~140px apiece).

                            It sits INSIDE this fragment, before the main
                            column, because the enclosing element is a flex ROW
                            (`flex-1 flex overflow-hidden relative`, verified by
                            measuring the live DOM). So it docks by being a
                            sibling and pushes the stage right — no fixed
                            positioning, no overlay, no reserved-space variable.

                            onOpenProject / onOpenStaging are PLAIN per-render
                            arrows, never useCallback (the memoised-callback
                            HARD RULE: a memoised caller pins the routing/load
                            closure chain). loadProject owns the unsaved-changes
                            dialog, so switching cannot drop edits. */}
                        <WorkspaceProjectTabs
                            currentProjectId={currentProjectId}
                            projectSummaries={projects}
                            onOpenProject={(projectId, meta) => Promise.resolve(loadProject({ id: projectId, ...meta })).catch(ignoreQuoteRunActive)}
                            // Import Projects is a MODAL surface, not a page
                            // route: the rail entry's action is
                            // 'open-project-imports' and it calls
                            // onOpenFileExplorer, so handleNavigate() would
                            // silently do nothing.
                            onOpenStaging={() => openGlobalFileExplorer()}
                        />
                        {/* Main Area */}
                        <div className="flex-1 flex flex-col">

                        {/* Universal Dark Industrial Nav Bar - responsive across all sizes.
                            Mobile (< lg): stacks into rows via flex-wrap so the stage strip,
                            title, and actions don\u2019t overlap. Nav cluster ([BACK][NEXT])
                            stays adjacent at every viewport so it\u2019s always reachable. */}
                        {/* Top-nav strip extracted to <QuoteMakerNavBar> (TS-060 T3
                            compose). App keeps the StagePills click handler (passed as
                            onSelectStage) and the Stage 4/5 totals gate+compute below
                            (passed as the display-ready navScopeTotals object), so
                            fmtNavMoney + the *ForNav derived totals stay single-source. */}
                        {/* Stage 4 + Stage 5 — borderless Items + large-orange-Total
                            readout in the nav (Peter v5.41.187; extended to Stage 5 +
                            de-bordered v5.41.193). Replaces BOTH the verbose Scope
                            Builder pill AND the green "Final quote" pill. Stage 4 reads
                            the live scope; Stage 5 (quote editor open) reads the final
                            quote's items + inc-GST total. */}
                        <QuoteMakerNavBar
                            stage={stage}
                            quoteEditorOpen={quoteEditorOpen}
                            effectiveCompletedStages={effectiveCompletedStages}
                            notRunStages={notRunStages}
                            quoteData={quoteData}
                            canNavigateToQuoteStage={canNavigateToQuoteStage}
                            isStageTransitioning={isStageTransitioning}
                            isAnalyzing={isAnalyzing}
                            onSelectStage={(stageId) => {
                                if (!canNavigateToQuoteStage(stageId) || isStageTransitioning) return;
                                // Choosing the stage already shown, with no preview or editor to
                                // close, changes no view: it is not an operator choice.
                                const pillChangesNoView = stageId !== 5
                                    && Number(stageId) === Number(stage)
                                    && !quoteEditorOpen;
                                if (stageId === 5) {
                                    openStage5FromCurrentScope({ persistStage: true });
                                } else {
                                    if (quoteEditorOpen) {
                                        editor.close({ transitionToStage4: false });
                                    }
                                    if (stageId === 1) {
                                        // 5C / C27: the no-view rule above. Pressing the Stage 1 pill while Stage 1 is already
                                        // showing is not an operator choice, so it must not cancel the ordinary Summary that is running or its Risk
                                        // continuation. Every other revoking gesture still revokes.
                                        if (!pillChangesNoView) revokeAttendedAutomaticRiskIntent();
                                        if (formData.notes || documents.length > 0 || photos.length > 0) {
                                            setImportPhase('form');
                                        }
                                    }
                                    setStageWithTransition(stageId);
                                }
                            }}
                            stageNavContext={stageNavContext}
                            navMessage={navMessage}
                            dismissNavMessage={dismissNavMessage}
                            navScopeTotals={buildNavScopeTotals({
                                stage,
                                quoteEditorOpen,
                                navMessage,
                                quoteDataForNav,
                                scope,
                                scopeGstInclusive,
                                quoteTotalForNav,
                                scopeTotalForNav,
                            })}
                            isSummarizing={isSummarizing}
                            isGenerating={isGenerating}
                            photoAssignBusy={photoAssignBusy}
                            projectWMSGenerating={projectWMSGenerating}
                            hasLivePipelineEntry={hasLivePipelineEntry}
                            isSavingProject={isSavingProject}
                            hasUnsavedChanges={hasUnsavedChanges}
                            onManualSave={handleManualProjectSave}
                            isStage1Processing={isStage1Processing}
                            isFastTrackManualScope={isFastTrackManualScope}
                            quoteBrainRoute={quoteBrainRoute}
                            isNextStageJustReady={isNextStageJustReady}
                            analysis={analysis}
                            lastSavedAt={lastSavedAt}
                            projectWMS={projectWMS}
                            aiPipelineCancelToast={aiPipelineCancelToast}
                            canGoNext={canGoNext}
                            cancelAiPipeline={cancelAiPipeline}
                            handleBack={handleBack}
                            handleNext={handleNext}
                            handleQuoteEditorBack={handleQuoteEditorBack}
                            openNewProjectWorkflowChooser={openNewProjectWorkflowChooser}
                            quoteEditorActionsRef={quoteEditorActionsRef}
                            quoteEditorSaveState={quoteEditorSaveState}
                        />

                    {/* TS-060: Missing Files Banner extracted to src/components/ui/ProjectMissingFilesBanner.tsx */}
                    <ProjectMissingFilesBanner
                        missingFiles={missingFiles}
                        onDismiss={() => setMissingFiles({ photos: [], documents: [], subfolder: null })}
                    />

                    {/* The Quote Brain strip: "Write the quote", the live run, the
                        Adopt / Keep mine panel for a held result. Renders nothing
                        when there is nothing to say. */}
                    <QuoteBrainBar
                        projectId={currentProjectId}
                        stage={stage}
                        newJob={quoteBrainWritesThisJob}
                        hasQuoteWork={(Array.isArray(scope) && scope.length > 0) || !!projectWMS || !!projectCostingSheet}
                        preparing={isStage1Processing || isSummarizing}
                        files={{ processed: procState.processedFiles, total: procState.totalFiles }}
                        filesProcessing={isStage1Processing}
                        summarizing={isSummarizing}
                        itemCount={Array.isArray(scope) ? scope.length : 0}
                        onOpenScope={() => {
                            // As Stage 1's SCOPE button on a job the Quote Brain wrote (quoteBrainStage1Route 'written').
                            setCompletedStages(prev => new Set([...prev, 1]));
                            setStageWithTransition(4);
                        }}
                        hasSummary={!!projectSummary}
                        manual={isFastTrackManualScope}
                        onWriterChange={handleQuoteBrainWriterChange}
                        canReloadNow={() => !hasUnsavedChangesRef.current && pipelineOwnerRef.current !== 'batch-runner'}
                        getSaveToken={() => baseUpdatedAtRef.current}
                        ensureSaved={async () => {
                            try {
                                await saveCurrentProject();
                                return true;
                            } catch {
                                return false;
                            }
                        }}
                        reloadProject={() => {
                            const id = currentProjectIdRef.current;
                            if (!id) return Promise.resolve(null);
                            // A stale-save conflict on this job is settled by the same load, as the update notice's own
                            // Reload settles it (review round 2, W1 to W3).
                            if (crossTabConflict?.projectId === id) return handleCrossTabReload();
                            return loadProjectUnchecked({ id });
                        }}
                        showConfirm={showConfirm}
                        setToast={setToast}
                    />
                    {/* Stage Content with iOS-style Transitions */}
                    <main
                        className="flex-1 overflow-hidden bg-gray-50 relative"
                        onInputCapture={captureReviewEditIntent}
                        onChangeCapture={captureReviewEditIntent}
                        onDropCapture={captureReviewEditIntent}
                    >
                        <div
                            key={stage}
                            className={stageDirection === 'forward' ? 'stage-enter' : 'stage-enter-back'}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                willChange: 'transform, opacity',
                                // overflowY: scroll up/down is intended; overflowX: hidden
                                // prevents a spurious left/right scrollbar when a tooltip
                                // or rounding artifact pushes content by a few pixels.
                                overflowY: 'auto',
                                overflowX: 'hidden',
                            }}
                        >
                          {/* Stage 1-4 pages are all lazy-loaded; share one Suspense
                              boundary so the fallback swap feels like a page transition. */}
                          <Suspense fallback={<PageLoadingShim />}>
                            <AppStageViewsContainer
                              stage={stage}
                              pricingWorktableActive={pricingWorktableActive}
                              stage1Ref={stage1Ref}
                              projectWorkflowMode={projectWorkflowMode}
                              pipelineOwner={pipelineOwnerRef.current}
                              handleSwitchProjectWorkflowMode={handleSwitchProjectWorkflowMode}
                              currentProjectId={currentProjectId}
                              currentProjectIdRef={currentProjectIdRef}
                              projectLoadGenerationRef={projectLoadGenerationRef}
                              projectPersistenceAbortRef={projectPersistenceAbortRef}
                              ensureProjectOwnerForImport={ensureStage1ProjectOwnerForImport}
                              formData={formData}
                              quoteObjective={quoteObjective}
                              setFormData={setFormData}
                              documents={documents}
                              setDocuments={setDocuments}
                              photos={photos}
                              setPhotos={setPhotos}
                              handleMarkupReferencePhoto={handleMarkupReferencePhoto}
                              videos={videos}
                              setVideos={setVideos}
                              videoTranscripts={videoTranscripts}
                              setVideoTranscripts={setVideoTranscripts}
                              handleRemoveReferenceFile={handleRemoveReferenceFile}
                              apiKey={apiKey}
                              model={model}
                              audioModel={audioModel}
                              parentFolder={parentFolder}
                              setParentFolder={setParentFolder}
                              quoteSubfolder={quoteSubfolder}
                              setQuoteSubfolder={setQuoteSubfolder}
                              setFolderFiles={setFolderFiles}
                              setSaveStatus={setSaveStatus}
                              scanPhotosForOCR={scanPhotosForOCR}
                              summaryRerunHeld={summaryRerunHeld}
                              cockpitTabRequest={cockpitTabRequest}
                              onCockpitTabRequestConsumed={consumeCockpitTabRequest}
                              // B10: only a landing that is up to date on arrival is read out. A run the job
                              // changed under (an edit to the cards during the provider call) lands out of
                              // date; its remake lands fresh and is the one that speaks and bills TTS.
                              summaryLanding={projectSummaryEvidenceIsFresh ? summaryLanding : null}
                              stage1Notice={stage1Notice}
                              summaryRetryWaiting={summaryFailureRetryWaiting}
                              summaryUpdatesItself={!!stage1SummaryWaitingNotice}
                              releaseInspectorNotesPause={releaseInspectorNotesPause}
                              isAnalyzing={isAnalyzing}
                              isSummarizing={isSummarizing}
                              projectSummary={projectSummary}
                              showConfirm={showConfirm}
                              openSettings={openSettings}
                              quoteData={quoteData}
                              setStageWithTransition={setStageWithTransition}
                              setIsStage1Processing={setIsStage1Processing}
                              importAbortRef={importAbortRef}
                              resetCounter={resetCounter}
                              postProcessContext={postProcessContext}
                              isPostProcessing={isPostProcessing}
                              preprocessedFiles={preprocessedFiles}
                              setPreprocessedFiles={setPreprocessedFiles}
                              importPhase={importPhase}
                              setImportPhase={setImportPhase}
                              procState={procState}
                              procDispatch={procDispatch}
                              extractedFacts={extractedFacts}
                              setExtractedFacts={setExtractedFacts}
                              inspectorNotesAiPaused={inspectorNotesAiPaused}
                              pauseAiForInspectorNotes={pauseAiForInspectorNotes}
                              continueAiAfterInspectorNotes={continueAiAfterInspectorNotes}
                              handleStage1ImportIntent={handleStage1ImportIntent}
                              appHandlersRef={appHandlersRef}
                              summaryError={summaryError}
                              runProjectSummary={runProjectSummary}
                              summaryNeedsRefresh={summaryNeedsRefresh}
                              handleNext={handleNext}
                              handleRequoteShortcut={handleRequoteShortcut}
                              isRequoteShortcutRunning={isRequoteShortcutRunning}
                              quoteBrainRoute={quoteBrainRoute}
                              clientIdentity={clientIdentity}
                              handleNavigate={handleNavigate}
                              analysis={analysis}
                              photoAnalysis={photoAnalysis}
                              scope={scope}
                              projectWMS={projectWMS}
                              quoteTitle={quoteTitle}
                              setPricingWorktableActive={setPricingWorktableActive}
                              openStage5FromCurrentScope={openStage5FromCurrentScope}
                              setScope={setScopeFromScreens}
                              handleSetBuilderHbcfDecision={handleSetBuilderHbcfDecision}
                              currentBusinessPricingSnapshot={currentBusinessPricingSnapshot}
                              businessPricingSnapshotStatus={businessPricingSnapshotStatus}
                              setSelectedHeroImage={setSelectedHeroImage}
                              aiGeneratedItems={aiGeneratedItems}
                              setAiGeneratedItems={setAiGeneratedItems}
                              autoAssignAbortRef={autoAssignAbortRef}
                              photoAssignBusy={photoAssignBusy}
                              retryPhotoAssignment={retryPhotoAssignment}
                              photoAssignBusyRef={photoAssignBusyRef}
                              setPhotoAssignBusy={setPhotoAssignBusy}
                              setAiAssistantState={setAiAssistantState}
                              quoteNote={quoteNote}
                              setQuoteNote={setQuoteNoteByOperator}
                              scopeGstInclusive={scopeGstInclusive}
                              setScopeGstInclusive={setGstBasisForBothStages}
                              setNavMessage={setNavMessage}
                              dismissNavMessage={dismissNavMessage}
                            />
                          </Suspense>
                        </div>

                        {/* Quote Editor - overlays stage content when open.
                            Lazy-loaded - wrap in Suspense so chunk fetch shows shim. */}
                        {quoteEditorOpen && (
                    <Suspense fallback={<ModalLoadingShim />}>
                    <AppQuoteEditorModalWrapper
                        quoteEditorOpen={quoteEditorOpen}
                        quoteJob={quoteJobEditorProp}
                        onQuoteActionsOpen={quoteJobActions.recheckCapability}
                        closeQuoteEditor={closeQuoteEditor}
                        editor={editor}
                        quoteData={quoteData}
                        setQuoteData={setQuoteData}
                        quoteDataRef={quoteDataRef}
                        quoteTitle={quoteTitle}
                        quoteTitleRef={quoteTitleRef}
                        setQuoteTitle={setQuoteTitle}
                        titleEditedByUserRef={titleEditedByUserRef}
                        setTitleEditedByUser={setTitleEditedByUser}
                        formData={formData}
                        setFormData={setFormData}
                        quoteObjective={quoteObjective}
                        projectSummary={projectSummary}
                        scopeGstInclusive={scopeGstInclusive}
                        setScopeGstInclusive={setGstBasisForBothStages}
                        scope={scope}
                        setScope={setScopeFromScreens}
                        scopeRef={scopeRef}
                        quoteEditorViewMode={quoteEditorViewMode}
                        selectedHeroImage={selectedHeroImage}
                        selectedHeroImageRef={selectedHeroImageRef}
                        setSelectedHeroImage={setSelectedHeroImage}
                        parentFolder={parentFolder}
                        quoteSubfolder={quoteSubfolder}
                        folderFiles={folderFiles}
                        setFolderFiles={setFolderFiles}
                        documents={documents}
                        preprocessedFiles={preprocessedFiles}
                        setDocuments={setDocuments}
                        photos={photos}
                        photosRef={photosRef}
                        setPhotos={setPhotos}
                        photoAnalysis={photoAnalysis}
                        videos={videos}
                        analysis={analysis}
                        diagnosticResult={diagnosticResult}
                        ensureQuoteSubfolder={ensureQuoteSubfolder}
                        ensureQmOutputSubfolder={ensureQmOutputSubfolder}
                        generateProjectName={generateProjectName}
                        apiKey={apiKey}
                        model={model}
                        businessPricingSnapshotStatus={businessPricingSnapshotStatus}
                        currentBusinessPricingSnapshot={currentBusinessPricingSnapshot}
                        quoteEditorActionsRef={quoteEditorActionsRef}
                        quoteEditorLiveSnapshot={quoteEditorLiveSnapshot}
                        setQuoteEditorLiveSnapshot={setQuoteEditorLiveSnapshot}
                        setQuoteEditorSaveState={setQuoteEditorSaveState}
                        setQuoteEditorObjectiveScope={setQuoteEditorObjectiveScope}
                        quoteEditorIsEditing={quoteEditorIsEditing}
                        setQuoteEditorIsEditing={setQuoteEditorIsEditing}
                        showConfirm={showConfirm}
                        projectCostingSheet={projectCostingSheet}
                        handleSetBuilderHbcfDecision={handleSetBuilderHbcfDecision}
                        saveCurrentProjectRef={saveCurrentProjectRef}
                        isCurrentProjectCommercialWriteInFlight={isCurrentProjectCommercialWriteInFlight}
                        isCurrentProjectQuoteGenerationInFlight={isCurrentProjectQuoteGenerationInFlight}
                        currentProjectId={currentProjectId}
                        currentProjectIdRef={currentProjectIdRef}
                        user={user}
                        paintedProjectLoadGeneration={paintedProjectLoadGeneration}
                        projectStrategy={projectStrategy}
                        handleNavigate={handleNavigate}
                        originalAiScope={originalAiScope}
                        aiGeneratedItems={aiGeneratedItems}
                        quoteNote={quoteNote}
                        setQuoteNote={setQuoteNote}
                        acquireQuoteEditorSaveLease={acquireQuoteEditorSaveLease}
                        saveCurrentProject={saveCurrentProject}
                        saveErrorRef={saveErrorRef}
                        setSaveError={setSaveError}
                        savedFingerprintRef={savedFingerprintRef}
                        projects={projects}
                        setProjects={setProjects}
                        adoptServerProjectVersion={adoptServerProjectVersion}
                        projectLoadGenerationRef={projectLoadGenerationRef}
                        handleMarkupReferencePhoto={handleMarkupReferencePhoto}
                        handleAddReferenceFiles={handleAddReferenceFiles}
                        handleRemoveReferenceFile={handleRemoveReferenceFile}
                        handleMarkWon={handleMarkWon}
                        handleMarkLost={handleMarkLost}
                        handleSetFollowUp={handleSetFollowUp}
                        setToast={setToast}
                    />
                    </Suspense>
                        )}
                    </main>
                        </div>
                    </>
                )}
                </div>

            {/* Upload Status Indicator - temporarily disabled for freeze diagnosis */}
            {/* <UploadStatusBar /> */}

            {/* The working set used to mount HERE, as a horizontal shelf above
                the BottomNavBar. It moved into the QuoteMaker flex row (see
                ~:9516) because the operator could not read job names across a
                shared-width strip: "with the tabs on the bottom, i can't see
                the full text. that's why i imaged it on the left." */}

            {/* Bottom Navigation Bar (Windows Taskbar Style)
                Phase-9 polish: project title + address live here (moved out of the
                top nav bar). Only render when the user is on the QuoteMaker page
                - other pages get their own context and shouldn't show a quote title. */}
            <BottomNavBar
                currentPage={currentPage}
                onNavigate={handleNavigate}
                user={user}
                onLogout={handleSettingsLogout}
                onOpenSettings={openSettings}
                stage={stage}
                apiKey={apiKey}
                projectTitle={currentPage === 'quotemaker' && (currentProjectId || !globalFileExplorer.open)
                    ? quoteTitle
                    : ''}
                projectAddress={currentPage === 'quotemaker' && (currentProjectId || !globalFileExplorer.open)
                    ? (formData?.address || '')
                    : ''}
                // v5.50.6: HISTORY chip beside the title while an imported
                // historical quote is loaded. Read from the summaries row, the
                // same source laneOf uses, so the chip and the Run Sheet lane
                // can never disagree. Cheap: the find only runs on quotemaker.
                isHistoryProject={currentPage === 'quotemaker' && !!currentProjectId && (() => {
                    const row = projects.find(p => p.id === currentProjectId);
                    return (row?.projectType || row?.project_type) === 'history';
                })()}
                onProjectTitleChange={currentPage === 'quotemaker' && (currentProjectId || !globalFileExplorer.open)
                    ? (val) => {
                        captureReviewEditIntent();
                        setQuoteTitle(val);
                        setTitleEditedByUser(true);
                    }
                    : undefined}
                onRegenerateTitle={currentPage === 'quotemaker' && (currentProjectId || !globalFileExplorer.open)
                    ? () => { setQuoteTitle(generateQuoteTitle()); setTitleEditedByUser(false); }
                    : undefined}
                // 2026-09-22: a site assessor gets no AI Assistant. Their job is
                // capture, the panel is a quoting tool, and on the phone it was one
                // more thing that could fail in the field. Withholding BOTH props
                // means the rail's button has nothing to open; the panel itself is
                // gated by role inside AppAssistantPanelWrapper. Same helper as the
                // landing-page choice, so both spellings of the role are covered.
                aiAssistantState={isSiteVisitRole(user?.role)
                    ? undefined
                    : { isOpen: globalAiAssistantOpen || !!assistantDockHost, isProcessing: globalAiProcessing }}
                onAiAssistantToggle={isSiteVisitRole(user?.role) ? undefined : () => {
                    // 2026-10-14: at Stage 4 and Stage 5 the Assistant lives in
                    // the cockpit. The button opens that tab (and puts the
                    // cursor in the composer when it already shows), never the
                    // floating panel over the cockpit.
                    if (requestCockpitAssistant()) return;
                    // ★ UNIFIED ASSISTANT ★
                    // Open once, then let repeated navbar presses toggle the
                    // panel between full and compact modes. Stage 4 only exposes a
                    // scope snapshot callback; the assistant itself is mounted
                    // once at App level so history survives page/stage changes.
                    // Conversation history persists across stage navigation because the
                    // global panel is mounted once at App level and never unmounts.
                    if (globalAiAssistantOpen) {
                        setAiAssistantViewToggleSignal(n => n + 1);
                    } else {
                        setGlobalAiAssistantOpen(true);
                    }
                }}
                hasNewRationale={currentPage === 'quotemaker' && stage === 4 && scope.length > 0 && !hasViewedRationale}
                onRationaleViewed={() => setHasViewedRationale(true)}
                // Project-history filter - the "All / Won / Pipeline / Lost" chips
                // that used to live in a big strip on the AllQuotesPage itself.
                // Moved here to the BottomNavBar when the user is on Projects.
                // Peter 2026-04-20: "in a nice, tasteful, elegant way."
                quotesFilter={currentPage === 'quotes' ? computeBottomNavQuotesFilter(projects, quotesFilter) : null}
                onQuotesFilterChange={setQuotesFilter}
                onOpenFileExplorer={openQuoteRunPage}
                isFileExplorerOpen={globalFileExplorer.open}
                quoteBrainReportVisible={quoteBrainReport.visible}
                // Dashboard pulse - previously rendered as a TODAY sliver inside
                // the main dashboard frame. Moved here so the dashboard's real
                // estate is freed up. Computed inline here from `projects` so it
                // stays in sync without requiring a separate state hoist.
                // Peter 2026-04-20: "show that on the bottom navbar instead of
                // taking up space within the dashboard's Main frame."
                dashboardPulse={currentPage === 'dashboard' ? computeBottomNavDashboardPulse(projects) : null}
                onStageNavigate={handleQuoteStageNavigate}
            />

            {(globalFileExplorer.open || batchActiveRef.current) && (
                <AppFileExplorerContainer
                    globalFileExplorer={globalFileExplorer}
                    setGlobalFileExplorer={setGlobalFileExplorer}
                    closeGlobalFileExplorer={closeGlobalFileExplorer}
                    batchActive={batchActiveRef.current}
                    fileExplorerSelectProject={fileExplorerSelectProject}
                    fileExplorerChangeFolder={fileExplorerChangeFolder}
                    rescanGlobalFileExplorer={rescanGlobalFileExplorer}
                    fileExplorerShowToast={fileExplorerShowToast}
                    showConfirm={showConfirm}
                    fileExplorerOpenSyncSettings={fileExplorerOpenSyncSettings}
                    appHandlersRef={appHandlersRef}
                    apiKey={apiKey}
                    setToast={setToast}
                    generateQuoteForPreprocessedProject={generateQuoteForPreprocessedProject}
                    getAllProjectSummaries={getAllProjectSummaries}
                    setProjects={setProjects}
                    loadProject={loadProject}
                    setParentFolder={setParentFolder}
                    setQuoteSubfolder={setQuoteSubfolder}
                />
            )}

            {/* DEC-TQ-003 (2026-05-06): coarse AI-call banner.
                Subscribes to PipelineMonitor and renders a fixed top strip
                whenever the currently-loaded project has an in-flight AI
                call. Hidden otherwise. Lets a user click "View live" on a
                queued project's row and watch the AI work in real time. */}
            {/* Top "AI is running" banner — gated OFF via notification-policy
                (Peter 2026-06-27, progress-chatter removal). Flip
                PIPELINE_BANNER_ENABLED in src/config/notification-policy.js to
                restore. */}
            {isPipelineBannerEnabled() && <LivePipelineBanner projectId={currentProjectId || null} />}

            {/* v5.40.0 (UX audit): global offline banner. Renders only when
                the browser reports offline; auto-hides on reconnect. The
                network-resilience service already has the signal — this is
                the UI consumer that was missing.
                v5.40.13 (Wave 13 bug-hunt fix B5): the top-of-viewport
                banners used to be separate `position: fixed; top: 0` siblings with
                different z-index — only the topmost-z showed when two fired
                concurrently. Now wrapped in a single fixed-top flex column
                so they stack vertically + reliably. */}
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    // 2026-08-20: was 9998, which sat UNDER FileExplorer's opaque
                    // shell (z-9999, FileExplorer.tsx:2373), the Assistant backdrop
                    // (z-10000, QuoteMakerAssistantPanel.jsx:7501) and the Assistant
                    // panel (z-10001, :7754). The saveError banner carries a
                    // recovery action, so it must remain visible and clickable.
                    // 10002 clears both surfaces and stays UNDER the menu rail
                    // (10090/10100, portalled from BottomNavBar; the bar itself
                    // moved to 9900 on 2026-09-22 and sits at the bottom edge,
                    // so it never overlaps this top-edge stack),
                    // ReconnectFolderModal (10110) and ConfirmModal's default
                    // (10200), so a decision dialog still outranks a notice.
                    // Pinned by notification-surface-z-order.test.js.
                    zIndex: 10002,
                    display: 'flex',
                    flexDirection: 'column',
                    pointerEvents: 'none', // children re-enable per banner
                }}
            >
                {/* One status at a time: while the server banner is up (or
                    was dismissed for this outage) the browser-offline strip
                    would only repeat it. */}
                {!apiStatus.disabled && (
                    <div style={{ pointerEvents: 'auto' }}>
                        <OfflineBanner />
                    </div>
                )}

                <AppTopBanners
                    topNotice={topNotice}
                    onDismissTopNotice={() => setTopNotice(null)}
                    saveError={saveError}
                    onDismissSaveError={() => setSaveError(null)}
                    isSavingProject={isSavingProject}
                    onRetrySave={() => {
                        setManualSaveRetryLatch(null);
                        saveCurrentProject().catch(() => { /* error path already sets banner */ });
                    }}
                    onNavigateToQuotes={() => {
                        setSaveError(null);
                        handleNavigate('quotes');
                    }}
                />

            </div>
            {/* end v5.40.13 stacked-banner wrapper */}

            <QuoteMakerWrittenNotices
                notices={quoteMakerWritten.notices}
                onReview={(notice) => { void openProjectFromList({ id: notice.projectId }); }}
                onDismiss={quoteMakerWritten.dismiss}
            />

            {crossTabConflict && (
                <ProjectUpdateNotice
                    // A new conflict starts a new review: a confirm step never
                    // describes an older saved version (F48).
                    key={`${crossTabConflict.projectId || ''}:${crossTabConflict.actualUpdatedAt || ''}:${crossTabConflict.kind || ''}`}
                    hasLocalEdits={hasUnsavedChanges}
                    saveRefused={crossTabConflict.kind === 'server-stale'}
                    onReload={handleCrossTabReload}
                    onOverwrite={handleConflictOverwrite}
                    describeOverwrite={describeConflictOverwrite}
                    onDismiss={handleDismissRemoteUpdate}
                    // The saved version holds the Quote Brain's quote this tab lacks: the notice then offers only to load
                    // it, never Review or Replace, which would erase it (review round 2, W1 to W3).
                    quoteBrainWrote={!!quoteBrainWriteUnloaded(crossTabConflict.projectId)}
                />
            )}

            {/* Global portal-based tooltip manager.
                Mounted once at the app root. Listens for mouseover on any
                element carrying `data-tooltip` and renders a single
                fixed-position tooltip portaled to document.body, escaping
                the overflow/transform stacking contexts that used to clip
                the old CSS pseudo-element tooltips inside scope builder. */}
            <TooltipManager />

            {/* Global QuoteMaker Assistant Panel - lazy-loaded; Suspense shows a
                tiny fallback while the chunk downloads. Panel is hidden by
                default (isOpen=false) so the chunk fetch happens on first open.
                v5.16.10 (Peter 2026-05-04): wrapped with AssistantErrorBoundary
                so a render-time / tool-call crash can't take the whole app
                down. Boundary shows a recoverable UI + Reset button that
                remounts the assistant subtree without affecting Stage 1-5
                state. */}
            <AppAssistantPanelWrapper
                apiKey={apiKey}
                userRole={user?.role}
                currentPage={currentPage}
                currentProjectId={currentProjectId}
                assistantDraftSessionKey={assistantDraftSessionKey}
                assistantStageNavigationState={assistantStageNavigationState}
                globalAiAssistantOpen={globalAiAssistantOpen}
                aiAssistantViewToggleSignal={aiAssistantViewToggleSignal}
                setGlobalAiAssistantOpen={setGlobalAiAssistantOpen}
                stage={stage}
                quoteEditorOpen={quoteEditorOpen}
                quoteEditorIsEditing={quoteEditorIsEditing}
                showConfirm={showConfirm}
                model={model}
                formData={formData}
                quoteTitle={quoteTitle}
                setFormData={setFormData}
                analysis={analysis}
                diagnosticResult={diagnosticResult}
                scope={scope}
                setScope={setScopeFromScreens}
                photos={photos}
                documents={documents}
                setDocuments={setDocuments}
                videos={videos}
                quoteData={quoteData}
                quoteLiveSnapshot={quoteEditorLiveSnapshot}
                quoteObjectiveText={quoteObjective.text}
                setSettingsOpen={setSettingsOpen}
                handleNavigate={handleNavigate}
                handleQuoteStageNavigate={handleQuoteStageNavigate}
                refuseProjectChangeDuringQuoteRun={refuseProjectChangeDuringQuoteRun}
                showQuoteRunJobInsteadOfOpening={showQuoteRunJobInsteadOfOpening}
                readPaintedProjectLoad={readPaintedProjectLoad}
                readLivePage={readLivePageForAssistant}
                navigateToQuoteStageLive={navigateAssistantToQuoteStage}
                toggleGlobalFileExplorer={openQuoteRunPage}
                isFileExplorerOpen={globalFileExplorer.open}
                scopeSnapshotCallbackRef={scopeSnapshotCallbackRef}
                setClientRefreshTrigger={setClientRefreshTrigger}
                setGlobalAiProcessing={setGlobalAiProcessing}
                quoteNote={quoteNote}
                setQuoteNote={setQuoteNote}
                projectDefaultMargin={projectDefaultMargin}
                setScopeGstInclusive={setGstBasisForBothStages}
                onHbcfThresholdCrossed={showHbcfNotice}
                currentBusinessPricingSnapshot={currentBusinessPricingSnapshot}
                businessPricingSnapshotStatus={businessPricingSnapshotStatus}
                projectStrategy={projectStrategy}
                projectWMS={projectWMS}
                quoteEditorActionsRef={quoteEditorActionsRef}
                quoteJob={quoteJobEditorProp}
                quoteMakerByHand={isFastTrackManualScope}
                projects={projects}
                originalAiScope={originalAiScope}
                scopeGstInclusive={scopeGstInclusive}
                isAnalyzing={isAnalyzing}
                isSummarizing={isSummarizing}
                isGenerating={isGenerating}
                photoAssignBusy={photoAssignBusy}
                projectWMSGenerating={projectWMSGenerating}
                lastAssignedPhotoIdsRef={lastAssignedPhotoIdsRef}
                intakeAssistantContext={currentPage === 'intake' ? intakeAssistantContext : null}
                cancelAiPipeline={cancelAiPipeline}
                retryPhotoAssignment={retryPhotoAssignment}
                rfqSourceDoubt={rfqSourceDoubt.reading}
                onRfqSourceDoubtSwitch={switchRfqSourceFromAssistant}
                onRfqSourceDoubtKeep={keepRfqSourceFromAssistant}
                onRfqSourceDoubtClose={rfqSourceDoubt.close}
                quoteMakerReview={quoteMakerReview.review}
                onQuoteMakerReviewDone={quoteMakerReview.done}
                isCurrentProjectQuoteGenerationInFlight={isCurrentProjectQuoteGenerationInFlight}
                loadProject={loadProject}
            />

            {/* Modals — available on all pages.
                v5.13.13: each lazy modal is wrapped in Suspense AND gated on
                its open-state so the chunk only fetches when the user opens
                it. Without the open-state gate, React would resolve every
                lazy import on the first parent render (suspending → showing
                the fallback briefly even when the modal is closed).
                ConfirmModal (further down) stays eager — small + critical-
                path destructive-action confirmation. */}
            <AppAuxiliaryModalsContainer
                confirmModal={confirmModal}
                closeConfirm={closeConfirm}
                mediaRecoveryNeeded={mediaRecoveryNeeded}
                setMediaRecoveryNeeded={setMediaRecoveryNeeded}
                currentProjectIdRef={currentProjectIdRef}
                projectLoadGenerationRef={projectLoadGenerationRef}
                projectPersistenceAbortRef={projectPersistenceAbortRef}
                projects={projects}
                photos={photos}
                documents={documents}
                setDocuments={setDocuments}
                setPhotos={setPhotos}
                reconnectModalOpen={reconnectModalOpen}
                setReconnectModalOpen={setReconnectModalOpen}
                pendingProjectLoad={pendingProjectLoad}
                setPendingProjectLoad={setPendingProjectLoad}
                FileSystemService={FileSystemService}
                setParentFolder={setParentFolder}
                setQuoteSubfolder={setQuoteSubfolder}
                setMissingFiles={setMissingFiles}
                setFolderFiles={setFolderFiles}
                completeProjectLoad={completeProjectLoad}
            />

            {/* Global drag-and-drop overlay - Dashboard ONLY (Peter 2026-04-20:
                Stage 1 has its own drop zone inside FileImportView). */}
            <GlobalDashboardDropOverlay active={globalDragActive && currentPage === 'dashboard'} />

            {/* Global Toast Notification (v5.14.85: rendered via the canonical
                <Toast> component — frosted-glass surface, glowing left accent
                stripe, animated countdown bar, hover-to-pause, spring entrance.
                See src/components/ui/Toast.jsx + src/styles/qm-toast.css.)
                v5.x (Peter 2026-06-27): progress/completion toasts are
                suppressed via notification-policy.shouldShowToast(); failures
                (warning/error) + guard notices still show. State still updates
                + auto-clears; it just doesn't render when suppressed. */}
            {toast && shouldShowToast(toast) && (
                <QmToastStack position="bottom-right">
                    <QmToast
                        // Key off message+type so consecutive setToast() calls
                        // with different content force a re-mount and re-trigger
                        // the entrance animation. Stable key for identical
                        // updates so the user doesn't see a flash.
                        key={toastKey(toast)}
                        type={toast.type || 'info'}
                        title={toast.title || null}
                        message={toast.message}
                        // `sticky` disables the App-level auto-clear (see the toast
                        // effect above) but was never forwarded here, so the component
                        // armed its OWN 4s timer and dismissed anyway - the session-
                        // expiry notice and its "Log in" key vanished after 4s while
                        // apiFetch's 30s dedup suppressed the re-fire, leaving a
                        // signed-out app with no notice. duration 0 is Toast's
                        // no-auto-dismiss mode: the close key and the action keys
                        // still render, the countdown ring does not.
                        duration={toast.sticky ? 0 : (Number.isFinite(toast.duration) ? toast.duration : 4000)}
                        action={toast.action || null}
                        secondaryAction={toast.secondaryAction || null}
                        // Clears only the toast this one showed, matched by the
                        // key above: a button that raises a toast with new text
                        // keeps it (review round 3), and a re-keyed copy of the
                        // same toast (the HBCF notice on a first save) goes with
                        // it instead of sitting on an exiting instance (round 4).
                        onDismiss={() => {
                            const shownKey = toastKey(toast);
                            setToast((prev) => afterToastExit(prev, shownKey));
                        }}
                    />
                </QmToastStack>
            )}
        </div>
        </ProjectClientLink>
        </OperationalFieldBoundary>
    );
};

export default App;
// TS-060 / DEC-COUNCIL-015 T2: ErrorBoundary now lives in its own .tsx module;
// re-exported here so src/main.jsx's `import App, { ErrorBoundary } from './App'`
// is unchanged.
export { ErrorBoundary } from './components/ErrorBoundary';
